// ============================================================
// PERRY — THE ONE SOURCE OF TRUTH
// Every species, enemy, beat, shot, node, Impera, Tome, Curio,
// dial, and line of the tale lives HERE and only here.
// (Law 13: Terror has PackArt.js; Sol has gameData.js; Perry has
//  perryData.js. No special-case content scattered in components.)
//
// VOCABULARY (BLATANT — the Empire speaks on screen):
//   Anima  — your life, and a currency. Spendable. The strikes.
//   Chao   — your breath/energy. Refills each encounter. The
//            GROWTH LAW: clear an encounter spending none and
//            your ceiling permanently rises. Restraint compounds.
//   Lyt    — your riposte's edge. Lyf — a foe's skin.
//   Kindling — the one coin. Ascend — spend it at the tree.
//   Dim / Ruin / Fracture / Fallen — the Empire's verbs.
//   Solrend — one run down the road.
//
// THE PARRY TRUTH (the Lyf Cycle, honored):
//   PERFECT — you defend AND your Lyt breaks clean through the
//             foe's guard (the earned opening — never rolled).
//   GOOD    — you defend. The Lyf holds. Chip damage is worthless.
//   MISS    — the foe's Lyt Dims your Anima.
//
// NUMBERS: flat, single-digit, slowly stacking (Small Number
// Law). Windows/durations are temporal and may use percentages.
// Every constant here is a DIAL — tune in playtest, not in chat.
// ============================================================

// ── THE GUARD ROSE — six angles, both paws on the home row ───
// caP'n's rose, 2026-07-26: S D F high, J K L low. The left paw
// rests on SDF, the right on JKL, and neither ever has to move.
//
// THERE IS NO PROMPT ON THE GLASS. The foe's BODY is the prompt.
// It winds up from an angle, an ember arc paints the plane it is
// travelling on, and that arc FILLS toward you as impact nears.
//
// ════════════════════════════════════════════════════════════
// THE SCREEN LAW — added 2026-07-27, and it is the whole reason
// the rose was unreadable before.
//
//   ax / ay are NOT world offsets. They are SCREEN offsets.
//   ax = −1 is the LEFT of the frame. ay = +1 is the TOP of it.
//
// They are resolved against the LIVE CAMERA's right and up
// vectors every frame (`roseWorld` in CinematicStage). That
// means S is up-and-left ON THE GLASS no matter which shot is
// cut to, no matter where Perry is facing, no matter where the
// road has bent to. A camera move can never again silently
// rotate the rose out from under the player's fingers.
//
// The old code mapped ax → world X, which on the qte shots is
// the road axis — the very axis the foe already stands on. All
// six angles collapsed onto one line pointing at the enemy, and
// six distinct answers rendered as the same picture. That bug is
// what this law exists to make impossible.
// ════════════════════════════════════════════════════════════
export const GUARD = {
  // ══════════════════════════════════════════════════════════
  //  THE GRID — relaid 2026-07-27 (caP'n)
  // ══════════════════════════════════════════════════════════
  // The old rose was S D F across the top and J K L across the
  // bottom: the LEFT hand meant HIGH and the RIGHT hand meant LOW.
  // It reads fine written down and it is wrong under the fingers —
  // caP'n, after playing it: "sounded good in theory, in practice
  // not so much." Nothing about your left hand means "up."
  //
  // It is a 2 × 3 grid now, and the mapping is the hand itself:
  //
  //        F  ·  J        top      ← both INDEX fingers (the bumps)
  //        D  ·  K        middle   ← both MIDDLE fingers
  //        S  ·  L        bottom   ← both RING fingers
  //        ↑     ↑
  //     left   right hand
  //
  // Two things fall out of that for free. The COLUMN is which hand,
  // so left-on-screen is left-under-your-hand — the thing the old
  // layout could never say. And the ROW is which finger, the same
  // finger on both hands, so "the row" is a single physical idea
  // instead of six memorised letters. F and J are the home keys
  // with the tactile bumps, which is why they get the top row: it
  // is the only row you can find without looking.
  //
  // THE CENTRE IS GONE. There is no OVERHEAD and no BRACE; every
  // angle now has a side. `lane: 0` no longer exists anywhere, and
  // APPROACH.CENTER_LANE / CENTER_X went with it.
  KEYS: ["F", "J", "D", "K", "S", "L"],   // reading order: top, middle, bottom

  // The canonical groupings. Anything drawing the rose should read
  // ROWS — three rows of two — and never assume two rows of three.
  ROWS: [["F", "J"], ["D", "K"], ["S", "L"]],
  ROW_NAMES: ["HIGH", "MID", "LOW"],
  LEFT: ["F", "D", "S"],
  RIGHT: ["J", "K", "L"],
  // retained so a stale import cannot throw; they mean the TOP and
  // BOTTOM rows now, not the old left-hand/right-hand split.
  HIGH: ["F", "J"],
  LOW: ["S", "L"],

  // SPREAD — how far the grid opens, in world units, resolved on
  // the camera plane. Columns are closer than the old rose and the
  // rows are further apart, because a 2 x 3 grid wants its space
  // spent vertically. Swept against screen projection on the real
  // s_qte_close mark: the closest on-screen pair is a VERTICAL
  // neighbour (rows are always the tight axis with 3 of them and
  // only 2 columns), and these are the numbers that buy the most
  // separation before the bottom row sinks into the dirt.
  // Rows land at world y 1.13 / 0.59 / 0.05 — over his crown, his
  // chest, and the ground itself. (Dials.)
  SPREAD_X: 0.50,
  SPREAD_Y: 0.54,
  CENTER_Y: 0.59,     // height of the grid's middle row on Perry's body
  // ══════════════════════════════════════════════════════════
  //  THE COLOURS — caP'n, 2026-07-27
  // ══════════════════════════════════════════════════════════
  // "maybe we should also colour coordinate them? After the
  //  tutorial the letters go away, the coloured bubbles are still
  //  there."
  //
  // The letter is a SCAFFOLD. The colour is the DURABLE CHANNEL.
  // During the tutorial you learn both at once, which is why the
  // colours are laid out on the same logic the grid is:
  //
  //   ROW = the family      HIGH warm · MID cold · LOW violet
  //   COLUMN = which member  left is the deeper one, right the
  //                          brighter one — same as your hands
  //
  // So "the row is a single physical idea" is now true in the eye
  // as well as in the fingers. Six unambiguous hues, none of them
  // the ember-orange the road itself burns at, none of them the
  // teal Th'rakk burns at, and none of them the PERFECT flash.
  //
  // WHY THIS MATTERS LATER: once a foe starts SHUFFLING the coals
  // (see SIGHT below), the colour is the only thing still telling
  // the truth. A player who learned positions gets moved. A player
  // who learned colours does not. A player who learned to read the
  // CREATURE never looked at the coals in the first place, and is
  // the one this whole ladder is quietly training. (Dials.)
  HUES: {
    F: "#ffb02e", J: "#ff6a3c",   // HIGH — ember gold · ember red
    D: "#3ad6ff", K: "#4a7cff",   // MID  — cyan · deep blue
    S: "#b45cff", L: "#ff5cc8",   // LOW  — violet · magenta
  },
  HUE_NAMES: {
    F: "AMBER", J: "EMBER RED", D: "CYAN", K: "DEEP BLUE",
    S: "VIOLET", L: "MAGENTA",
  },
  DIRS: {
    F: { id: "F", tier: "top", lane: -1, name: "HIGH LEFT",  ax: -1, ay:  1, roll:  0.70, hue: "#ffb02e" },
    J: { id: "J", tier: "top", lane:  1, name: "HIGH RIGHT", ax:  1, ay:  1, roll: -0.70, hue: "#ff6a3c" },
    D: { id: "D", tier: "mid", lane: -1, name: "MID LEFT",   ax: -1, ay:  0, roll:  1.20, hue: "#3ad6ff" },
    K: { id: "K", tier: "mid", lane:  1, name: "MID RIGHT",  ax:  1, ay:  0, roll: -1.20, hue: "#4a7cff" },
    S: { id: "S", tier: "low", lane: -1, name: "LOW LEFT",   ax: -1, ay: -1, roll:  1.55, hue: "#b45cff" },
    L: { id: "L", tier: "low", lane:  1, name: "LOW RIGHT",  ax:  1, ay: -1, roll: -1.55, hue: "#ff5cc8" },
  },
};

export const hueOf = (k) => GUARD.HUES[k] || "#ffb058";

// ══════════════════════════════════════════════════════════════
//  SIGHT — THE LADDER OF CRUTCHES, 2026-07-27 (caP'n)
// ══════════════════════════════════════════════════════════════
// caP'n's spec, verbatim: "Tutorial stage has the letters assigned
// to the buttons... After the tutorial stage, they go away, the
// coloured bubbles are still there but not the letters. You will
// need to level up, change gear, or items etc to be able to see it
// temporarily. Bosses later get creative and mix up the bubbles or
// even later in the game more than likely they're just gone
// forever, which is where the Tomes, Treasures, level ups, Imperas
// come in."
//
// Four rungs. Each one DELETES A CRUTCH AND LEAVES THE TRUE SIGNAL
// INTACT — the fire's real angle never lies at any tier, so every
// rung is survivable by reading the creature, which is the skill
// the title has always been about.
//
//   LETTERED  coal shows its letter AND its colour, at its true
//             angle. The tutorial. Also what a charge buys back.
//   COLOURED  colour only, true angle. The default once the road
//             has been walked. You know the grid; you get the map.
//   SHUFFLED  the coals swap places. Each still burns its own
//             colour, so the colour is now the only truth and the
//             POSITION of the lit coal is a lie. Opt-in per foe.
//   DARK      no coals at all. Read the creature. Opt-in per foe.
//
// THE EXCEPTION LAW, honoured on the wire: `sight` is a STRING
// FIELD on the encounter, never `if (enemy.isBoss)`. A husk can be
// DARK, a Lord can be LETTERED, and neither costs a line of code.
// The list below is the set of rungs that exist TODAY, not the set
// of rungs that may exist.
//   PARTIAL   half the coals burn, half are out — and WHICH half is
//             rolled off the run seed, so a Solrend is consistent
//             with itself and different from the last one. Added
//             2026-07-28 (R-41). It is the rung between COLOURED
//             and DARK, and it teaches the only lesson that
//             matters going into DARK: you already read the
//             creature for three of these. Read it for six.
export const SIGHT = {
  LETTERED: "lettered",
  COLOURED: "coloured",
  PARTIAL: "partial",
  SHUFFLED: "shuffled",
  DARK: "dark",
};

// ══════════════════════════════════════════════════════════════
//  IS THIS ROAD TEACHING? — the one predicate, 2026-07-30
// ══════════════════════════════════════════════════════════════
// THE question the Tutorial hangs off, and the only place it is
// ever answered. Four consumers: the letter floor below, the
// banner, the deathlessness, and the ESC door out.
//
// It is per-ACT and per-SOUL, and it has to be both. `teaches` is
// a fact about the LEVEL — it opts a road in, it is data, so a
// later level that introduces something genuinely new gets a
// teaching pass of its own for one word. `milestone` is the fact
// about the BEB — once you have arrived at the far end once, the
// road has taught you and it stops holding your paw.
//
// This existed as two hand-written copies before today: the floor
// below had the milestone in it and the director's banner did
// not, so the coals correctly stopped being lettered on a second
// walk while the banner went on promising you could not fall.
// Two copies of the same mathematics will drift, and these had.
//
// Stored nowhere — the Derivation Law. Store the milestone,
// compute the lesson. The good half of that: a Soul who deletes
// and starts again is taught again, for free, with no flag to
// clear anywhere.
export const isTeaching = (act, soul) =>
  !!(act?.teaches && !soul?.milestones?.[act.milestone]);

// Resolution order matters, and this is the whole economy:
//   1. the player switched hints OFF entirely → DARK, and no
//      charge is spent (never burn a Tome on someone who asked
//      not to see it)
//   2. a LIVE CHARGE outranks everything, INCLUDING a boss that
//      asked for DARK. That is the entire reason Tomes, Treasures,
//      nodes and Imperas grant charges at all.
//   3. hints pinned ON → LETTERED
//   4. otherwise: what the encounter asked for, and if it asked
//      for nothing, the milestone decides — lettered until the
//      road has been walked once, coloured forever after.
export const resolveSight = ({ soul, hintMode = "auto", enc = null, named = false, act = null }) => {
  if (hintMode === false) return SIGHT.DARK;
  if (named) return SIGHT.LETTERED;
  if (hintMode === true) return SIGHT.LETTERED;
  // ══════════════════════════════════════════════════════════
  // THE LETTER FLOOR — rescoped 2026-07-28
  // ══════════════════════════════════════════════════════════
  // This used to read `!soul.milestones.hollowmere`, and that was
  // right by COINCIDENCE: there was one level, so "has not finished
  // the Cinders Road" and "has never been taught" were the same
  // sentence. Two levels pull them apart.
  //
  // The fact it always MEANT is per-level: has this Soul ever
  // finished THIS level? A first walk down the Cinders Road is
  // lettered; a fifth is not. And a first walk into the fens is
  // NOT lettered, because a beb standing in Hollowmere with a
  // Treasure and three stars lit has been taught — by the Cinders
  // Road, which is what a level that teaches is for.
  //
  // `teaches` opts a level in. It is DATA, not a category: a later
  // level that introduces something genuinely new may want a floor
  // of its own, and that will cost one word.
  //
  // An encounter may only ever make things HARDER than the floor.
  // Th'rakk shuffles the coals for everyone ELSE; it does not
  // shuffle them for a beb who has never seen a coal before.
  //
  // ── ONE PREDICATE, 2026-07-30 ───────────────────────────────
  // This used to compute the teaching test inline, and the
  // director computed its OWN copy for the banner — which had
  // already drifted: the coals correctly stopped being lettered
  // after the first clear while the banner went on promising
  // deathlessness forever. Two hand-maintained copies of the same
  // mathematics will drift. There is one now, `isTeaching`, and
  // everything that asks the question asks it there.
  const floor = act
    ? isTeaching(act, soul)
    : !soul?.milestones?.hollowmere;
  if (floor) return SIGHT.LETTERED;
  return enc?.sight || SIGHT.COLOURED;
};

// ── THE PARTIAL ROLL — which three coals are burning ─────────
// Off the run seed, like everything else, so the fen is the same
// fen twice inside one Solrend and a different one next time.
// Returns a Set of the keys that ARE lit; everything else is out.
// Guaranteed at least one per hand, or a "partial" that darkened
// one whole side would just be a worse DARK with extra steps.
export const rollPartial = (R) => {
  const pick = (arr) => arr[Math.floor(R() * arr.length)];
  const lit = new Set([pick(GUARD.LEFT), pick(GUARD.RIGHT)]);
  const rest = GUARD.KEYS.filter((k) => !lit.has(k));
  lit.add(pick(rest));
  return lit;
};

// A deranged permutation of the six keys — no coal may sit at its
// own angle, or the shuffle would sometimes do nothing and the
// player would learn to distrust a tell that was telling the
// truth. Seeded off the run's roller, so the same Solrend shuffles
// the same way twice (DETERMINISM LAW).
export const rollShuffle = (R) => {
  const keys = GUARD.KEYS;
  for (let guard = 0; guard < 40; guard++) {
    const pool = keys.slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(R() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    if (pool.every((k, i) => k !== keys[i])) {
      const map = {};
      keys.forEach((k, i) => { map[k] = pool[i]; });   // key → angle it HANGS at
      return map;
    }
  }
  // fall back to a rotation, which is deranged by construction
  const map = {};
  keys.forEach((k, i) => { map[k] = keys[(i + 1) % keys.length]; });
  return map;
};

export const dirOf = (k) => GUARD.DIRS[k] || GUARD.DIRS.D;

// ── THE APPROACH LAW — where a strike is allowed to come FROM ──
// caP'n's ruling, 2026-07-27: "what matters is the attack is
// visible from the left." An angle is not a label on a widget;
// it is a place on the screen that the fire has to physically
// arrive from. A foe standing on your right may not deliver a
// HIGH LEFT by wiggling. It has to get there, or send something
// that gets there for it.
//
// Three legal ways, one per `path`:
//
//   "step"  the foe MOVES. It crosses to the lane the blow will
//           come from during the first stretch of its wind-up,
//           and you watch it commit. The most readable thing in
//           the game, and the default.
//   "hook"  the foe stays put and the fire LOOPS — out wide,
//           around, and back in from the far side. caP'n's
//           boomerang. Used when a foe won't yield ground or
//           when a follow-up beat has no time to walk.
//   "fall"  no lane at all — it arrives on the vertical, from the
//           height its ROW names. Rewritten with the Grid Relay,
//           2026-07-27: the top row (F/J) drops out of the dark,
//           the bottom row (S/L) comes up off the dirt, and the
//           middle row (D/K) arrives LEVEL. It used to be a
//           boolean — "overhead, or everything else" — which had
//           no answer left once the centre was deleted.
//
// APPROACH_FRAC is how much of a wind-up is spent travelling
// before the coil begins. Below it the foe is moving; above it
// the foe is winding. The split is what makes a fast attack feel
// like a scramble and a slow one feel like a decision.
export const APPROACH = {
  FRAC: 0.46,          // fraction of wind spent getting into place
  LANE_X: 1.24,        // how far to the side of Perry a lane sits
  LANE_Z: 0.34,        // UPSTAGE units — away from the lens, so a foe in a
                       // lane can never stand in front of the guard it is
                       // aiming at. (Sign verified by screen-projection sim;
                       // it was negative and put every foe downstage.)
  // CENTER_X / CENTER_LANE deleted 2026-07-27 with the centre
  // itself. Every angle owns a side now, so no foe can ever be
  // asked to stand dead behind Perry in the first place, and the
  // special case that used to nudge it off-centre has nothing
  // left to correct.
  HOOK_OUT: 2.30,      // how far outside the frame a hook swings
  SPEED_CAP: 7.5,      // world units/sec a foe may scramble (dial)

  // ── THE WRAP — added 2026-07-27 with the Chain Kunai ──
  // A fourth path. The foe does NOT move: it plants, throws the
  // blade clean PAST you into the dark, and then hauls. The thing
  // that kills you is already behind your head before the fight
  // tells you which side it is coming back on.
  WRAP_BEHIND: 2.45,   // how far past Perry the blade flies, on the camera plane
  WRAP_LIFT: 0.55,     // how high it hangs back there while the chain pays out
  WRAP_OUT: 3.20,      // how far outside the frame the return curve bulges
  PLANT_X: 0.30,       // a planted foe stands barely off Perry's centre line
  PLANT_Z: 1.15,       // ...and well upstage, so it is never in his way
};


// ── QTE BASELINE — THE canon baseline for every future strike ──
// (caP'n: the burning-husk grab at Reedbrook is the measuring
//  stick all later, faster, meaner strikes are tuned against.)

export const QTE = {
  KEY_POOL: GUARD.KEYS,   // kept as a name; the pool IS the rose now
  WINDOW_MS: 1400,       // telegraph appears → impact
  INPUT_OPEN_MS: 260,    // presses before this are ignored, not punished
  COMMIT_MS: 320,        // the last N ms before impact: a WRONG angle now COMMITS to a MISS.
                         //   Before it, a wrong angle is a probe — ignored, free, correctable.
                         //   This is what makes six angles fair instead of cruel.
  PERFECT_MS: 150,       // |press − impact| ≤ this → PERFECT
  GOOD_EARLY_MS: 450,    // press up to this EARLY of impact → GOOD
  GOOD_LATE_MS: 180,     // press up to this LATE of impact → GOOD
  MISS_TIMEOUT_MS: 180,  // after impact + this with no press → MISS
  FEINT_HOLD_MS: 900,    // a feint telegraphs this long; pressing during it = MISS
  GUARD_HOLD_MS: 260,    // how long the Treasure sits at an angle after a press
  BEAT_GAP: [700, 1250], // default ms between strikes — the fight's breathing
  HINT_FADE_MS: 260,     // how fast the training rose fades in/out
  // ── THE CHORD, 2026-07-28 ──
  // How far apart two halves of a chord may land and still count
  // as one press. 140 ms is roughly the spread between two hands
  // trying to do the same thing, and it is deliberately generous:
  // the difficulty of a chord is knowing WHICH TWO, not being a
  // metronome with your knuckles. The later half is what gets
  // timed against impact, so a sloppy-but-together pair grades on
  // its slowest hand. (Dial.)
  CHORD_MS: 140,
  // ── THE HOLD ──
  // Slack on the release. Let go inside this and the arc still
  // counts as ridden — a keyboard bounce should never cost a fight.
  HOLD_SLACK_MS: 90,
};

// ── THE ATTACK GRAMMAR — what a foe can actually DO ──────────
// Every strike is one of these. `angles` is the pool of guard
// directions it may arrive from; the roll picks one per strike,
// seeded, so a fight is DIFFERENT every Solrend but reproducible
// within one. `arc` tells the stage which telegraph to paint.
//
// perfect/good are MULTIPLIERS on the QTE bands — the shape of
// each attack's window, not new numbers to remember.
//
// ════════════════════════════════════════════════════════════
// THE EXCEPTION LAW — caP'n, 2026-07-27
// ════════════════════════════════════════════════════════════
//   "let's never ever get too Dogmatic, always leave room for
//    THE EXCEPTION."
//
// Three fields exist purely so the next idea needs data and not
// an engine change. Nothing in this table is a category; they are
// three independent knobs and any combination is legal:
//
//   path        WHERE it comes from — step · hook · fall · wrap
//   body        WHAT THE FOE DOES  — swing · kick · spin · overhead
//                                    · cast · plant
//   projectile  WHAT THE STAGE DRAWS — none · orb · kunai · comet
//                                      · jet · coin
//
// A thing that spin-kicks a comet at you is `body:"kick"` +
// `projectile:"comet"` + `path:"hook"`, and the only new code in
// the whole Empire is the comet's geometry. Fists, kicks, flying
// kicks, fireballs, guns, slices, comets, waterjets, coins,
// Terror cards, Divine Crests and dolphins with lasers on them
// are all already legal moves in this grammar. They are not in
// the TUTORIAL because the tutorial is three minutes long — that
// is a content decision, never a systems limit. See Roadmap R-40.
//
// ── THE DROWNED MILE PASS, 2026-07-28 — three more knobs, all
//    orthogonal to the three above and to each other ──
//
//   chord    TWO angles at once, one per hand. `chord: true` and
//            the roller draws one key from GUARD.LEFT and one from
//            GUARD.RIGHT. Both must be down inside CHORD_MS; the
//            LATER of the two is what gets timed. This is the
//            thing the grid bought us — on the old rose a chord
//            was two fingers of one hand, which is not a thing
//            hands do. (R-25, open since the overhaul.)
//   hold     ms you must KEEP the angle down after meeting it.
//            You do not answer a beat, you RIDE an arc. Let go
//            early and the rest of it lands. (R-25, same.)
//   bounty   ⋔ paid on a PERFECT against this strike, on top of
//            the standard purse. This is how a thing that throws
//            MONEY at you is worth answering, and it is a data
//            field so the next thing that throws money needs no
//            code at all.
// ════════════════════════════════════════════════════════════
export const ATTACKS = {
  swipe: {
    id: "swipe", name: "Swipe", arc: "wide", tell: "#ff8a3c",
    verb: "sweeps a burning arm",
    wind: 1400, angles: ["S", "F", "J", "L"], perfect: 1, good: 1,
    path: "step",          // it walks around to the side it wants
    body: "swing",
  },
  lunge: {
    id: "lunge", name: "Lunge", arc: "thrust", tell: "#ffd08a",
    // THE FIRST EXCEPTION, and it cost one word. A husk that
    // "throws its whole ruined weight at you" was never swinging
    // an arm — it was leaving the ground. `body:"kick"` and the
    // same attack is now a flying kick, with no other change to
    // any file. That is the whole point of the field.
    verb: "leaves the ground entirely",
    wind: 980, angles: ["D", "K"], perfect: 1, good: 0.85, closes: true,
    body: "kick",
    // `fall` is ROW-driven now, so the Lunge survived the relay
    // untouched: D and K are the middle row, and the middle row
    // arrives LEVEL. A thrust that comes in flat at chest height
    // from the side it committed to is a better Lunge than the
    // old "overhead or ankles" one ever was.
    path: "fall",
  },
  slash: {
    id: "slash", name: "Slash", arc: "thin", tell: "#ff5a3c",
    verb: "rakes — twice, and the second is faster",
    wind: 860, angles: ["S", "F", "J", "L"], perfect: 0.9, good: 0.8,
    double: 520,   // ms to the second beat. Twin Needles answers both with one press.
    path: "step", echoPath: "hook", body: "swing",
    // The first rake it walks to. The second has no time to walk,
    // so it HOOKS — the same claw whips around the far side. That
    // is why the mirror is readable instead of arbitrary.
  },
  hurl: {
    id: "hurl", name: "Hurl", arc: "orb", tell: "#ffb058",
    verb: "tears a piece of the fire loose and throws it",
    wind: 640, travel: 1150, angles: ["S", "D", "F", "J", "K", "L"],
    perfect: 1.15, good: 1.3,   // generous bands — but the tell starts far away
    path: "hook",          // thrown fire curves. It always did; now it means something
    body: "cast", projectile: "orb",
  },

  // ── THE CHAIN KUNAI — Th'rakk's answer, and the boss beat ────
  // caP'n's spec, verbatim: "stand in front of you, throw it
  // behind you and have it come from the side somewhere."
  //
  // It is the only attack in the game whose telegraph is not in
  // front of you. The warden plants — it does NOT take a lane —
  // and throws the blade clean past your ear into the dark. For
  // most of a second the only thing on screen is a chain running
  // off the edge of the frame, going slack. Then it HAULS, the
  // slack snaps out of the chain, and the blade comes back in on
  // an angle rolled fresh off the run seed. Never the same side
  // twice running, never the same speed twice — the wind scale
  // rides the aggro ladder like everything else.
  //
  // The three fractions below are OF THE WIND, not of the whole
  // strike, and `makeStrike` converts them into both absolute
  // timestamps (for the stage) and normalized ones (for the body,
  // which only ever sees `prog`). One source, two consumers.
  chain: {
    id: "chain", name: "Chain Kunai", arc: "chain", tell: "#6fe6d4",
    verb: "plants, and throws the blade past your ear",
    wind: 1500,            // plant → throw → the blade hangs behind you
    travel: 900,           // the haul: the return flight you actually answer
    angles: ["S", "D", "F", "J", "K", "L"],
    perfect: 1.05, good: 1.25,   // generous — the tell is behind your head
    path: "wrap", body: "plant", projectile: "kunai",
    cast: 0.24,            // fraction of the wind at which the blade leaves the hand
    behind: 0.62,          // ...at which it reaches the point past your ear
  },
  feint: {
    id: "feint", name: "Feint", arc: "wide", tell: "#f4726a", feint: true,
    verb: "draws back — and does not come",
    wind: 900, angles: ["S", "D", "F", "J", "K", "L"],
    path: "step",          // a lie has to be told from somewhere believable
    body: "swing",
  },
  // ══════════════════════════════════════════════════════════
  //  THE DROWNED MILE — six new verbs, zero new subsystems
  // ══════════════════════════════════════════════════════════
  // caP'n's catalogue, R-40: "enemies should attack with their
  // hands, kicks, spin kicks, flying kicks, fireballs, guns,
  // slices, slashes, comets, waterjets, sending damn dolphins with
  // lasers on em, coins, Terror cards!, divine crests (mini goblin
  // game ;3)."
  //
  // Every one of these is DATA. Not one of them needed a new
  // engine path. That is the Exception Law paying its rent.

  // ── THE SPIN KICK ── caP'n's list, item one.
  // A hound does not swing an arm at you. It turns its whole body
  // into the blow and comes back around facing you again.
  roundhouse: {
    id: "roundhouse", name: "Roundhouse", arc: "wide", tell: "#d8c2a8",
    verb: "turns its whole body into the blow",
    wind: 900, angles: ["S", "F", "J", "L"], perfect: 0.95, good: 0.9,
    path: "step", body: "spin",
  },

  // ── THE COMET ── an orb, a long trail, and it comes DOWN.
  // The tell is the sky, not the creature — which is exactly why
  // it belongs on the road AFTER the one that teaches you to read
  // creatures.
  comet: {
    id: "comet", name: "Cinderfall", arc: "orb", tell: "#ff7a4a",
    verb: "throws a piece of the sky at you, and the sky agrees",
    wind: 820, travel: 900, angles: ["F", "J", "D", "K"],
    perfect: 1.1, good: 1.2,
    path: "fall", body: "cast", projectile: "comet",
  },

  // ── THE WATERJET, and the first HOLD in the game ──
  // It does not arrive and leave. It arrives and KEEPS arriving.
  // You meet it on the beat like anything else, and then you stand
  // in it. Letting go early is the same as never having answered:
  // the rest of the jet goes straight through the gap you opened.
  jet: {
    id: "jet", name: "Drown", arc: "jet", tell: "#3fb8a0",
    verb: "opens, and does not stop opening",
    wind: 1000, travel: 260, hold: 620,
    angles: ["S", "D", "F", "J", "K", "L"], perfect: 1.15, good: 1.15,
    path: "step", body: "cast", projectile: "jet",
  },

  // ── THE UNDERTOW ── the hold again, low and long and mean.
  // Same verb, different sentence: no projectile, comes up out of
  // the water on the bottom row, and holds nearly a full second.
  undertow: {
    id: "undertow", name: "Undertow", arc: "thin", tell: "#2f7f86",
    verb: "takes hold of your ankles from underneath",
    wind: 1150, hold: 900, angles: ["S", "L"], perfect: 1.2, good: 1.25,
    path: "fall", body: "cast",
  },

  // ── THE COIN STORM ── caP'n: "coins, ... divine crests (mini
  // goblin game ;3)". A Mudlark throws money at you. It RICOCHETS
  // — one bounce off the causeway before it reaches you, which is
  // why the arrival angle is not the angle it left on.
  //
  // `bounty: 1` means a PERFECT here pays you the coin. You are
  // not defending yourself from a Mudlark. You are being paid to
  // stand still and be excellent.
  coinstorm: {
    id: "coinstorm", name: "Coin Storm", arc: "orb", tell: "#e8c264",
    verb: "throws money at you, which is somehow worse",
    wind: 700, travel: 780, angles: ["S", "D", "F", "J", "K", "L"],
    perfect: 1.1, good: 1.25, bounty: 1,
    path: "hook", body: "cast", projectile: "coin",
  },

  // ── THE CLEFT ── the CHORD. Two angles at once, one per hand.
  // The Disciple's signature and the reason it is a Champion: it
  // is the first thing on any road that asks for two answers in
  // the same instant. On the old rose this was two fingers of one
  // hand and unshippable. On the grid it is one key from each
  // hand — F+L, D+K, S+J — which is a thing hands actually do.
  cleft: {
    id: "cleft", name: "The Cleft", arc: "wide", tell: "#c084f0",
    verb: "brings the bill down through two places at once",
    wind: 1350, angles: ["F", "D", "S"], angles2: ["J", "K", "L"],
    perfect: 1.15, good: 1.1, chord: true,
    path: "step", body: "overhead",
  },

  calamity: {
    id: "calamity", name: "CALAMITY", arc: "storm", tell: "#4ff0dc",
    verb: "opens itself and lets the fire out of it",
    wind: 1500, beats: 3, beatGap: 640, hold: 900,
    angles: ["S", "D", "F", "J", "K", "L"], perfect: 1, good: 0.9,
    path: "hook",          // it stops moving its feet and throws fire everywhere
    body: "cast",
  },
};

// THE CALAMITY LAW — three angles in a row, then a held brace.
// Answer every beat and the foe FRACTURES: a full-beat opening
// where your next answer lands free. Drop one beat and the whole
// calamity Dims you at once. It is the biggest swing in the game
// in both directions, which is why it is called that.


// ── RESOURCE DIALS ───────────────────────────────────────────
// ══════════════════════════════════════════════════════════════
//  THE CURRENCY REGISTRY — IE ECONOMY LAW v1 §8
// ══════════════════════════════════════════════════════════════
// Canonical block. Keep this shape byte-identical across every IE
// title so a reader who has seen one has seen them all.
//
// Perry mints KINDLING (⋔) — 2026-07-28, caP'n's ruling. All four
// ingredients present: a name, a symbol, an owning app (this one,
// and only this one), and a lane at the Crest Well.
//
//   name .... Kindling. What you feed a fire that is not big
//             enough yet. Reedbrook children are sent out for it
//             before they are trusted with anything that burns.
//   symbol .. ⋔ — two twigs crossed. HUD/log fallback ONLY; the
//             real symbol is a WebP on the CDN (see ie-cdn-assets).
//             If the glyph ever renders wrong on a device, swap
//             this one constant and every surface follows.
//   owner ... Perry writes profiles/{uid}/kindling. Nobody else
//             writes it. Perry writes nobody else's.
//   rate .... 1 ◆ = 120 ⋔, filed with Pierre. Provisional — set
//             from an estimated ~500 ⋔/hour against the Empire
//             reference of 3–5 ◆/hour. Re-file the moment payouts
//             move (Toil Law: a rate is a living number).
//
// ◆ DIVINE CRESTS are nobody's. Perry may GRANT them, from
// one-time sources only, and may SPEND them on the top shelf only.
// Perry may never build an exchange. That is the Well's, at
// W'ily's Pier, and the Well is Pierre's alone. Absolute.
export const CURRENCIES = {
  DC: { id: "DC", name: "Divine Crests",   short: "DC", path: "divineCrests",   icon: "◆" },
  KD: { id: "KD", name: "Kindling",        short: "KD", path: "kindling",       icon: "⋔" },
};

// Convenience — the two glyphs, in one place, so no surface ever
// hardcodes one. Change the WebP, change the fallback, done.
export const GLYPH = { dc: CURRENCIES.DC.icon, kd: CURRENCIES.KD.icon };

// ── PERRY'S ONLY LEGAL ◆ FAUCET ───────────────────────────────
// Achievements, and nothing else, ever. Each one is one-time and
// idempotent — it cannot fire twice for the same account — which
// is exactly and only what the Faucet Law permits. Scaled by the
// shared IE rarity ladder, so a title that later grants harder
// achievements pays out more without anyone editing a number.
//
// Commons pay nothing on purpose. "Land your first parry" should
// not put Empire reserve currency in a beb's paw; it should feel
// like the tutorial it is. The ladder starts where the game does.
//
// Read against the top shelf: the Clapper is 8 ◆, which is one
// mythic achievement, or a legendary and two epics, or a long
// patient sweep of the rest. That is the intended shape — the
// hardest thing you ever did buys the heaviest thing you can lift.
export const CREST_BOUNTY = {
  common: 0,
  uncommon: 1,
  rare: 1,
  epic: 2,
  legendary: 4,
  mythic: 8,
};

// ── THE COMMON CAP, hoisted 2026-07-30 ───────────────────────
// `ACT_TIERS` inside RULES needs this value, and an object literal
// cannot read its own fields while it is being built. One const,
// referenced twice, so the cap can never be two different numbers
// in two places — the drift caP'n's own law warns about.
const RULES_COMMON_ANIMA = 2;

export const RULES = {
  ANIMA_BASE: 1,          // a fresh beb: one grab and you fall (the pitch)
  // ── CEILINGS RAISED 2026-07-28 with the second act ──
  // Both were set when there was one road, one village and one
  // milestone. There are two of each now and the constellation
  // grew a fourth Heart and a third Breath to match. Still single
  // digits, still the Small Number Law, still a beb. (Dials.)
  ANIMA_NAT_CAP: 6,       // natural ceiling via milestones + nodes
  CHAO_BASE: 1,
  CHAO_NAT_CAP: 6,        // the Growth Law ceiling (Deviants lock below it forever)

  // ══════════════════════════════════════════════════════════
  //  LYT — FIXED. THE ONE STAT NO STAR MAY TOUCH (caP'n, 2026-07-29)
  // ══════════════════════════════════════════════════════════
  // "You cannot upgrade it. There are ways around it — items,
  //  treasures, weapons — I just meant we cannot spend our skill
  //  points on it."
  //
  // So the rule is precise, and the precision is the whole point:
  // ASCENSION may never buy Lyt. Everything else still may. What
  // you ARE (species) and what you are HOLDING (Treasure, Tome)
  // decide your edge; what you have LEARNED never does. It makes
  // the weapon rack matter more than the sky, which is the correct
  // shape for a game about a beb who never grows up.
  //
  // Enforced in one place — `deriveLyt` no longer reads `fx.lyt`
  // off any node, and `nodeFx` refuses to define it. See THE LYT
  // REFUSAL below the tracks.
  LYT_BASE: 1,

  // ══════════════════════════════════════════════════════════
  //  LYF — YOURS NOW, AND IT IS BREATH, NOT SKIN (2026-07-29)
  // ══════════════════════════════════════════════════════════
  // A foe's Lyf is what you chip through. YOUR Lyf is how many
  // answers you can get WRONG back to back before your paws stop
  // listening to you.
  //
  // ONE. That is the base, and it is meant to read as brutal: a
  // fresh beb who whiffs a single parry is winded, and a winded
  // beb in front of a husk is a dead beb. Every way out of that is
  // something you went and got.
  //
  // A LANDED ANSWER COSTS NOTHING. Parry cleanly forever and your
  // breath never moves — stamina in this game is not a budget for
  // playing, it is a penalty for flailing. Spam the grid and the
  // grid stops answering.
  LYF_BASE: 1,
  LYF_NAT_CAP: 5,
  // How long your paws are somebody else's while you get them back.
  // The fen does not wait for you — strikes keep coming, and every
  // one that lands while you are winded lands for free.
  EXHAUST_MS: 3000,
  // Each LEVEL of the Sharpened Answer track buys this much of it
  // back. Three levels → 3000 → 2400 → 1800 → 1200. (Dials.)
  EXHAUST_PER_LEVEL: 600,
  EXHAUST_FLOOR: 900,     // no beb ever recovers instantly
  // ── WHAT A BLOW COSTS WHILE YOU CANNOT ANSWER (2026-07-30) ──
  // A winded beb's input is refused outright, so every strike
  // during a wind is a free hit on something that is not allowed
  // to be in the fight. Charging T'yphiz's full Lyt 3 for each of
  // those turned one bad answer into a death sentence. Flat 1 —
  // frightening when it stacks, survivable when you have bought
  // the Sharpened Answer track down. (Dial.)
  WINDED_HIT: 1,

  // ══════════════════════════════════════════════════════════
  //  THE RANK LADDER — THREE LEVELS, EVERYWHERE, FOREVER
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-07-29: "no, 3 levels is the universal ceiling —
  //  the highest level of powers etc are U'hltis, they are level 3."
  //
  // Every ranked thing in the Empire tops out at THREE. Not four
  // for the good ones, not five for the endgame. Three. An Art
  // knows it (Primera 1 → Impera 2 → U'hlti / Emperium 3) and now
  // every stat track knows it too, which means a player learns the
  // shape ONCE and it is true of everything they will ever touch.
  //
  // RANKS are the small unit. LEVELS are what ranks buy. Five
  // ranks is a level; three levels is the ceiling; fifteen ranks
  // is a maxed track and there is no sixteenth.
  //
  // THE GEAR SEAM — this is the feature, not a footnote:
  //   "to get to level 1 you need 5 ranks. You get a weapon with a
  //    passive of 3 ranks toward exhaustion recovery — now you only
  //    need 2 if you have it equipped."
  //
  // A Treasure does not hand you a LEVEL. It hands you RANKS, into
  // the same pool your Ascension points go into, and the level
  // falls out of the total. So gear and stars are genuinely
  // interchangeable currency toward the same ceiling — and taking
  // the weapon off drops you back down, honestly, in the open.
  RANKS_PER_LEVEL: 5,
  LEVEL_CAP: 3,

  // ══════════════════════════════════════════════════════════
  //  THE TWO-CURRENCY LAW — Renown is retired (caP'n, 2026-07-29)
  // ══════════════════════════════════════════════════════════
  // "Renown and Kindling are going to be one single currency now.
  //  You can either spend it on weapons or use it to level up. We
  //  do not want so many currencies — typically 2 is all we will
  //  ever need."
  //
  // So Perry now runs the Empire's standard pair and nothing else:
  //   ⋔ KINDLING — minted here, buys EVERYTHING local, including
  //                 Ascension. The road pays it; the counter and
  //                 the sky both take it.
  //   ◆ DIVINE CRESTS — the Empire's reserve. Top shelf only.
  //
  // WHAT THIS COSTS US, SAID OUT LOUD. Ascension points used to be
  // DERIVED from an unspent, unspendable Renown pool — pure Law 14.
  // A currency that buys two things cannot be derived from, because
  // the axe and the star are now competing for the same coin, and
  // that competition is the entire reason caP'n merged them. So
  // `soul.ascensions` is STORED: a count of points bought, add-only,
  // never decremented. We still derive what matters — how many are
  // FREE — from (bought − spent on nodes). Store the input, compute
  // the output. Law 14 survives; it just moved up one level.
  ASCEND_COST: 20,        // ⋔ per Ascension point, bought at the sky

  // ── THE PERFECT STREAK — caP'n, 2026-07-29 ──
  // "Successful PERFECT back-to-back parries, starting at 3, give
  //  you a little extra currency."
  //
  // Not a milestone table like the chain grants — a per-answer
  // bonus that switches ON at the third clean answer in a row and
  // stays on until you drop one. Miss, take a GOOD, get grabbed,
  // and it is gone; the fourth PERFECT after a GOOD is a first
  // PERFECT again. It pays a beb for being *precisely* right
  // repeatedly, which is a different virtue from surviving.
  STREAK_AT: 3,
  STREAK_BONUS: 1,        // extra ⋔ per PERFECT once the streak is lit

  // ── THE ANIMA CEILING — caP'n, 2026-07-29 ──
  // Five, and no more than five things ever hold it. This is not a
  // clamp the code enforces on a data row; it is a BUDGET, and a
  // budget is only real if somebody writes down who is spending it.
  // So: `animaLedger` below is the whole list, and the day it has
  // six names in it, one of them is wrong.
  ANIMA_CEILING: 5,
  // ── NOBODY HOLDS IT YET, AND THAT IS CORRECT (caP'n, 2026-07-29) ──
  // "the 5 enemies with 5 Anima... they only have 3 now — all
  //  others are either 2 Anima or 1."
  //
  // So a seat is a RESERVATION, not a current value. T'yphiz owns
  // seat one and stands at Anima 3 today; he climbs to 5 when the
  // road behind him is long enough to have earned it. Every foe
  // that is NOT holding a seat is capped at 2 — and that, not the
  // ceiling, is the rule that does the day-to-day work. `anima: 3`
  // on anything without a seat here is a bug.
  ANIMA_CEILING_SEATS: [
    "T'yphiz — the Champion at the end of the Cinders Road (reserved · at 3 today)",
    "the Warden Beneath — Act I's one secret boss (built, unplaced · at 2 today · R-52)",
    "— unassigned —",
    "— unassigned —",
    "— unassigned —",
  ],
  // What everything without a seat may not exceed. This is the
  // number the bestiary actually lives at.
  //
  // RESTATED 2026-07-30, because there are now three ceilings and
  // they are not the same ceiling:
  //   · a COMMON foe (no `boss`)      → this, always, every act
  //   · a BOSS                        → `ACT_TIERS[act].bossAnima`
  //   · a CEILING HOLDER (`animaSeat`)→ ANIMA_CEILING, 5, five ever
  // caP'n's act-tier ruling lets an Act II boss reach 4 — that is
  // a BOSS allowance, not a loosening of this line. Commons stay
  // at 2 forever. `auditBestiary()` enforces all three.
  ANIMA_COMMON_CAP: RULES_COMMON_ANIMA,

  // ── THE FOE LYF CEILING — caP'n, 2026-07-29 ──
  // A foe's Lyf tops out at 5, same number as the Anima Ceiling,
  // for the same reason: a wall that keeps climbing stops meaning
  // anything. Unlike Anima there is no seat list — any number of
  // foes are ALLOWED to sit at 5 — but 5 is still meant to read as
  // rare, so the hardest walls in the game earn it, not everything
  // wearing a `boss` flag by default.
  FOE_LYF_CEILING: 5,

  // ══════════════════════════════════════════════════════════
  //  THE THREE TIERS — caP'n, 2026-07-30
  // ══════════════════════════════════════════════════════════
  // "let's do it easy. Act 1 is early game, Act 2 Midgame, Act 3
  //  Official End game."
  //
  // WHY THIS TABLE EXISTS AT ALL. Under the regenerating wall a
  // foe's real size is `anima × lyf`, not either number alone —
  // T'yphiz at Anima 3 / Lyf 5 is a FIFTEEN-DEEP wall, and every
  // number in the bestiary was authored under the old break-once
  // rule where it was merely five. Left alone, Act I's boss would
  // have become harder than the Champion at the end of Act II.
  //
  // So the caps are data, `auditBestiary()` checks every row
  // against them, and a fourth act is a row here and nothing else.
  //
  // ANIMA IS THE MULTIPLIER NOW, and that is the design lever
  // worth understanding: high Lyf / low Anima is a short sharp
  // wall you hit once. Low Lyf / high Anima is a long grind of
  // many small walls. They are genuinely different fights at the
  // same total size, which is exactly the variety caP'n asked for
  // — "don't just give every high Lyf and high Anima haha."
  ACT_TIERS: {
    act1: {
      id: "act1", name: "EARLY GAME",
      // "Act 1 should never have one of those tough bosses maybe 1?
      //  for like a supppper secret boss?"
      bossAnima: 2, bossLyf: 3,
      commonAnima: RULES_COMMON_ANIMA, commonLyf: 1,
      note: "A beb with a branch and no stars must be able to finish this road.",
    },
    act2: {
      id: "act2", name: "MIDGAME",
      bossAnima: 4, bossLyf: 3,
      commonAnima: RULES_COMMON_ANIMA, commonLyf: 3,
      note: "Expects a weapon and a level or two. Not a build.",
    },
    act3: {
      id: "act3", name: "END GAME",
      // "Act 3 only the one restriction I gave you" — the Anima
      // Ceiling, seat-gated, and the Lyf ceiling that has always
      // applied to everything.
      bossAnima: 5, bossLyf: 5,
      commonAnima: RULES_COMMON_ANIMA, commonLyf: 4,
      note: "Expects end game items and Arts. Come correct or do not come.",
    },
  },

  // ══════════════════════════════════════════════════════════
  //  THE KINDLING LANE — Perry's own currency (IE ECONOMY LAW v1)
  // ══════════════════════════════════════════════════════════
  // Everything below is ⋔ KINDLING, minted by Perry, owned by
  // Perry, spent at Whyllie's Counter. It is NOT Divine Crests.
  //
  // WHY THE SPLIT, 2026-07-28. Every one of these is a REPEATABLE
  // faucet — you can walk the road again and earn them again,
  // forever. Under the Faucet Law that disqualifies them from ◆:
  // a parry game is the easiest grind loop in the Empire, and a
  // repeatable ◆ faucet on it would have printed money for every
  // other title inside an afternoon. So the whole repeatable loop
  // moved into a lane of Perry's own, 1:1, no number changed.
  //
  // ◆ Divine Crests still reach Perry — from ONE-TIME sources
  // only (achievements), and they buy exactly one thing: the
  // top shelf. See CURRENCIES below and Whyllie's Counter.
  KINDLING_ARRIVE: 20,      // ⋔ granted on reaching Hollowmere
  // Per-act override; falls back to KINDLING_ARRIVE. A longer, meaner
  // road pays more for surviving it. (Dial — see R-35, the ⋔ lane
  // is still the biggest open economy question in the title and
  // this pass makes it bigger, not smaller. Deliberately.)
  KINDLING_ARRIVE_ACT2: 45,
  // ══════════════════════════════════════════════════════════
  //  ⋔ THE TUTORIAL PAYS — caP'n, 2026-07-30
  // ══════════════════════════════════════════════════════════
  // "you should get some extra Kindling after all if you actually
  //  complete the Tutorial, and you gain extra just like the
  //  normal game, so defeating an enemy would reward the players
  //  with extra Kindling."
  //
  // Two halves, and only one of them was missing. The teaching
  // road ALREADY pays like the normal game — every parry, every
  // breach, every Ruin runs through the same `purse` as the fen
  // does, because the director never had a branch for what road
  // it was on. That half was already right.
  //
  // The missing half is this: WALKING it beats SKIPPING it. The
  // door out is real and it is caP'n's own design, so the road
  // has to be worth more than the door — otherwise the only
  // reward for playing the tutorial is having played it.
  //
  // Banked on the arrive beat and settled with the rest of the
  // purse in ONE transaction, like every other ⋔ in the title.
  // Paid on the walk ONLY: a skipped road still hands you the
  // arrival grant at the far end (that is what "go to the Kit Up
  // and get your proper rewards" means) and nothing beyond it.
  // (Dial.)
  KINDLING_TUTORIAL: 25,
  KINDLING_CHAIN_10: 5,     // ⋔ chain milestone grants (delta transactions only)
  KINDLING_CHAIN_30: 15,
  KINDLING_CHAIN_50: 30,
  // ── ⋔ PER ANSWER — caP'n, 2026-07-27 ──
  // "every single correct parry, perfect awards more and so on,
  //  awards currency, that way even if you can't make it to the
  //  village you can still eventually level up and try to progress
  //  that way."
  //
  // This closes a real hole, and the merge of 2026-07-29 made it
  // the ONLY hole that mattered: ⋔ is now the road's pay, the
  // counter's price AND the sky's price at once, so a player who
  // keeps dying on the ridge is still, slowly, buying the thing
  // that gets them past it. Nothing about a bad run is wasted.
  //
  // NOT WRITTEN PER BEAT. They accumulate in a ref and settle in
  // ONE delta transaction at the end of the run — a transaction
  // per parry would hammer the database and stutter the loop. The
  // run is the unit of truth, not the beat.
  // (All dials. See Roadmap R-35 — this is now the top economy
  //  item to watch, ahead of the chain grants.)
  KINDLING_GOOD: 1,         // ⋔ you turned it aside
  KINDLING_PERFECT: 2,      // ⋔ you turned it aside AND bit back
  KINDLING_HELD: 1,         // ⋔ you did not fall for the lie
  KINDLING_FRACTURE: 5,     // ⋔ you ate a whole Calamity
  // ── ⋔ BREACH — the recurring wall, split off 2026-07-30 ──
  // Stripping a foe's Lyf used to pay KINDLING_FRACTURE, and under
  // the break-once rule that fired exactly once per fight so the
  // two could share a constant. The wall REGENERATES now (see
  // `bite`), so it fires once per Anima — T'yphiz would have paid
  // 15 ⋔ in walls alone and the whole lane would have inflated
  // sideways without a single design decision behind it.
  //
  // Two events, two words, two numbers. A Breach is a thing you do
  // several times in one fight and it pays like one. A FRACTURE is
  // surviving an entire Calamity and it stays rare and it stays 5.
  KINDLING_BREACH: 2,       // ⋔ you opened the skin (repeatable within a fight)
  KINDLING_RUIN: 3,         // ⋔ you broke the thing
  KINDLING_RIDDEN: 2,       // ⋔ you met an arc and stayed in it (the HOLD)
  // ── THE TOIL RATE, filed with Pierre 2026-07-28 ──
  // Display only. The LIVE rate is Pierre's, at
  // pierre/exchange/rates/KD, and it may move without a deploy on
  // our side. Never price anything off this constant; it exists so
  // the Counter can print a legible line for a player who has
  // never been to the Pier.
  TOIL_RATE_HINT: 120,      // ⋔ per ◆, provisional
  // ── SIGHT — how many strikes a charge names ──
  SIGHT_NODE: 1,          // per encounter, from the constellation
  SIGHT_THIRD_EYE: 2,     // the Impera names what it foresees
  SIGHT_PAGE: 3,          // the consumable Tome, once per Solrend
  WALK_SPEED: 2.2,        // world units / second on the road
  // ── THE TUTORIAL'S TWO CLOCKS, 2026-07-30 ──
  // How long the first ESC stays armed before the door forgets
  // it was asked. Two presses, because ESC is also the key every
  // menu in this title closes with and one stray tap must never
  // be able to end the road. (Dial.)
  SKIP_ARM_MS: 2600,
  // The report card refuses input for this long after it rises,
  // so the key you were mid-parry with cannot dismiss the thing
  // before your eyes have reached it. (Dial.)
  CARD_LOCK_MS: 550,
  // ── THE AGGRO LADDER — a fight is not a script ──
  // Each point of Anima a foe loses tightens its wind-up and
  // sharpens its taste for lies. A wounded thing fights worse
  // for itself and worse FOR YOU at the same time.
  AGGRO_TIGHTEN: 0.08,    // wind-up shortens this much per Anima lost
  AGGRO_LIE: 0.9,         // feint weight multiplies by this ^ (-anima lost)
  AGGRO_FLOOR: 0.62,      // no foe may ever wind up faster than this fraction
  FRACTURE_MS: 1600,      // the opening a survived Calamity buys you
  // ══════════════════════════════════════════════════════════
  //  THE GRIMOIRE — how much of your power you can actually carry
  // ══════════════════════════════════════════════════════════
  // FIVE. Ten powers exist; you take five onto the road, and the
  // five you took are keys 1 2 3 4 5 in the order you slotted them.
  //
  // This is the number the whole Kit Up screen turns on, so it is
  // worth saying why it is not ten. Both of Perry's paws live on
  // S D F J K L during a fight. Every key outside that grid is a
  // hand LEAVING the guard, which is a real cost paid in real
  // milliseconds — so the ceiling is not "how many powers can we
  // fit," it is "how many can a player reach without dying for it."
  // Five reaches. Six starts to stretch. (Dial.)
  //
  // It is also the entire point of the feature. Owning everything
  // and carrying everything would make the constellation a
  // shopping list. Owning ten and carrying five makes it a build.
  SLOTS: 5,
  // The free power a Hollow Crown buys you, per foe. Data, not a
  // branch — a second Tome that does the same thing louder is a
  // row, not an edit.
  CROWN_FREE: 1,
};

// ══════════════════════════════════════════════════════════════
//  THE TRACKS — every ranked thing in the game, in one table
// ══════════════════════════════════════════════════════════════
// THE ONE SOURCE OF TRUTH for progression (Law 13). Before this
// existed there were four Kindled Hearts, three Deep Breaths, two
// Ember Recalls and a lone Drowned Grip, each a hand-placed star
// with its own cost and its own `fx` — eleven rows saying six
// things, and no way for a player to see how far along any of
// them they were.
//
// Now there are six tracks and one law: FIVE RANKS IS A LEVEL,
// THREE LEVELS IS THE CEILING. A star on the constellation is a
// rank in a track. A Treasure that grants ranks pours into the
// same pool. The level is always `floor(ranks / 5)`, capped at 3,
// computed nowhere but `trackLevel()`.
//
//   per       what ONE LEVEL is worth (flat, single-digit — Law 16)
//   unit      how the Kit Up screen renders that worth
//   ranks     total ranks available to buy at the sky (15 = maxed)
//
// ADDING A SEVENTH TRACK IS A ROW HERE AND NOTHING ELSE. That is
// the Exception Law paid forward: this table is the set of tracks
// that exist today, never the set that may exist.
// ── THE COPY LAW, 2026-07-29 (caP'n) ──────────────────────────
// "Some of these descriptions are a bit too vague. I love how
//  nuanced they are and lore heavy! But we do need to say a little
//  more blatantly as well what they actually do."
//
// So every track now carries FOUR strings and they have four
// different jobs. Never collapse them:
//
//   unit   the number, on the pip bar. Four words, no poetry.
//   plain  WHAT IT DOES, said flatly, to somebody who has never
//          played an IE game. This one leads everywhere. If a
//          player reads only this they should be able to spend a
//          point correctly.
//   lore   the flavour. Comes AFTER the plain line, never instead
//          of it, and never carries mechanical information that
//          isn't already in `plain`.
//   also   cross-references. What this track touches that has its
//          own row somewhere else — because "Deep Breath" sounding
//          like it governs Lyf is exactly the confusion this field
//          exists to kill.
//
// A DESIGN NOTE IS NOT PLAYER COPY. "Temporal, which is the one
// place a percentage is legal" shipped to a player on this screen,
// and that sentence is a note to ME about the Small Number Law.
// It has been removed. Design rationale lives in comments.
export const TRACKS = {
  heart: {
    id: "heart", name: "Kindled Heart", glyph: "❤", tint: "#f4726a",
    per: 1, unit: "+1 ANIMA",
    plain: "Raises your maximum ANIMA by 1 per level. Anima is your life — and your purse, since some powers are paid for in it.",
    lore: "One more answer before the dark. Reedbrook would not recognise you.",
    also: "ANIMA",
  },
  breath: {
    id: "breath", name: "Deep Breath", glyph: "✦", tint: "#4ff0dc",
    per: 1, unit: "+1 MAX CHAO",
    plain: "Raises your maximum CHAO by 1 per level. Chao is what you spend to speak an Art, and it only refills when you reach a fire — so this is how many Arts you can afford between one checkpoint and the next.",
    lore: "More to carry from one fire to the next.",
    // The single most necessary cross-reference in the file. It is
    // called Deep BREATH and it has nothing to do with your Lyf.
    also: "MAX CHAO — and note it does NOT touch LYF · WIND, which is a separate bar with a separate job",
  },
  // The old Sharpened Answer ladder, repointed. It used to buy Lyt
  // and may never buy Lyt again — but "sharpened answer" was always
  // a better name for RECOVERY than for damage: an answer that is
  // sharp is one you get back from quickly.
  wind: {
    id: "wind", name: "Sharpened Answer", glyph: "⟳", tint: "#ffb058",
    per: RULES.EXHAUST_PER_LEVEL, unit: "−0.6s WINDED",
    plain: "Cuts 0.6 seconds off how long you are WINDED, per level. When your Lyf runs out you cannot parry at all for a few seconds — this makes that stretch shorter. It does not give you more Lyf.",
    lore: "Being wrong stops costing so much of the fight.",
    also: "LYF · WIND · WINDED",
  },
  grace: {
    id: "grace", name: "Read the Flame", glyph: "◷", tint: "#ffd8a0",
    per: 0.08, unit: "+8% WINDOWS",
    plain: "Widens every parry window by 8% per level. You get more milliseconds to answer each strike — the timing on everything gets more forgiving, PERFECT included.",
    lore: "The fire slows for those who watch it.",
    also: "every strike on the road",
  },
  sight: {
    id: "sight", name: "Ember Recall", glyph: "👁", tint: "#a06ac0",
    per: 1, unit: "+1 COAL NAMED",
    plain: "One more strike per fight tells you which key it wants before it lands, per level. Priceless against a foe that fights in the dark or shuffles its tells.",
    lore: "Ash keeps records. So, apparently, does fire.",
    also: "SIGHT · the Lantern Hook and the Drowned Censer grant it too",
  },
  grip: {
    id: "grip", name: "Drowned Grip", glyph: "✊", tint: "#4aa8e0",
    per: 0.12, unit: "+12% HOLDS",
    plain: "Widens HOLD windows by 12% per level. A hold is a strike you must meet and then keep the key down through — this forgives a bounced or early release.",
    lore: "Meeting a blow is a moment. Standing in one is a decision.",
    also: "HOLDS · the Mire Fork grants it too",
  },
};

// ── WHAT EVERY NUMBER ON THE READING ACTUALLY MEANS ──────────
// caP'n, 2026-07-29: "a tiny description too when you click on the
//  sections like 'Lyf - Breath - 2' should have a quick sentence
//  explaining what that is."
//
// `track` links a row to the ladder that raises it, so the panel
// can say "raised by: Kindled Heart" and the player can go from a
// number they don't understand to the star that moves it in one
// click. Rows with `track: null` are deliberately NOT buyable and
// say so — that is information, not an omission.
export const STAT_NOTES = {
  anima: {
    key: "ANIMA", track: "heart",
    what: "Your life, and your purse. Every MISS Dims it by the foe's Lyt. At zero your Solrend ends — this is the only way to lose.",
    more: "It is also spendable on purpose: the Ember Pact trades 1 Anima for a full Chao refill, and the U'hlti costs 2 on top of its Chao. Life is a currency here.",
  },
  chao: {
    key: "MAX CHAO", track: "breath",
    what: "How much breath you can carry. Chao is what speaking an Art costs.",
    more: "It refills ONLY at a fire — a village, bonfire, chapel, the Lady, a stranger, a standing stone. Between two fires this is your whole budget, so spending a breath on a husk is breath you will not have at the next boss. Clear a whole leg without spending any and your Max rises by 1, permanently.",
  },
  lyt: {
    key: "LYT · FIXED", track: null,
    what: "Your edge. Add your weapon's number to it and that is how much LYF you chip off a foe with every landed parry.",
    more: "NO STAR RAISES THIS, EVER. Ascension may never buy Lyt — only your species and what you are holding decide it. Want a bigger bite? Go and get a bigger weapon.",
  },
  lyf: {
    key: "LYF · WIND", track: null,
    what: "How many answers you may get WRONG, back to back, before your paws stop listening. A landed parry costs none of it — only a MISS does.",
    more: "At zero you are WINDED and cannot parry at all until it passes. It is NOT raised by Deep Breath (that is Chao) and it is NOT a constellation track — it comes from milestones and from gear. Sharpened Answer does not add Lyf; it shortens the wind.",
  },
  winded: {
    key: "WINDED", track: "wind",
    what: "How long your paws belong to the fen after your Lyf runs out. Strikes keep coming and every one of them lands free.",
    more: "Being winded never kills you by itself — the Anima it costs you does, and Anima was always the only fail state. Sharpened Answer cuts this down, 0.6s a level, and can never cut it to nothing.",
  },
  stars: {
    key: "STARS LIT", track: null,
    what: "How many constellation stars you have lit, and how many Ascension points they cost.",
    more: "Points are bought with Kindling at 20 apiece — the same Kindling the weapon rack takes. Every star is an axe you did not buy.",
  },
  edge: {
    key: "EDGE", track: null,
    what: "Your Lyt plus your weapon's number. This is the actual amount of LYF you take off a foe with every landed parry, GOOD or PERFECT.",
    more: "Once a foe's Lyf reaches zero it is FRACTURED for the rest of the fight, and from then on each landed parry takes exactly 1 ANIMA — flat, no matter how big your edge is.",
  },
};

export const TRACK_ORDER = ["heart", "breath", "wind", "grace", "sight", "grip"];

// ── THE LYT REFUSAL ──────────────────────────────────────────
// There is deliberately no `lyt` track and there never will be.
// If a future node, milestone or Ascension is ever caught trying
// to grant Lyt, this is the line it broke. Weapons, species and
// Tomes grant Lyt. The sky does not.
export const TRACKABLE = new Set(TRACK_ORDER);

// Five ranks is a level, three levels is the ceiling — the only
// place that arithmetic is allowed to live.
export const trackLevel = (ranks) =>
  Math.min(RULES.LEVEL_CAP, Math.floor((ranks || 0) / RULES.RANKS_PER_LEVEL));

// How many ranks to the next level, and how far into this one you
// are. Returned as data so the constellation can draw a pip bar
// without doing any maths of its own.
export const trackProgress = (ranks) => {
  const r = Math.max(0, ranks || 0);
  const level = trackLevel(r);
  const maxed = level >= RULES.LEVEL_CAP;
  const into = maxed ? RULES.RANKS_PER_LEVEL : r % RULES.RANKS_PER_LEVEL;
  return {
    ranks: r, level, maxed,
    into, need: RULES.RANKS_PER_LEVEL,
    toNext: maxed ? 0 : RULES.RANKS_PER_LEVEL - into,
    cap: RULES.RANKS_PER_LEVEL * RULES.LEVEL_CAP,
  };
};

// ══════════════════════════════════════════════════════════════
//  KIT UP — where the road lets you breathe
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-07-29: Chao is no longer per-encounter. It refills
// at a CHECKPOINT — "another village, or bonfire, or church, lady,
// stranger, statue, so many check points where we can stop and
// Kit Up." And the big ones — the Emperium, the U'hlti, any level
// 3 Art — are ONCE PER REFRESH, not once per fight.
//
// This is the single largest change to how the road feels. Chao
// used to be free breath handed back at every foe, which made
// spending it the obvious move every time. Now it is a tank you
// carry between fires, and the question "do I spend a breath on
// this husk, or save it for whatever is past the bend" is a real
// question with a real wrong answer.
//
// `kind` is a STRING THE SCENE SWITCHES ON, never a boolean — a
// bonfire and a statue want different art and different lines, and
// a seventh kind should be a row here, not an engine change.
export const KITUPS = {
  village:  { id: "village",  name: "Village",       icon: "🏘", line: "Roofs, and smoke that means somebody meant it. You are allowed to put your paws down." },
  bonfire:  { id: "bonfire",  name: "Bonfire",       icon: "🔥", line: "Somebody built this on purpose and left it burning for whoever came next. You are whoever came next." },
  church:   { id: "church",   name: "Chapel",        icon: "⛪", line: "The roof is mostly gone. What is left of it is still, technically, shelter." },
  lady:     { id: "lady",     name: "The Lady",      icon: "🕯", line: "She does not say where she is going and she does not ask where you have been. She has a kettle." },
  stranger: { id: "stranger", name: "A Stranger",    icon: "🧍", line: "Sat down like he was always sat there. Shares his fire without looking up." },
  statue:   { id: "statue",   name: "Standing Stone",icon: "🗿", line: "Worn past a face. Whatever it was for, it is windbreak now, and that is enough." },
};

export const kitupFor = (kind) => KITUPS[kind] || KITUPS.bonfire;

// ── THE FOUR STARTERS — the Anchors, one per Ancestry ────────
// (Recurrence Doctrine: H-01 · Shadow Fox · Antdroid · Chaos
//  Dragon travel freely between IE titles. The hero is always
//  named PERRY — you choose what Perry IS.)
export const SPECIES = {
  h01: {
    id: "h01", name: "H-01 Beb", ancestry: "Humanoid / Space",
    ilk: "Humanoid", klass: "Space", tint: "#4aa8e0", glow: "#7cc8ff",
    coat: "#2a5a8e", coatHot: "#8fd0ff", eye: "#cfe9ff",
    tagline: "A newborn Blue Giant. Small. Radiant. Full of potential.",
    perk: "Radiant reserves — begin with +1 Max Chao.",
    perkId: "chao1",
  },
  shadow_fox: {
    id: "shadow_fox", name: "Shadow Fox Beb", ancestry: "Beast / Phantom",
    ilk: "Beast", klass: "Phantom", tint: "#a06ac0", glow: "#c99df0",
    coat: "#241a34", coatHot: "#7a5aa8", eye: "#e8c96a",
    tagline: "A kit of living umbra. Slips between the cracks of reality.",
    perk: "Slips between beats — GOOD windows are 10% wider.",
    perkId: "goodWide",
  },
  antdroid: {
    id: "antdroid", name: "Antdroid Beb", ancestry: "Machine / Time",
    ilk: "Machine", klass: "Time", tint: "#fb923c", glow: "#ffc27a",
    coat: "#5a3a1a", coatHot: "#e09a4a", eye: "#7ce8d8",
    tagline: "A single antdroid, separated from the swarm. For now.",
    perk: "Counts every second — +1 ⋔ Kindling per encounter cleared.",
    perkId: "kindling1",
  },
  chaos_dragon: {
    id: "chaos_dragon", name: "Chaos Dragon Beb", ancestry: "Dragon / Dragon",
    ilk: "Dragon", klass: "Dragon", tint: "#f43f5e", glow: "#ff8098",
    coat: "#4a1220", coatHot: "#e04a68", eye: "#ffd24a",
    tagline: "A newborn storm. Dangerous at any size. Especially small.",
    perk: "Glass storm — +1 Lyt, but PERFECT windows are 8% tighter.",
    perkId: "glass",
  },
};
export const SPECIES_ORDER = ["h01", "shadow_fox", "antdroid", "chaos_dragon"];

// ══════════════════════════════════════════════════════════════
//  THE REGALIA — every power a beb can SPEAK
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-07-28: "just cause Perry is a Beb doesn't mean he
// can't access immense power, time for him to power up."
//
// THE ONE RULE THIS REGISTRY EXISTS TO MAKE POSSIBLE:
//   Everything you ACTIVATE is one kind of thing, and you carry
//   FIVE of them. Imperas, the U'hlti, the Emperium — one list,
//   one gate, one set of keys. A power is a data row. Adding an
//   eleventh costs a row and nothing else, forever.
//
// This replaces the old arrangement, where three Imperas were
// nailed to keys 1/2/3 and the U'hlti to key 4. That was correct
// while there were four things. There are ten now, and nailing ten
// powers to ten keys in a game where both paws live on S D F J K L
// is not a control scheme, it is a dare. So the keys stopped
// belonging to the POWERS and started belonging to the SLOTS —
// which is what "Kit Up" was always for. See RULES.SLOTS.
//
// Fields:
//   rung       "impera" | "uhlti" | "emperium" — display + gating
//   costChao   flat. Checked against CURRENT pool (Gate B).
//   costAnima  flat. A power may NEVER kill you — the check is
//              strictly `anima > costAnima`, everywhere.
//   node       the constellation star that teaches it
//   fx         what perryPowers does with it. A STRING, switched
//              on by the engine — never a hardcoded branch per id.
//
// THE STEERING LAW, honoured on purpose (Blueprint §16): a beb
// standing in Hollowmere wearing an Emperium, having spent every
// star he owns to hold it, is not a rules violation. It is the
// coolest thing anyone sees that day. That is the whole title.
export const REGALIA = {
  // ── THE THREE FOUNDING IMPERAS — unchanged in every respect
  //    except that their key is now wherever you put them.
  p_perry_still: {
    id: "p_perry_still", name: "Still the Flame", rung: "impera", holder: "arm",
    costChao: 2, costAnima: 0, icon: "❄", fx: "still",
    desc: "Spend 2 Chao — the next 3 strikes burn 35% slower.",
    node: "n_imp_still",
  },
  p_perry_third_eye: {
    id: "p_perry_third_eye", name: "Third Eye", rung: "impera", holder: "arm",
    costChao: 1, costAnima: 0, icon: "👁", fx: "eye",
    desc: "Spend 1 Chao — the next 2 strikes show their whole arc up front, and the coals remember their names.",
    node: "n_imp_eye",
  },
  p_perry_ember_pact: {
    id: "p_perry_ember_pact", name: "Ember Pact", rung: "impera", holder: "arm",
    costChao: 0, costAnima: 1, icon: "🩸", fx: "pact",
    desc: "Dim yourself 1 Anima — your Chao refills to Max. Life is a currency.",
    node: "n_imp_pact",
  },

  // ══════════════════════════════════════════════════════════
  //  THE FIVE, 2026-07-28 — caP'n's list, built
  // ══════════════════════════════════════════════════════════
  // Every one of them is small on purpose. The brief was explicit:
  // "the game is still about parrying so the effects shouldn't be
  // too crazy but should help." So not one of these answers a
  // strike FOR you without costing you something you could have
  // spent on answering it yourself. They buy you a beat, a bite,
  // or a mistake — never a fight.

  // ── SHADOW ── Intangible, the canon word, for exactly one blow.
  // It is NOT a parry and it never pretends to be: the blow passes
  // through, so nothing was answered — the chain FREEZES rather
  // than growing. You paid a breath to not be there. Fair trade,
  // honest bookkeeping.
  p_perry_shadow: {
    id: "p_perry_shadow", name: "Shadow", rung: "impera", holder: "arm",
    costChao: 1, costAnima: 0, icon: "◐", fx: "shadow",
    desc: "Spend 1 Chao — you go Intangible. The next blow that would land passes through you instead. It costs no Anima, and it breaks no chain. It also answers nothing.",
    node: "n_shadow",
  },

  // ── ECHO ── the Lyf Cycle's honest back door.
  // A free hit is a WHOLE hit — it ignores skin entirely, which is
  // the only way past Lyf that does not violate the Cycle. It is
  // not chip damage; it is one clean Anima, off the top, once.
  // It fires on a GOOD as well as a PERFECT deliberately: that is
  // the one moment in the game where a decent answer does real
  // work, and you had to spend a breath to buy it.
  p_perry_echo: {
    id: "p_perry_echo", name: "Echo", rung: "impera", holder: "arm",
    costChao: 1, costAnima: 0, icon: "◎", fx: "echo",
    desc: "Spend 1 Chao — your next answer that lands rings twice. One free hit, straight through Lyf, whatever its skin had to say about it.",
    node: "n_echo",
  },

  // ── MIRAGE ── the patient one, and the difference between it and
  // The Answer is the whole design.
  //   The Answer  — spends on the NEXT strike, whatever happens.
  //                 You choose the moment. It costs blood.
  //   Mirage      — spends on the next FAILURE, and only then. It
  //                 sits there for a whole encounter if you never
  //                 need it. It is insurance, not tempo.
  // Cheap in Chao, expensive in a slot. Carrying it means carrying
  // one fewer thing that could have won you the fight outright.
  p_perry_mirage: {
    id: "p_perry_mirage", name: "Mirage", rung: "impera", holder: "arm",
    costChao: 2, costAnima: 0, icon: "❈", fx: "mirage",
    desc: "Spend 2 Chao — the next blow that would land on you meets something that is not there. It is answered PERFECTLY, even if you swung at the wrong angle or did not swing at all, and it bites back once.",
    node: "n_mirage",
  },

  // ── KING'S GRACE ── canon word (IE_LORD_NAMES_CANON): a Lord's
  // Grace is the thing they trade something for. A beb has no
  // business holding one and cannot hold it long — hence the clock.
  //
  // The difference from Still the Flame is the UNIT, not the size.
  // Still the Flame slows the next THREE STRIKES; the Grace slows
  // eight SECONDS. Against a Calamity the Grace covers all three
  // beats and the interval between them. Against a slow foe it
  // covers less than the Flame does. Two answers to one question,
  // and which one is right depends on what is in front of you.
  p_perry_kings_grace: {
    id: "p_perry_kings_grace", name: "King's Grace", rung: "impera", holder: "arm",
    costChao: 2, costAnima: 0, icon: "♔", fx: "grace",
    ms: 8000, slow: 1.55,
    desc: "Spend 2 Chao — for eight seconds the world stops being in a hurry with you. Everything swung at you comes slower. You are a beb; you cannot hold a king's patience for longer than that.",
    node: "n_kings_grace",
  },

  // ── BANK THE HEARTH ── the "Imperas: vary" entry, and the exact
  // mirror of the Ember Pact. The Pact sells life for breath; this
  // buys life with breath. Both directions of the Anima Doctrine
  // in one Grimoire, which is the most Empire thing in the file.
  //
  // Once per encounter, and Chao refills per encounter, so the
  // ceiling is one Anima per foe for the price of your entire
  // breath — and the Growth Law never speaks to you again that
  // fight. Expensive in every currency the game has.
  p_perry_hearth: {
    id: "p_perry_hearth", name: "Bank the Hearth", rung: "impera", holder: "arm",
    costChao: 3, costAnima: 0, icon: "🜂", fx: "hearth",
    desc: "Spend 3 Chao — bank the breath as heat instead of spending it. +1 Anima, never above your ceiling. Once per foe.",
    node: "n_hearth",
  },

  // ── THE U'HLTI ── unchanged. It simply lives in a slot now.
  // One per archetype, and the only thing in the game that spends
  // both currencies in one breath: 3 Chao AND 2 Anima. The next
  // strike is answered PERFECTLY, whatever it was, from whatever
  // angle, chord or hold or lie or all three. A Calamity beat
  // answered by The Answer still counts toward the Fracture.
  //
  // It is deliberately not a panic button. Three Chao is your whole
  // breath at most ceilings and two Anima is most of a beb, so by
  // the time you can say it, saying it costs you the fight you were
  // saving it for. That tension IS the ability.
  u_perry_answer: {
    id: "u_perry_answer", name: "The Answer", rung: "uhlti", holder: "arm",
    costChao: 3, costAnima: 2, icon: "⚡", fx: "answer",
    desc: "Spend 3 Chao AND 2 Anima — the next strike is answered PERFECTLY, no matter what it is or where it comes from.",
    node: "n_uhlti",
  },

  // ══════════════════════════════════════════════════════════
  //  THE EMPERIUM — a rung the Empire did not have until today
  // ══════════════════════════════════════════════════════════
  // caP'n: "landscape completely changes to their character's
  // theme/style and you get 3 auto-parry, time limit of 20 seconds
  // so if not parrys in that time they're gone, not saved."
  //
  // WHAT IT IS, IN CANON. The Emperium is the Empire's seat — the
  // Outskirts, the Inner Halls, the Chao Vaults, the Throne of
  // J'ynxi. To speak one is to say the road belongs to you now, and
  // the road agrees. The attachment ladder runs Impera → U'hlti →
  // Affinity → Supreme and has no rung for this; a domain is louder
  // than a signature move and smaller than a crown. So Perry mints
  // one, and it wants a ruling before it becomes Empire canon —
  // logged in the Roadmap, not decided here.
  //
  // THE THREE ARE NOT SAVED. That is the whole shape of it. Twenty
  // seconds of absolute sovereignty, and if the fire does not come
  // at you three times inside it, the ones you did not use go out
  // with the landscape. It rewards speaking it into a Calamity and
  // punishes speaking it out of fear — which is exactly the
  // difference this title has been arguing about since Reedbrook.
  e_perry_emperium: {
    id: "e_perry_emperium", name: "The Emperium", rung: "emperium", holder: "arm",
    costChao: 5, costAnima: 1, icon: "◈", fx: "emperium",
    ms: 20000, parries: 3, slow: 1.12,
    desc: "Spend 5 Chao AND 1 Anima — the road stops being the road and becomes YOURS. For twenty seconds the next three strikes are answered PERFECTLY. Any you do not spend are gone when it closes. Nothing is saved.",
    node: "n_emperium",
  },
};

export const REGALIA_ORDER = [
  "p_perry_still", "p_perry_third_eye", "p_perry_ember_pact",
  "p_perry_shadow", "p_perry_echo", "p_perry_mirage",
  "p_perry_kings_grace", "p_perry_hearth",
  "u_perry_answer", "e_perry_emperium",
];

// ── THE EMPERIUM'S FIVE SKIES ────────────────────────────────
// "their character's theme/style" — one per Anchor, plus the
// fallback nobody should ever see. `biome` is the string the stage
// already switches on (`director.act?.biome`), so an Emperium is
// a WORLD SWAP through machinery that was built for the Drowned
// Mile and costs nothing new to drive.
//
// The wash is what sells it before any 3D work exists at all:
// a full-frame screen-space gradient in the species' own colour,
// which is deliberately the cheapest possible version and is
// deliberately enough. Prettiness is not the point yet — see the
// Roadmap. The hooks are what matter, and they are all here.
export const EMPERIUM_THEMES = {
  h01: {
    biome: "radiance", name: "THE RADIANT HALL",
    tint: "#7ce8d8", deep: "#06201e", hot: "#d8fff8",
    line: "The ash stops falling. It hangs there, lit from inside, and the road becomes a floor somebody polished.",
  },
  shadow_fox: {
    biome: "phantom", name: "THE LONG DUSK",
    tint: "#a06ac0", deep: "#170a24", hot: "#e8c8ff",
    line: "Every shadow on the road stands up, and none of them are yours, and all of them are on your side.",
  },
  antdroid: {
    biome: "clockwork", name: "THE COUNTED HOUR",
    tint: "#fb923c", deep: "#241206", hot: "#ffd8a0",
    line: "Something enormous begins to tick. The fire keeps moving because it is being allowed to.",
  },
  chaos_dragon: {
    biome: "storm", name: "THE NEWBORN STORM",
    tint: "#f43f5e", deep: "#26060f", hot: "#ffb0c0",
    line: "The sky comes down to have a look at you and decides, on balance, to help.",
  },
  _: {
    biome: "emperium", name: "THE EMPERIUM",
    tint: "#f0984a", deep: "#1c1009", hot: "#ffe6c2",
    line: "The road stops being the road.",
  },
};
export const themeFor = (speciesId) => EMPERIUM_THEMES[speciesId] || EMPERIUM_THEMES._;

// ── BACKWARD COMPATIBILITY, and it is not a courtesy ─────────
// THE VYREN PATTERN: structure may change, internal ids never do,
// and no consumer should have to care which pass it was written
// in. Everything that imported IMPERAS or UHLTI still gets exactly
// what it asked for — derived from the one registry above, so the
// two can never drift into disagreeing.
export const IMPERAS = Object.fromEntries(
  Object.entries(REGALIA).filter(([, p]) => p.rung === "impera")
);
export const UHLTI = REGALIA.u_perry_answer;
export const EMPERIUM = REGALIA.e_perry_emperium;
// ── TOMES — face-down helpers. No cadence tag → consumed (Ruin Law). ──
export const TOMES = {
  tome_kindled_blade: {
    id: "tome_kindled_blade", name: "Kindled Blade", tag: "ATV",
    price: { kindling: 15 }, icon: "🔥",
    desc: "ATV — While lit: each PERFECT stacks +1 Lyt (cap +3). The foe may snuff it at any moment by WITHHOLDING its strike — a feint destroys the flame.",
  },
  tome_fork_of_embers: {
    id: "tome_fork_of_embers", name: "Fork of Embers", tag: "ACB",
    price: { kindling: 10 }, icon: "⑂",
    desc: "ACB 1–3 — As the next encounter begins, the FOE names a number aloud. You gain that much Chao; the encounter gains that many extra strikes.",
  },
  tome_rays_of_hope: {
    id: "tome_rays_of_hope", name: "Rays of Hope", tag: null,
    price: { anima: 2 }, icon: "🌅",
    desc: "The Blood Bargain — pay 2 Anima NOW. Later, at the moment you would fall, you stand at 1 instead. Consumed.",
  },
  // ── THE ITEMS CHANNEL, 2026-07-27 ──
  // Cheap on purpose. This is the rung a player buys with the ⋔
  // they earned parrying on a run they lost, which is the entire
  // point of the per-answer grants above: the road that beat you
  // pays for the thing that gets you past it.
  tome_page_of_names: {
    id: "tome_page_of_names", name: "A Page of Names", tag: null,
    price: { kindling: 8 }, icon: "📜",
    desc: "Torn out of something older than Reedbrook. For three strikes of your Solrend the coals remember their own names, and show them — even to a foe that put them out. Consumed.",
  },

  // ── THE DROWNED MILE'S TWO ─────────────────────────────────
  // One ATV, one ACB, exactly like the Cinders Road's pair, and
  // for the same reason: Tier 2 conformance is not decoration.
  // Every act gets a piece of greed with a hostile counterplay,
  // and a piece of generosity the FOE gets to size.
  tome_black_water: {
    id: "tome_black_water", name: "Black Water", tag: "ATV",
    price: { kindling: 25 }, icon: "🌑",
    // The direct answer to the Lyf Cycle, which the fens are the
    // first road to actually enforce — the imps have Lyf 1 and the
    // Disciple has Lyf 1 behind Lyt 2. A base-Lyt beb bounces off
    // all three. This is one of four ways past that (the others:
    // the Cleaver, the Bill, and getting good).
    //
    // ATV, so it has teeth going the other way: the fen puts it
    // out the moment you are hit. Not when you fall — when you are
    // TOUCHED. It is the greediest thing in the game.
    desc: "ATV — While lit: your Lyt ignores 1 Lyf, and skin stops being an argument. The water puts it out the instant anything lands on you. Not when you fall. When you are touched.",
  },
  tome_ferrymans_due: {
    id: "tome_ferrymans_due", name: "The Ferryman's Due", tag: "ACB",
    price: { kindling: 18 }, icon: "⚱",
    // ACB — the FOE names the number, exactly like the Fork of
    // Embers, and the number is a price AND a wage at the same
    // time. It darkens N of the fight's strikes and pays N ⋔ for
    // each one you answer anyway. A generous foe is a dangerous
    // foe. That is the Fork's whole joke, told wetter.
    desc: "ACB 1–3 — As the encounter begins the foe names a number. That many of its strikes come with the coals DARK. Every one of them you answer anyway pays you that many ⋔.",
  },

  // ══════════════════════════════════════════════════════════
  //  THREE FOR THE REGALIA, 2026-07-28 — "Tomes: vary"
  // ══════════════════════════════════════════════════════════
  // A new power layer wants Tomes that argue with it, not Tomes
  // that ignore it. Each of these three is a different sentence
  // about the same thing — the price of speaking a power — and
  // between them they cover the Fare, the Ruin and the Cadence.
  tome_hollow_crown: {
    id: "tome_hollow_crown", name: "The Hollow Crown", tag: "OPT",
    price: { kindling: 45 }, icon: "♛",
    // OPT, and the FARE LAW is the whole joke: you paid once, at
    // the counter, and the fire keeps burning — one free Regalia
    // per foe, every foe, forever. It is the most expensive thing
    // on the cart that is not on the high shelf, and it should be.
    desc: "OPT — Beaten out of something that used to sit on a head. The FIRST power you speak against each foe costs no Chao. It does not make you a king. It makes you free once a fight, which is arguably better and definitely cheaper.",
  },
  tome_mirror_pool: {
    id: "tome_mirror_pool", name: "The Mirror Pool", tag: null,
    price: { kindling: 22 }, icon: "🪞",
    // Consumed (Ruin Law — no cadence tag). Spends across the
    // whole Solrend like A Page of Names, not per encounter, so
    // WHEN you let it go off is a real decision rather than a
    // formality at the top of every fight.
    desc: "A hand's width of standing water that has not moved since before the fire. The first time in a Solrend that you speak a power and have nothing left to speak it with, the pool speaks it for you. Free, once, and then it is only water. Consumed.",
  },
  tome_long_hour: {
    id: "tome_long_hour", name: "The Long Hour", tag: "ATV",
    price: { kindling: 55 }, icon: "⏾",
    // ATV — the greed with the hostile counterplay, aimed squarely
    // at the two timed powers. Hold a King's Grace or an Emperium
    // and the clock runs slower... until the foe simply declines to
    // swing, and refusing is what destroys it. A withheld strike
    // costs you the hour. Feints have never been more expensive.
    desc: "ATV — While lit: every clock you are holding runs at two thirds speed — a King's Grace, an Emperium, anything with an hour on it. The foe may end it at any moment by WITHHOLDING its strike. A lie puts the hour out.",
  },
};


// ── TREASURES — the thing in his paws ────────────────────────
// Canon word: a Treasure is EQUIPMENT that belongs to one Being
// (Blueprint — Gauntlets of A'zka, Sword of Shadows, the Weeping
// Dagger). Perry's Treasure is what he parries WITH, and it is
// modelled, carried, swung and sparked in 3D.
//
// AMENDED 2026-07-26 (caP'n's ruling). The founding pitch said
// "you never get a bigger weapon." That pillar is now RETIRED in
// favour of: you never get a bigger BODY and you never get a
// bigger RUNG. Treasures may be genuinely, wildly different —
// heavier, wider, whackier, better. What never changes is that a
// Treasure only ever shapes THE ANSWER. None of them swing on
// their own, none of them fight for you, and the beb holding it
// is the same size he was in Reedbrook.
//
// Fields:
//   lyt        flat bite added (Small Number Law — single digits)
//   windowMod  ± on every timing band (temporal % — permitted)
//   bias       per-guard-angle window multipliers. THE character.
//   pierce     ignores this much of a foe's Lyf
//   hurlMod    extra window against thrown fire only
//   twin       a Slash's second beat is answered by the same press
//   ladle      N times per encounter, a GOOD is quietly a PERFECT
//   shape      how TreasureBody builds it from primitives
export const TREASURES = {
  // ── THE REED IS ZERO — caP'n's shape, honoured 2026-07-29 ────
  // "the first boss is 5 hit (cause that's the Lyf max) and then
  //  1 Hit to his anima, now he has 0 so he's dead."
  //
  // That reading — Lyf 5 means FIVE answers — is only true at edge
  // 1, and the reed was quietly making it 2. So the reed drops to
  // 0 and `LYT_BASE` alone carries a fresh beb. Th'rakk is now
  // exactly the six answers caP'n described, and every other wall
  // in the game reads at face value: Lyf N costs N answers barehanded.
  //
  // ══════════════════════════════════════════════════════════════
  //  THE LYT RESCALE — caP'n, 2026-07-30
  // ══════════════════════════════════════════════════════════════
  // "we will have to bring the user's Lyt down drastically as well
  //  so we don't start 1 shotting the Lyf."
  //
  // The rack was authored for a wall you broke ONCE per fight, so
  // +3 Lyt was a reasonable opening shave. The wall regenerates
  // now, and the act tiers cap it at 3 through Act II — so an
  // edge of 4 would have deleted every wall in two thirds of the
  // game in a single answer, and the wall would have stopped being
  // a wall the day you could afford a Cleaver.
  //
  //   was 0·1·3·1·2·3·3·2·3   →   now 0·0·2·1·1·2·1·1·2
  //
  // Standing edge now runs 1 → 4 (LYT_BASE 1, +1 Chaos Dragon,
  // +2 the heaviest Treasure), which puts every wall in the game
  // at TWO to THREE answers for a player at the tier it was
  // written for. That is the number to protect.
  //
  // WHAT THIS DOES NOT DO IS FLATTEN THE RACK. A weapon's identity
  // was never mostly its Lyt — it is `windowMod`, `grip`, `bias`,
  // `pierce`, `sight` and the RANKS it pours into your tracks, and
  // now that Read the Flame forgives weight (see qteEngine's
  // `masteredWindowMod`) those knobs have more room, not less.
  // Under the Sheathe's Ascension a sword's power comes from its
  // OWN tree anyway; the number printed on the shelf is a floor,
  // not a ceiling. (Dials, all of them.)
  //
  // It also fits the thing's own fiction better than 1 ever did.
  // The reed is "honest, even, yours before any of this started" —
  // it is the BASELINE, not a bonus, and every weapon on Whyllie's
  // rack is now measurably a step up from nothing-special rather
  // than a step up from something-already-decent.
  //
  // DIAL. One field. Put it back to 1 and base edge is 2 again.
  t_reed_switch: {
    id: "t_reed_switch", name: "Fire-Hardened Reed", icon: "🌾", rung: "Escling",
    price: null,                       // the one you walk out of Reedbrook holding
    lyt: 0, windowMod: 0, pierce: 0, bias: {}, ranks: {},
    desc: "Cut from the slow water you grew up beside, and dried three days in a fire that burned for three days. Honest. Even. Yours before any of this started.",
    shape: { haft: 0.50, thick: 0.014, head: "switch", guard: 0, tint: "#c8a066", hot: "#ffd08a" },
  },
  t_pyre_ladle: {
    id: "t_pyre_ladle", name: "The Pyre Ladle", icon: "🥄", rung: "Escling",
    price: { kindling: 20 },
    lyt: 0, windowMod: 0.05, pierce: 0, ladle: 1, ranks: { grace: 2 }, bias: { K: 0.2 },
    desc: "It is a soup ladle. Whyllie swears on three separate ancestors that it is a relic. Once per encounter it will, for no reason it is willing to discuss, turn a decent answer into a perfect one — and then sit there looking like a ladle.",
    shape: { haft: 0.42, thick: 0.018, head: "ladle", guard: 0, tint: "#8e7a5a", hot: "#ffd8a0" },
  },
  t_ash_cleaver: {
    id: "t_ash_cleaver", name: "Ash Cleaver", icon: "🪓", rung: "Beb",
    price: { kindling: 30 },
    lyt: 2, windowMod: -0.10, pierce: 0, ranks: { wind: 2 },
    // REMAPPED 2026-07-27, intent preserved exactly. This used to
    // read { J, K, L } because those WERE the low row; they are the
    // right hand now. A heavy chopping thing is good coming DOWN
    // (the top row) and bad going down after it (the bottom row).
    bias: { F: 0.12, J: 0.12, S: -0.18, L: -0.18 },
    desc: "Salvaged off a door that did not hold. It bites deeper than a beb has any business biting. It is also heavy, and low is a very long way down.",
    shape: { haft: 0.34, thick: 0.026, head: "cleaver", guard: 1, tint: "#4a4038", hot: "#ff8a3c" },
  },
  t_lantern_hook: {
    id: "t_lantern_hook", name: "Whyllie's Lantern Hook", icon: "🪝", rung: "Beb",
    price: { kindling: 35 },
    lyt: 1, windowMod: 0.12, pierce: 0, hurlMod: 0.35, sight: 1, bias: {}, ranks: { sight: 3 },
    // SIGHT 1 — caP'n's "change gear" channel. It is a LANTERN
    // hook; a lantern's whole job is showing you a thing in the
    // dark. One named coal per encounter, and against a foe that
    // shuffled or snuffed them, that one is the one that matters.
    desc: "\"Catches things,\" said Whyllie, and then did not say what things. Wide, forgiving, and it plucks thrown fire out of the air like fruit off a low branch. Once per foe the lantern remembers a coal's name and says it out loud.",
    shape: { haft: 0.58, thick: 0.015, head: "hook", guard: 0, tint: "#7a5a2a", hot: "#ffc27a" },
  },
  t_twin_needles: {
    id: "t_twin_needles", name: "Twin Ember Needles", icon: "🗡", rung: "Beb",
    price: { kindling: 40 },
    lyt: 1, windowMod: -0.05, pierce: 0, twin: true, ranks: { wind: 3 },
    bias: { S: 0.15, F: 0.15, J: 0.15, L: 0.15, D: -0.1, K: -0.1 },
    desc: "Two, because some things come twice. The corners are yours; the centre is not, and the needles will not pretend otherwise.",
    shape: { haft: 0.30, thick: 0.011, head: "needles", guard: 0, tint: "#3a2a34", hot: "#ff5a6a" },
  },
  t_bill_of_iron: {
    id: "t_bill_of_iron", name: "A Bill of Beaten Iron", icon: "⚔", rung: "Champion",
    price: { kindling: 60 },
    lyt: 2, windowMod: -0.08, pierce: 1, lyf: 1, bias: { D: 0.1, K: 0.1 }, ranks: { heart: 2 },
    desc: "Torn off something that wore it in its master's honour, which means it has been close to T'yphiz and came back. It ignores 1 Lyf. Skin has never impressed it.",
    shape: { haft: 0.40, thick: 0.030, head: "bill", guard: 2, tint: "#5a5a62", hot: "#9fe8ff" },
  },

  // ══════════════════════════════════════════════════════════
  //  LEVEL 2 — three Treasures for a wetter road
  // ══════════════════════════════════════════════════════════
  // Priced above everything in Act I on purpose. The Cinders Road
  // pays 25–45 ⋔ a walk and the Drowned Mile pays more; these are
  // what that money is FOR. A player who never leaves Hollowmere
  // can still reach them, slowly, which is the whole point of the
  // per-answer purse.
  //
  //   grip  ± on HOLD tolerance only. A new field, because the
  //         hold is a new verb and a Treasure that is good at
  //         standing in a jet is a genuinely different Treasure
  //         from one that is good at meeting a beat.
  t_mire_fork: {
    id: "t_mire_fork", name: "The Mire Fork", icon: "🔱", rung: "Beb",
    price: { kindling: 75 },
    lyt: 1, windowMod: -0.04, pierce: 0, grip: 0.30, ranks: { grip: 4 },
    bias: { D: 0.18, K: 0.18 },
    desc: "Three tines. Two of them are for the eel. The fenfolk will not say what the third is for, and they will not say it in a way that makes you stop asking.",
    shape: { haft: 0.54, thick: 0.020, head: "fork", guard: 0, tint: "#4a5a52", hot: "#7fe8c8" },
  },
  t_drowned_censer: {
    id: "t_drowned_censer", name: "The Drowned Censer", icon: "🏮", rung: "Beb",
    price: { kindling: 90 },
    lyt: 1, windowMod: 0.18, pierce: 0, hurlMod: 0.20, sight: 2, bias: {}, ranks: { sight: 3, breath: 2 },
    // TWO sight charges — the deliberate answer to a foe that put
    // the coals out. It is not a crutch, it is a COUNTER: you can
    // buy your way to two named strikes in a DARK fight, and the
    // fight has eleven.
    desc: "It went under with the chapel and it is still smoking. Nobody in Candlemarch has explained this and nobody in Candlemarch is going to. Twice per foe it remembers what the coals are called, and says so out loud, even in the dark.",
    shape: { haft: 0.62, thick: 0.013, head: "censer", guard: 0, tint: "#6a5a3a", hot: "#ffd8a0" },
  },
  t_bell_clapper: {
    id: "t_bell_clapper", name: "The Sunken Bell's Clapper", icon: "🔔", rung: "Champion",
    // ── THE TOP SHELF, 2026-07-28 ──
    // The only thing in Perry priced in ◆, and therefore the only
    // thing in Perry a player cannot grind for. Under the Faucet
    // Law ◆ reaches this title from one-time sources only, so the
    // Clapper is bought with achievements — with things you did
    // once and can never do again — and it is ONE PER ACCOUNT,
    // because ◆ IS grindable at the Well and the Mythical Law
    // closes that loophole by capping the buy, not the supply.
    // A New Game does not give it back. Nothing does.
    //
    // Eight ◆ against a hundred and ten ⋔ looks like a discount.
    // It is roughly a thousand ⋔ at the Toil rate. It is the most
    // expensive object in the game by a factor of nine.
    price: { dc: 8 }, oncePerAccount: true,
    // ── LYT 5 → 3, 2026-07-29, THE CEILINGS PASS ──────────────
    // The rack read 1,1,3,1,2,3,3,2 and then FIVE. Everything in
    // the Empire tops out at three; the Clapper was the one object
    // in the title quietly ignoring that, and it only survived
    // because foe Lyf used to run to 14 and something had to be
    // able to chew it.
    //
    // With walls capped at 5 that job no longer exists. At Lyt 5
    // (edge 6, plus pierce) it deleted the skin of every boss in
    // the game in one answer AND made itself indistinguishable
    // from the 30-⋔ Ash Cleaver, which also deletes it. A stat
    // nobody can feel the top of is not a top shelf.
    //
    // So the Clapper's identity moves off the big number and onto
    // the seven fields it already had and nothing else does: the
    // ONLY item in the game with two rank-3 tracks, plus pierce,
    // plus +1 Lyf, plus the tightest window in the title paid for
    // with a grip penalty. It is now the most demanding weapon
    // rather than the largest one, which is a better Champion and
    // a much better ◆ purchase. (DIAL — one field, reversible.)
    lyt: 2, windowMod: -0.14, pierce: 1, grip: -0.15, lyf: 1, ranks: { wind: 3, heart: 3 },
    bias: { F: 0.15, J: 0.15, S: -0.20, L: -0.20 },
    desc: "Rung once a night for two hundred years, then once more, and then the water came in. It is the heaviest thing a beb has ever been able to lift and it does not pretend to be anything else. High is glorious. Low is a decision you have to live with.",
    shape: { haft: 0.36, thick: 0.034, head: "clapper", guard: 1, tint: "#4e4a44", hot: "#ffc27a" },
  },
};
export const TREASURE_ORDER = [
  "t_reed_switch", "t_pyre_ladle", "t_ash_cleaver",
  "t_lantern_hook", "t_twin_needles", "t_bill_of_iron",
  "t_mire_fork", "t_drowned_censer", "t_bell_clapper",
];

// ── FINISHES — pure cosmetic, all free, all yours ────────────
// The ember it burns with and the trail it leaves. Costs nothing,
// changes nothing, and is the first thing anyone will touch.
// ══════════════════════════════════════════════════════════════
//  THE SHEATHE — caP'n, 2026-07-30. THE RESTRUCTURE.
// ══════════════════════════════════════════════════════════════
// "After Perry's first Parry, the Sheathe (who Perry thinks at
//  first is the sword not Sheathe) was monologuing for us the
//  story and then reveals himself to Perry (the sword chimes in
//  too... yeah it can talk.) The Sheathe helps any object with
//  potential to Ascend and become more than itself, and it grants
//  it speech, doesn't mean they have to talk."
//
// THE RULING THAT REORGANISES THE WHOLE TITLE:
//
//   PERRY KEEPS HIS PRIMERAS. THE SWORD GETS EVERY OTHER ART.
//
// Imperas, the U'hlti, the Emperium, Shadow, Echo, Mirage — none
// of them are Perry's. They belong to whatever he is holding, they
// are bought on that object's own tree, and they leave with it
// when he puts it down. Perry is a beb. He does not get to be a
// wizard. He gets to be a beb carrying something that is.
//
// WHY THIS IS THE RIGHT SHAPE AND NOT JUST A FUN ONE:
//   · It answers "why does my endgame feel the same" permanently.
//     Power now lives on an object you chose, invested in, and can
//     see the state of — not in a sky full of stars that all read
//     as +1 to something.
//   · It makes SWITCHING WEAPONS a real decision with a real cost,
//     which is what caP'n asked for: "can switch and level up
//     others if you want, pay currency and reset your levels."
//   · It gives the starter branch somewhere to go. A weapon you
//     never replace, that costs far more than it is worth for a
//     long time, and then becomes a Lord — that is a whole run's
//     worth of story riding on one data row.
//   · It is where Cursed Weapons live (R-51). A cursed thing is
//     one with a punishing base and an extraordinary tree.
//
// WHAT IS AND IS NOT DONE IN THIS PASS — read this before the
// next session touches it:
//   ✅ The ladder, every sword's tree, the derivations, the Soul
//      schema, the Firebase writes, the Sheathe's voice.
//   ⛔ Perry's Art stars are STILL LIT on his constellation. They
//      are flagged `legacy: true` and every REGALIA row now says
//      `holder: "arm"`, but they have NOT been unplugged, because
//      the per-sword tree has no screen yet and pulling them today
//      would leave a live Soul with no way to reach any Art at
//      all. Unplug + refund migration is R-52, and it is a
//      deliberate, flagged, schema-touching job — not a sweep.
export const SHEATHE = {
  id: "sheathe",
  name: "The Sheathe",
  // Perry thinks the voice is the sword's for the whole first
  // scene. The reveal is that the scabbard has been doing the
  // talking, and the blade has opinions of its own about that.
  revealedAs: "the sword",
  tint: "#c9a227", hot: "#ffe6a8",
  what: "It finds objects with potential and helps them Ascend into more than they were. Speech comes with the offer. Accepting the speech is optional; most things do not shut up.",
  // Voice notes, so a later writer does not have to guess. These
  // are direction, not dialogue — dialogue lives in SHEATHE_LINES.
  voice: {
    sheathe: "Patient, grand, a little theatrical. Has done this many times and is still moved by it. Never explains a joke. Calls everything by what it could become rather than what it is.",
    blade: "Dry. Interrupts. Has been listening to the Sheathe do this speech for longer than anyone has been alive and is deeply unimpressed by the middle third of it.",
  },
  // ── UNNAMED ON PURPOSE ──
  // The Sheathe has a true name and this file does not know it.
  // Naming a thing of this rank is a canon act and belongs to
  // caP'n and to IE_LORD_NAMES_CANON, not to a systems pass.
  trueName: null,
};

// ── THE SHEATHE SPEAKS ───────────────────────────────────────
// caP'n wrote the cursed-branch exchange verbatim and it sets the
// register for everything else: the Sheathe orates, the blade
// undercuts, the world makes a small sound at the end.
//
// `at` is the EVENT KEY the Outfitter and the Ascension screen
// switch on. A seventh occasion is a row here and nothing else —
// no component learns a new branch to say a new thing.
//
// Each line is a LIST. One is picked per firing so the counter
// does not become a recording. `who` is "sheathe" | "blade" |
// "world", and a beat with no speaker is stage direction.
export const SHEATHE_LINES = {
  // Buying a rung on a weapon's tree.
  ascend: [
    [
      { who: "sheathe", text: "Ah, the cursed branch. I was beginning to won—" },
      { who: "blade", text: "I really feel like you're just saying things are cursed." },
      { who: "sheathe", text: "Ha. Your cursed speech is intriguing. Come now, your branch begs to Ascend." },
      { who: "world", text: "*tree leaves rustle*" },
    ],
    [
      { who: "sheathe", text: "Do you feel that? That is the object remembering it was once part of something taller." },
      { who: "blade", text: "It's a stick." },
      { who: "sheathe", text: "It is a stick that is LISTENING." },
    ],
    [
      { who: "sheathe", text: "Higher. Good. Most things stop here and spend the rest of their lives being adequate." },
    ],
  ],
  // ══════════════════════════════════════════════════════════
  //  THE REVEAL — caP'n's opening beat, 2026-07-30
  // ══════════════════════════════════════════════════════════
  // "After Perry's first Parry, the Sheathe (who Perry thinks at
  //  first is the sword, not Sheathe) was monologuing for us the
  //  story and then reveals himself to Perry (the sword chimes in
  //  too... yeah it can talk.)"
  //
  // This is the ONE beat-list that fires on the road rather than
  // at a counter, and it fires exactly once per Soul — the
  // director speaks it after the very first landed parry of the
  // very first Solrend and never again. See `sheatheBeat` in
  // RunDirector: it is gated on `milestones.sheathe`, granted
  // grant-before-flag by App like every other one-time thing.
  //
  // It is deliberately the only place in the title where three
  // voices talk over the top of a fight. Everything after this is
  // the Sheathe at a table, being theatrical at a shopkeeper.
  reveal: [
    [
      { who: "sheathe", text: "There. That — THAT is the sound a thing makes when it decides not to be finished." },
      { who: "sheathe", text: "I have been narrating you for some time. I want you to know I did not expect much." },
      { who: "blade", text: "He thinks you're me." },
      { who: "sheathe", text: "He does not." },
      { who: "blade", text: "He absolutely does. He has been thanking me for a mile and a half." },
      { who: "sheathe", text: "…" },
      { who: "sheathe", text: "I am the SHEATHE, small one. Not the blade. The blade is a very good blade and it has never had an idea in its life." },
      { who: "blade", text: "True." },
      { who: "sheathe", text: "I find objects with potential and I make them ask for more than they were. Speech comes with the offer. Most of them will not shut up afterwards." },
      { who: "blade", text: "Also true." },
      { who: "sheathe", text: "You are carrying one. In time — and this is not a promise, it is an observation — you may be one." },
      { who: "world", text: "*something very old settles against your back, and does not explain itself*" },
    ],
  ],
  // Buying a Treasure at Whyllie's counter.
  bought: [
    [
      { who: "sheathe", text: "Oh, THAT one. Yes. That one has been waiting a while to be picked up by somebody who was going somewhere." },
    ],
    [
      { who: "blade", text: "You bought another one." },
      { who: "sheathe", text: "He is allowed to buy another one." },
      { who: "blade", text: "I didn't say he wasn't." },
    ],
  ],
  // A weapon reaches Lord eligibility — every rung lit.
  crown: [
    [
      { who: "sheathe", text: "There. There is nothing left on it that I know how to give. Everything after this it has to go and take." },
      { who: "blade", text: "Here we go." },
      { who: "sheathe", text: "HERE WE GO." },
    ],
  ],
  // The Awakening mission begins — stats reset to nothing.
  awaken: [
    [
      { who: "sheathe", text: "It will feel like a stick again. It IS a stick again. That is the price and it is not negotiable and it has never once not been worth it." },
      { who: "blade", text: "He's going to die." },
      { who: "sheathe", text: "He is going to die SEVERAL TIMES. Then he is going to stop." },
    ],
  ],
  // Lord form. The only time the Sheathe stops performing.
  lord: [
    [
      { who: "sheathe", text: "…" },
      { who: "sheathe", text: "I have done this eleven times. I have never once got used to it." },
      { who: "blade", text: "Say the thing." },
      { who: "sheathe", text: "Rise, then. You are not an object any more." },
      { who: "world", text: "*something very old exhales*" },
    ],
  ],
  // Switching your invested weapon for another — the reset.
  reforge: [
    [
      { who: "blade", text: "He's putting us down." },
      { who: "sheathe", text: "He is putting us down for NOW. Objects are terrible at the difference." },
    ],
  ],
};

// Pick a beat-list for an occasion. Seeded where determinism
// matters (the Determinism Law), random where it does not — the
// counter is not the road and nothing downstream replays it.
export const sheatheSays = (at, roll = Math.random) => {
  const pool = SHEATHE_LINES[at];
  if (!pool || !pool.length) return null;
  return pool[Math.floor(roll() * pool.length) % pool.length];
};

// ══════════════════════════════════════════════════════════════
//  THE ASCENSION LADDER — every sword climbs the same shape
// ══════════════════════════════════════════════════════════════
// FIVE RUNGS, THREE LEVELS. The same law as everything else in
// the Empire, and deliberately the same SHAPE as the constellation
// ladder so a player learns it once:
//
//   I · II    → the object wakes up            (level 1)
//   III · IV  → it learns a verb — an IMPERA   (level 2)
//   V         → it becomes a thing with a NAME (level 3)
//   LORD      → past the ladder. Earned, not bought.
//
// Priced in ⋔ Kindling, because ⋔ buys everything local including
// progression (the Two-Currency Law) and because a sword and a
// star competing for the same coin is the entire reason caP'n
// merged the currencies in the first place.
//
// THE COSTS CLIMB HARD ON PURPOSE. caP'n on the starter branch:
// "which takes far more levels and upgrades for seemingly nothing
//  until it reaches Lord." A tree you finish in one shopping trip
// is a menu. (All dials.)
export const ARM_LADDER = [
  { key: "a1", level: 1, cost: 40,  numeral: "I" },
  { key: "a2", level: 1, cost: 70,  numeral: "II" },
  { key: "a3", level: 2, cost: 120, numeral: "III" },
  { key: "a4", level: 2, cost: 180, numeral: "IV" },
  { key: "a5", level: 3, cost: 260, numeral: "V" },
];
export const ARM_RUNG_KEYS = ARM_LADDER.map((r) => r.key);

export const RULES_ARM = {
  // What it costs to move your investment to a different weapon.
  // caP'n: "can switch and level up others if you want, pay
  // currency and reset your levels." The rungs are not refunded —
  // they are LEFT BEHIND, on the weapon that has them, and they
  // are still there if you come back. This is a fee for the
  // Sheathe's attention, not a respec.
  REFORGE_COST: 60,
  // The Awakening. Every rung lit makes a weapon ELIGIBLE; it is
  // not a Lord until it has gone back out with nothing and earned
  // it. Stats read level 1 for the duration — visually unchanged,
  // which is the cruelty and also the whole point.
  AWAKEN_RUINS: 12,
};

// ── WHAT EACH RUNG GIVES, PER WEAPON ─────────────────────────
// The ladder is shared; the CONTENTS are the weapon's character.
// Every field here mirrors a field on a TREASURES row and is
// simply added to it — so a rung that grants `ranks: { grace: 2 }`
// is indistinguishable downstream from a Treasure that shipped
// with it, which is exactly the seam `deriveRanks` was built on.
//
// `regalia` is the Art the rung teaches THE WEAPON. Under the
// restructure this is where every Impera, U'hlti and Emperium in
// the game now comes from.
//
// A WEAPON WITH NO TREE IS LEGAL. It simply never Ascends, and
// `deriveArm` hands back its base row untouched. That is the
// Exception Law: this table is the set of trees that exist today,
// never the set that may exist.
export const ARM_TREES = {
  // ── THE BRANCH — caP'n's long con ──────────────────────────
  // "an achievement for Ascending and unlocking everything for the
  //  starter sword which takes far more levels and upgrades for
  //  seemingly nothing until it reaches Lord."
  //
  // So it is deliberately the WORST tree in the game for four
  // rungs. No Lyt until the very top. It gives you patience and
  // small mercies and nothing you can show anybody. And then it
  // hands you the Emperium, which nothing else in the rack does.
  t_reed_switch: {
    name: "THE BRANCH THAT REMEMBERED THE TREE",
    a1: { ranks: { grace: 2 }, note: "It stops splintering." },
    a2: { ranks: { wind: 2 }, note: "It stops arguing with your paw." },
    a3: { ranks: { grace: 3 }, regalia: "p_perry_still",
          note: "It learns to slow a thing down by being in the way of it." },
    a4: { lyf: 1, ranks: { heart: 2 }, regalia: "p_perry_hearth",
          note: "Green wood does not break. It bends and it remembers." },
    a5: { lyt: 1, windowMod: 0.10, regalia: "e_perry_emperium",
          note: "The tree it came off is still standing somewhere. It has been in touch." },
    lord: { lyt: 1, lyf: 1, windowMod: 0.08, sight: 1,
            ranks: { grace: 3, heart: 3 }, regalia: "u_perry_answer",
            title: "REEDBROOK'S LAST GREEN THING",
            note: "It was cut from a hedge by somebody who is no longer alive, on a morning nobody remembers, for a job it did not do. It is the oldest thing you own and now it is the loudest." },
  },
  t_pyre_ladle: {
    name: "THE LADLE THAT FED THE ROAD",
    a1: { ranks: { grace: 2 }, note: "Still warm. Still stirring something." },
    a2: { lyf: 1, note: "It has fed people. It knows how long a body lasts." },
    a3: { ranks: { breath: 3 }, regalia: "p_perry_ember_pact",
          note: "It will trade you. It has always been willing to trade you." },
    a4: { ladle: 1, ranks: { heart: 2 }, note: "Twice the mercy, same soup." },
    a5: { lyt: 1, windowMod: 0.08, regalia: "p_perry_kings_grace",
          note: "Whoever held it last was very kind and very tired." },
    lord: { lyt: 1, lyf: 1, ladle: 1, ranks: { breath: 3, heart: 3 },
            regalia: "u_perry_answer", title: "THE LONG TABLE",
            note: "Everyone who ever ate from it is standing behind you now. None of them are armed. It does not seem to matter." },
  },
  t_ash_cleaver: {
    name: "THE CLEAVER THAT DID NOT ASK",
    a1: { lyt: 1, note: "Sharper. Meaner. No wiser." },
    a2: { ranks: { wind: 3 }, note: "You stop being surprised by your own swing." },
    a3: { pierce: 1, regalia: "p_perry_shadow",
          note: "It goes in further than the wound suggests." },
    a4: { windowMod: 0.06, ranks: { wind: 2 }, note: "Heavy stops meaning slow." },
    a5: { lyt: 1, regalia: "u_perry_answer", note: "It has a preference now and the preference is forward." },
    lord: { lyt: 1, pierce: 1, windowMod: 0.06, ranks: { wind: 3, grace: 2 },
            regalia: "e_perry_emperium", title: "THE FIRST BAD IDEA",
            note: "Somebody in Reedbrook once looked at a farming tool and thought about a person. Every axe since has been a footnote to that afternoon." },
  },
  t_lantern_hook: {
    name: "WHYLLIE'S HOOK, WHICH SEES",
    a1: { sight: 1, note: "One more coal says its name." },
    a2: { ranks: { sight: 3 }, note: "It starts looking before you do." },
    a3: { regalia: "p_perry_third_eye", ranks: { grace: 2 },
          note: "It stops telling you and starts showing you." },
    a4: { sight: 1, hurlMod: 0.15, note: "Nothing thrown at you arrives unannounced." },
    a5: { lyt: 1, regalia: "p_perry_mirage", note: "It shows the fen something too." },
    lord: { lyt: 1, sight: 1, windowMod: 0.08, ranks: { sight: 3, grace: 3 },
            regalia: "u_perry_answer", title: "THE LAMP THAT WENT FIRST",
            note: "Whyllie sold it because he could not stand what it kept showing him. He has asked after it exactly once and did not wait for the answer." },
  },
  t_twin_needles: {
    name: "TWO ANSWERS, ONE HAND",
    a1: { ranks: { wind: 3 }, note: "The second needle stops lagging." },
    a2: { lyt: 1, note: "Both of them bite now." },
    a3: { regalia: "p_perry_echo", note: "And then it happens again." },
    a4: { windowMod: 0.08, ranks: { grace: 2 }, note: "Light things forgive light hands." },
    a5: { pierce: 1, regalia: "p_perry_mirage", note: "One of them is not where you think." },
    lord: { lyt: 1, pierce: 1, windowMod: 0.08, ranks: { wind: 3, grace: 3 },
            regalia: "u_perry_answer", title: "THE ARGUMENT",
            note: "They have never agreed about anything and they have never once missed together. Nobody has explained this and the Sheathe has stopped trying." },
  },
  t_bill_of_iron: {
    name: "THE BILL, IN ITS MASTER'S HONOUR",
    a1: { lyf: 1, note: "It is between you and the road now." },
    a2: { ranks: { heart: 3 }, note: "Beaten iron does not care." },
    a3: { pierce: 1, regalia: "p_perry_still",
          note: "The fire slows near it. It always did." },
    a4: { lyt: 1, windowMod: 0.06, note: "Honour, lightly worn." },
    a5: { regalia: "e_perry_emperium", ranks: { heart: 2 },
          note: "It remembers whose it was and does not think you are worse." },
    lord: { lyt: 1, lyf: 1, pierce: 1, windowMod: 0.06,
            ranks: { heart: 3, wind: 3 }, regalia: "u_perry_answer",
            title: "THE DISCIPLE'S APOLOGY",
            note: "It stood in a chapel for a very long time waiting for the sky to be told what to do. Nobody ever came. It has decided, on its own, and it has decided for you." },
  },
  t_mire_fork: {
    name: "THE FORK THAT HOLDS",
    a1: { grip: 0.10, note: "Standing still gets easier." },
    a2: { ranks: { grip: 3 }, note: "And easier." },
    a3: { regalia: "p_perry_hearth", ranks: { breath: 2 },
          note: "Nothing you are holding goes out." },
    a4: { lyf: 1, grip: 0.10, note: "The fen stops being able to move you." },
    a5: { lyt: 1, regalia: "p_perry_kings_grace",
          note: "It decides what a moment is worth." },
    lord: { lyt: 1, lyf: 1, grip: 0.12, ranks: { grip: 3, heart: 3 },
            regalia: "u_perry_answer", title: "THE ACRE THAT SAID NO",
            note: "Every reed in the Drowned Mile bends. There is one place that does not, and it is wherever this is standing." },
  },
  t_drowned_censer: {
    name: "THE CENSER, STILL SMOKING",
    a1: { sight: 1, note: "Three times per foe, now." },
    a2: { ranks: { breath: 3 }, note: "It gives some of the water back." },
    a3: { regalia: "p_perry_third_eye", windowMod: 0.06,
          note: "It names them before they are lit." },
    a4: { ranks: { sight: 3 }, note: "It has started keeping a list." },
    a5: { lyt: 1, regalia: "e_perry_emperium",
          note: "The chapel is still down there and it is still burning." },
    lord: { lyt: 1, sight: 1, windowMod: 0.10, ranks: { sight: 3, breath: 3 },
            regalia: "u_perry_answer", title: "THE SERVICE THAT NEVER ENDED",
            note: "It went under with the roof and it did not stop. Whatever it is for is still being done, twenty feet under a fen, on schedule." },
  },
  t_bell_clapper: {
    name: "THE CLAPPER, WHICH IS ENORMOUS",
    // The top shelf gets the shortest ladder to full weight and
    // the biggest single jumps — it is already an 8 ◆ object and
    // the tree is about MASTERING it, not improving it. Every rung
    // buys back weight. This is the weapon Read the Flame exists
    // for, and Lord is where it stops apologising entirely.
    a1: { windowMod: 0.06, note: "You stop fighting it and start aiming it." },
    a2: { ranks: { wind: 3 }, note: "Your arms catch up to the idea." },
    a3: { pierce: 1, regalia: "p_perry_shadow",
          note: "Nothing it lands on stays entirely where it was." },
    a4: { lyt: 1, windowMod: 0.06, note: "Heavy, and no longer slow." },
    a5: { grip: 0.15, regalia: "e_perry_emperium",
          note: "It has stopped swinging you." },
    lord: { lyt: 1, lyf: 1, pierce: 1, windowMod: 0.10, grip: 0.15,
            ranks: { wind: 3, heart: 3 }, regalia: "u_perry_answer",
            title: "THE BELL, ANSWERED",
            note: "The bell it came out of is still down there and it has not rung since the night it went under. It is going to. That is not a metaphor and you should be somewhere else when it happens." },
  },
};

export const FINISHES = {
  f_ember:   { id: "f_ember",   name: "Ember",   tint: "#ffb058", hot: "#ffe6c2", trail: "#ff8a3c" },
  f_spectre: { id: "f_spectre", name: "Spectre", tint: "#4ff0dc", hot: "#c8fff4", trail: "#2bb8a8" },
  f_soot:    { id: "f_soot",    name: "Soot",    tint: "#8a8078", hot: "#d8dce0", trail: "#4a4440" },
  f_bloom:   { id: "f_bloom",   name: "Bloom",   tint: "#f4726a", hot: "#ffd0c8", trail: "#a8302a" },
  f_gold:    { id: "f_gold",    name: "Kindling", tint: "#f0984a", hot: "#ffbf70", trail: "#8e4a16" },
  f_umbra:   { id: "f_umbra",   name: "Umbra",   tint: "#a06ac0", hot: "#e0c0ff", trail: "#5a3a78" },
};
export const FINISH_ORDER = ["f_ember", "f_gold", "f_spectre", "f_umbra", "f_bloom", "f_soot"];

// One honest line of mechanics per Treasure — assembled from the
// data so a new one can never ship with a stale description.
export function treasureLine(t) {
  const bits = [];
  // The weapon's own number. caP'n, 2026-07-29: "the weapons will
  // now have a number — this plus your Lyt is the chip damage."
  // So it leads, because it is the first thing that matters now.
  if (t.lyt) bits.push(`${t.lyt} EDGE`);
  if (t.lyf) bits.push(`+${t.lyf} LYF`);
  for (const [k, n] of Object.entries(t.ranks || {})) {
    if (TRACKS[k]) bits.push(`+${n} ${TRACKS[k].name.toUpperCase()} RANK${n === 1 ? "" : "S"}`);
  }
  if (t.windowMod) bits.push(`${Math.round(t.windowMod * 100) > 0 ? "+" : ""}${Math.round(t.windowMod * 100)}% WINDOWS`);
  if (t.pierce) bits.push(`PIERCES ${t.pierce} LYF`);
  if (t.hurlMod) bits.push(`+${Math.round(t.hurlMod * 100)}% VS THROWN FIRE`);
  if (t.twin) bits.push("ANSWERS BOTH BEATS OF A SLASH");
  if (t.ladle) bits.push(`${t.ladle}× GOOD → PERFECT PER FIGHT`);
  if (t.sight) bits.push(`${t.sight}× NAMES A COAL PER FIGHT`);
  if (t.grip) bits.push(`${Math.round(t.grip * 100) > 0 ? "+" : ""}${Math.round(t.grip * 100)}% ON HOLDS`);
  const bias = Object.entries(t.bias || {});
  const up = bias.filter(([, v]) => v > 0).map(([k]) => k);
  const dn = bias.filter(([, v]) => v < 0).map(([k]) => k);
  if (up.length) bits.push(`STRONG ${up.join("")}`);
  if (dn.length) bits.push(`SLOW ${dn.join("")}`);
  return bits.length ? bits.join(" · ") : "EVEN ON EVERY ANGLE";
}

// ── CURIOS — checked against the PLAYER, never the board ─────
export const CURIOS = {
  curio_wizards_cap: {
    id: "curio_wizards_cap", name: "Wizard's Cap",
    price: { kindling: 25 }, icon: "🎩",
    desc: "The Empire's handshake. While worn: the first MISS of each Solrend costs no Anima.",
  },
  // Checked against the PLAYER, never the board — same law as the
  // Cap. It is a Divine Crest with a hole worn through it, which
  // is the most 2-2 object in this title: the Empire's own
  // currency, turned into a piece of a beb's kit, minted by nobody.
  //
  // IT PAYS ⋔ NOW, NOT ◆ (2026-07-28). "Every PERFECT pays 1 ◆"
  // was a repeatable Divine Crest faucet worn on a hat, which is
  // the exact thing the Faucet Law forbids — and it was the most
  // efficient one in the Empire, because a competent beb throws
  // PERFECTs by the dozen. The joke survives entirely: it is
  // still a holed Crest, the mudlarks are still furious, and it
  // still pays. It just pays in the currency Perry is allowed to
  // mint. Arguably funnier — the Empire's rarest coin, demoted to
  // a hat, dispensing firewood.
  curio_drowned_coin: {
    id: "curio_drowned_coin", name: "The Mudlark's Coin",
    price: { kindling: 40 }, icon: "🪙",
    desc: "A Divine Crest with a hole worn clean through it. Whatever made it a Crest went out through the hole. While worn: every PERFECT pays 1 ⋔. Mudlarks consider this deeply unfair and have said so, at length, to anyone who will stand in the water and listen.",
  },
};

// ── THE EMBER CONSTELLATION — Perry's skill tree ─────────────
// Small on purpose: a beb's Ascension should feel like an event.
// Positions are constellation coordinates (0..100 both axes).
// ══════════════════════════════════════════════════════════════
//  THE EMBER CONSTELLATION
// ══════════════════════════════════════════════════════════════
// TWO KINDS OF STAR, and the difference is the whole 2026-07-29
// pass:
//
//   RANK STARS — generated, five per track, six tracks. They pour
//   ranks into a pool. Five ranks is a level; three levels is the
//   ceiling; a Treasure's ranks pour into the SAME pool, which is
//   how a weapon can carry you most of the way to a level you never
//   paid a point for.
//
//   ART STARS — hand-placed, one-off, each one a thing you LEARN
//   rather than a number you raise. Primera, Impera, U'hlti,
//   Emperium. These are Arts (caP'n's umbrella noun, 2026-07-29)
//   and they carry a `level` — 1, 2 or 3 — under the same universal
//   ceiling everything else obeys.
//
// The rank stars used to be eleven hand-placed rows saying six
// things in four different shapes. They are now one table and one
// generator, and a SEVENTH TRACK IS A ROW IN `TRACKS`. Nothing
// here needs editing to add one.

// The ladder every track climbs, identically. Deliberately NOT
// five stars of one rank each: the uneven 2/3/2/3/5 shape is what
// makes a Treasure's +3 ranks land somewhere interesting instead
// of always being "most of a star."
//
//   after I+II  → 5 ranks → LEVEL 1
//   after III+IV → 10 ranks → LEVEL 2
//   after V      → 15 ranks → LEVEL 3, and there is no more
const RANK_LADDER = [
  { n: 2, cost: 1, numeral: "I" },
  { n: 3, cost: 1, numeral: "II" },
  { n: 2, cost: 2, numeral: "III" },
  { n: 3, cost: 2, numeral: "IV" },
  { n: 5, cost: 3, numeral: "V" },
];

// Six columns, climbing. Column x is derived from the track's index
// so the fan re-spaces itself if a track is ever added or cut.
const trackStars = () =>
  TRACK_ORDER.flatMap((key, col) => {
    const t = TRACKS[key];
    const x = 8 + col * ((100 - 16) / Math.max(1, TRACK_ORDER.length - 1));
    return RANK_LADDER.map((rung, i) => ({
      id: `n_${key}_${i + 1}`,
      name: `${t.name} ${rung.numeral}`,
      cost: rung.cost,
      x: Math.round(x),
      y: 88 - i * 14,
      req: i === 0 ? [] : [`n_${key}_${i}`],
      fx: { rank: key, n: rung.n },
      track: key,
      // THE COPY LAW: the number, then what it actually buys, then
      // the flavour. A player must never have to click a second
      // thing to find out what a star does.
      desc: `+${rung.n} rank${rung.n === 1 ? "" : "s"} toward ${t.name}. ${t.plain}`,
      lore: t.lore,
    }));
  });

// ── THE ARTS — hand-placed, in the top band above every track ──
// caP'n, 2026-07-29: "Art is a general term for all that jazz —
// primera, impera, affinity, all of my Krush's powers."
//
// So every one of these carries `level`, and the level means the
// same thing it means everywhere else in the Empire:
//   1 · Primera — the seed
//   2 · Impera — the trained verb you press
//   3 · U'hlti / Emperium — costs blood, ONCE PER KIT UP
const ART_STARS = [
  // `fx.regalia` is what every derivation reads. `fx.impera` is
  // kept verbatim as the compatibility surface it always was.
  { id: "n_imp_still", name: "Still the Flame", cost: 1, x: 30, y: 22, req: ["n_breath_1"],
    fx: { impera: "p_perry_still", regalia: "p_perry_still" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Still the Flame." },
  { id: "n_imp_eye", name: "Third Eye", cost: 1, x: 18, y: 14, req: ["n_imp_still"],
    fx: { impera: "p_perry_third_eye", regalia: "p_perry_third_eye" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Third Eye." },
  { id: "n_imp_pact", name: "Ember Pact", cost: 1, x: 42, y: 14, req: ["n_imp_still"],
    fx: { impera: "p_perry_ember_pact", regalia: "p_perry_ember_pact" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Ember Pact." },

  // ══════════════════════════════════════════════════════════
  //  THE BORROWED CROWN — opened 2026-07-28
  // ══════════════════════════════════════════════════════════
  // caP'n's five. Not "more of what you had" — the argument of the
  // whole title said out loud: a beb reaching above his rung and
  // getting there. Re-parented 2026-07-29 onto the track stars
  // that replaced Deep Breath and Sharpened Answer.
  { id: "n_shadow", name: "Shadow", cost: 2, x: 62, y: 22, req: ["n_breath_1"],
    fx: { regalia: "p_perry_shadow" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Shadow. 1 Chao — go Intangible, and let exactly one blow pass through you." },
  { id: "n_echo", name: "Echo", cost: 2, x: 78, y: 22, req: ["n_wind_1"],
    fx: { regalia: "p_perry_echo" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Echo. 1 Chao — your next landed answer rings twice. One free hit, straight through Lyf." },
  { id: "n_kings_grace", name: "King's Grace", cost: 3, x: 70, y: 14, req: ["n_shadow"],
    fx: { regalia: "p_perry_kings_grace" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: King's Grace. 2 Chao — for eight seconds everything swung at you comes slower. A beb cannot hold it for longer." },
  { id: "n_hearth", name: "Bank the Hearth", cost: 2, x: 90, y: 14, req: ["n_echo"],
    fx: { regalia: "p_perry_hearth" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Bank the Hearth. 3 Chao — bank the breath as heat. +1 Anima, once per foe. The Ember Pact, run backwards." },
  { id: "n_mirage", name: "Mirage", cost: 3, x: 54, y: 8, req: ["n_shadow", "n_echo"],
    fx: { regalia: "p_perry_mirage" }, impera: true, art: true, legacy: true, level: 2,
    desc: "Learn the Impera: Mirage. 2 Chao — the next blow that would land on you is answered PERFECTLY instead, even if you missed, and it bites back." },

  // ── LEVEL 3 — the ceiling, and ONCE PER KIT UP ──
  { id: "n_uhlti", name: "THE ANSWER — U'hlti", cost: 3, x: 30, y: 4,
    req: ["n_imp_eye", "n_imp_pact", "n_grace_2"],
    fx: { uhlti: true, regalia: "u_perry_answer" }, uhlti: true, impera: true, art: true, legacy: true, level: 3,
    desc: "Learn the U'hlti: The Answer. 3 Chao AND 2 Anima — the next strike is answered PERFECTLY, whatever it is, from wherever it comes, in whatever dark it comes out of. Once per Kit Up." },
  // THE TOP OF THE SKY. Behind the U'hlti, which is behind three
  // other stars, which is behind a real climb — and it costs four.
  // It is the single most expensive thing a Soul can hold.
  { id: "n_emperium", name: "THE EMPERIUM", cost: 4, x: 8, y: 4, req: ["n_uhlti"],
    fx: { regalia: "e_perry_emperium" }, emperium: true, impera: true, art: true, legacy: true, level: 3,
    desc: "Speak your own Emperium. 5 Chao AND 1 Anima — the road becomes yours for twenty seconds, and answers three strikes for you. The ones you do not spend are gone with it. Once per Kit Up." },

  // ══════════════════════════════════════════════════════════
  //  THE FAUSTIAN FORK — SHELVED 2026-07-29, NOT DELETED
  // ══════════════════════════════════════════════════════════
  // caP'n: "I like where you're going with it, like a monkey paw
  //  thing sort of, but it's just way too detrimental. We'll shelve
  //  it for now, just leave it alone."
  //
  // It used to sell +2 Lyt for a permanent Chao lock. Lyt is no
  // longer for sale at any price, so its entire product is gone —
  // and rather than invent a replacement nobody asked for, the star
  // sits here `shelved: true`, unrendered, unbuyable, its id intact
  // so any Soul that already took it keeps its flag and its history.
  //
  // DO NOT RE-PRICE THIS OR HANG A NEW EFFECT ON IT. When the Fork
  // comes back it comes back as a design conversation, not as a
  // field edit. Roadmap R-46.
  { id: "n_deviant", name: "Deviant Ember", cost: 0, x: 96, y: 96, req: [], deviant: true,
    shelved: true, fx: {},
    desc: "Shelved. The Fork will be back — it will not be back as +2 Lyt." },
];

export const NODES = [...trackStars(), ...ART_STARS];

// ── LEGACY NODE IDS — nobody logs in to a nerf ────────────────
// The 2026-07-29 pass renamed eleven stars. A Soul that took
// `n_anima_3` two sessions ago still has that key in Firebase and
// always will, because earned data is only ever added to. So every
// derivation reads through `soulNodes()`, which translates the old
// id to the new one on the way past. No migration, no write, no
// window where a player's sky is empty.
//
// `n_grace_1..3` kept their ids by luck of naming and are absent
// here on purpose — they need no translation.
export const LEGACY_NODES = {
  n_anima_1: "n_heart_1", n_anima_2: "n_heart_2",
  n_anima_3: "n_heart_3", n_anima_4: "n_heart_4",
  n_chao_1: "n_breath_1", n_chao_2: "n_breath_2", n_chao_3: "n_breath_3",
  n_lyt_1: "n_wind_1", n_lyt_2: "n_wind_2", n_lyt_3: "n_wind_3",
  n_recall: "n_sight_1", n_recall_2: "n_sight_2",
  n_grip: "n_grip_1",
};

// The ONE way anything reads a Soul's stars. Translates legacy ids,
// drops anything that no longer exists, returns a plain id array.
export const soulNodes = (soul) => {
  const out = [];
  for (const raw of Object.keys(soul?.nodes || {})) {
    if (!soul.nodes[raw]) continue;
    const id = LEGACY_NODES[raw] || raw;
    if (nodeById(id) && !out.includes(id)) out.push(id);
  }
  return out;
};

// ── ENEMIES — the bestiary ───────────────────────────────────
// ══════════════════════════════════════════════════════════════
//  THE TWO BARS — caP'n's ruling, 2026-07-29
// ══════════════════════════════════════════════════════════════
// `lyf` — THE CHIP BAR. Every landed answer takes (your Lyt + your
//   weapon's edge) off it. When it reaches zero the thing is
//   FRACTURED, permanently, for the rest of the fight.
//
// `anima` — THE FINISH. How many more parries it takes to end a
//   thing that is ALREADY fractured. One answer, one Anima, flat —
//   this bar does not care how big your axe is.
//
// A BASIC ENEMY HAS NO LYF AT ALL. `lyf: 0` means it walks onto
// the road pre-Fractured (Terror R29: a 0-Lyf being is permanently
// Fractured — we did not invent this exception, we inherited it),
// so every parry takes exactly one Anima and a husk with Anima 1
// dies to your first clean answer. That is the tutorial, and it is
// the same code path as the final boss.
//
// ── THE ANIMA CEILING: FIVE, AND ONLY FIVE THINGS ──
// "The highest Anima enemies you'll fight only go up to 5, and
//  there will only ever be 5 enemies like that in the entire game."
// Both halves are law. 4 is very-late endgame. Most bosses sit at
// 1 for a long, long time — a boss is HARD because its Lyf is a
// wall, not because its finish is a slog. See RULES.ANIMA_CEILING
// and the ledger under it.
//
// Small numbers everywhere else.
// A foe you never break RETREATS when its beats run out — you
// pass, but no Ruin pay. Chip it or don't swing.
//
// MOVESET is the new load-bearing field. It is a WEIGHT TABLE,
// not a sequence: the director rolls the next strike out of it
// with the run's seeded PRNG, so the same husk fights you a
// different way every Solrend and the same way twice inside one.
// That is the whole difference between a script and a battle.
//   tempo — multiplies every wind-up. Lower = meaner.
//
// ── ARMS, added 2026-07-27 ──
// `arms` is a LIST, and a body picks `arms[index % arms.length]`.
// Two husks rising out of the same pyre are not carrying the same
// thing, because two farmhands never grabbed the same thing when
// the fire came. Legal values live in `FoeWeapons.js`:
//   "scythe" · "pale" · "kunai" · "none"
// `bar` opts a foe into the floating Lyf pips over its head. It is
// deliberately NOT automatic — a road full of health bars is a
// menu, not a road. Bosses get one. The exception gets one.
export const ENEMIES = {
  burning_husk: {
    id: "burning_husk", name: "Burning Husk", rung: "Escling",
    anima: 1, lyt: 1, lyf: 0, tint: "#e05a2a", tempo: 1,
    moveset: { swipe: 7, lunge: 3, feint: 2 },
    arms: ["scythe", "pale"],
    lore: "What the pyre keeps. It remembers having hands, and it remembers what was in them.",
  },
  ridge_husk: {
    id: "ridge_husk", name: "Ridgeback Husk", rung: "Escling",
    anima: 2, lyt: 1, lyf: 1, tint: "#c8482a", tempo: 0.94,
    moveset: { swipe: 5, lunge: 3, slash: 3, feint: 3 },
    arms: ["pale", "scythe"],
    lore: "It climbed out of the fire twice. It thinks that makes it patient.",
  },

  // ── TH'RAKK — the Ridge Gate's last warden ───────────────────
  // The tutorial's boss, and the first thing on the Cinders Road
  // that is NOT beneath Perry: rung Beb, same as he is. It was a
  // person with a job. The job was to haul burning timber off the
  // gate road with a hooked blade on a chain so the village could
  // get out, and it did that job for three days, and then it
  // stopped being able to stop.
  //
  // It burns TEAL. Every other fire in Act I is ember-orange; the
  // ridge is where the fire ran out of Reedbrook to eat, and what
  // is left in Th'rakk has gone cold and spectral. On a road of
  // orange, the one cold thing is the one you watch. (It is also
  // the Empire's colour, and J'ynxi is sitting on a fencepost
  // eight beats back with the same eye. Read into that.)
  chain_warden: {
    id: "chain_warden", name: "Th'rakk", rung: "Beb",
    anima: 2, lyt: 1, lyf: 3, tint: "#6fe6d4", tempo: 0.9,
    boss: true, bar: true,
    arms: ["kunai"],
    moveset: { chain: 6, swipe: 4, slash: 3, lunge: 3, feint: 3 },
    lore: "The Ridge Gate's last warden. It hauled burning beams off this road for three days with a hooked blade on a chain so that others could pass, and nobody ever told it to stop, so it has not. The chain is called the Cinder Haul. It still works.",
  },
  // ══════════════════════════════════════════════════════════
  //  THE WARDEN BENEATH — Act I's one hard thing (caP'n, 2026-07-30)
  // ══════════════════════════════════════════════════════════
  // "Act 1 should never have one of those tough bosses maybe 1?
  //  for like a supppper secret boss? there's supposed to be 5
  //  scattered so that works as the only one there."
  //
  // So Act I gets exactly one, and it takes SEAT TWO of the Anima
  // Ceiling — the seat that has read "the Secret — unnamed,
  // unbuilt, harder than the last one" since the ceiling was
  // written. It is named now.
  //
  // Th'rakk is called the Ridge Gate's LAST warden. A last warden
  // implies earlier ones, and the road never says what happened to
  // them. This is what happened to them. It did the same job for
  // longer, under the gate rather than on it, and nobody has ever
  // told it that the gate is gone.
  //
  // IT IS DELIBERATELY NOT ON A ROAD YET. `secret: true` keeps it
  // out of the ordinary encounter flow; its door is an unlock
  // condition, which is the same machinery Cursed Weapons need
  // (R-51) and is roadmapped with them at R-52. A boss with no
  // way in is content waiting for a key, not a bug.
  //
  // THE EXEMPTION IS FLAGGED, NOT INFERRED. `animaSeat` + `secret`
  // is what lets it stand past ACT_TIERS.act1 — `auditBestiary()`
  // reads those two fields rather than guessing from the name, and
  // it still holds this row to ANIMA_CEILING and FOE_LYF_CEILING
  // like everything else. Anima 2 today; the seat is what makes
  // the climb toward 5 legal later without a code change.
  warden_beneath: {
    id: "warden_beneath", veil: true, secret: true,
    name: "The Warden Beneath", rung: "Champion",
    anima: 2, lyt: 2, lyf: 4, tint: "#3a5a7a", tempo: 0.8,
    boss: true, bar: true, animaSeat: true,
    arms: ["kunai"],
    moveset: { chain: 6, cleft: 4, undertow: 4, slash: 3, feint: 4, calamity: 3 },
    lore: "Th'rakk is called the Ridge Gate's last warden, and a last warden means there were others. Nobody in Reedbrook could tell you where the ones before it went, because the answer is down, and down is not a direction anybody in Reedbrook had reason to look. It is still holding the beam. It has been holding the beam for a very long time and the beam has been part of its arm for most of that, and it will hand the job to you if you let it finish asking.",
  },

  // ── THE DROWNED MILE'S BESTIARY ─────────────────────────────
  // `metOnly` came off all three of the old placeholders on
  // 2026-07-28 — they are on a road now, which is what they were
  // written for.
  ash_hound: {
    id: "ash_hound", veil: true, name: "Ash Hound", rung: "Beb",
    anima: 2, lyt: 1, lyf: 0, tint: "#8a8078", tempo: 0.84,
    // ── ITS OWN BODY, 2026-07-29 ──
    // It shipped on the husk's biped frame and read, correctly, as
    // broken: a dog standing upright with two idle arms, pale grey
    // against black water. `rig` was always a string field exactly
    // so this would cost one word. It costs one word.
    rig: "hound",
    arms: ["none"],
    // It does not carry anything. It IS the thing it hits you
    // with — which is why the roundhouse belongs to it and not to
    // anything that ever held a tool.
    moveset: { lunge: 6, roundhouse: 5, slash: 3, feint: 2 },
    lore: "Runs the ridgelines beyond Hollowmere and comes down into the fens to drink, which it does not need to do, which is the part worth worrying about. Faster than the road is long.",
  },
  ember_imp: {
    id: "ember_imp", veil: true, name: "Ember Imp", rung: "Beb",
    anima: 1, lyt: 1, lyf: 3, tint: "#ffab58", tempo: 0.95,
    arms: ["none"],
    moveset: { hurl: 6, comet: 4, slash: 3, feint: 4 },
    lore: "Giggles in sparks. Throws what it is made of. Its skin turns lazy parries — only the clean break lands, and it knows that, and that is why it giggles.",
  },

  // ── THE MUDLARK ── caP'n, R-40: "coins, ... divine crests
  // (mini goblin game ;3)". Here it is, and it is the funniest
  // fight in the title.
  //
  // It is not trying to kill you. It is trying to make you flinch
  // so it can go through your paws while you are busy. Every coin
  // you answer cleanly, you KEEP — `bounty` on the attack, no
  // special case anywhere in the engine. The joke lands because
  // the mechanics are honest: this fight genuinely pays.
  mudlark: {
    id: "mudlark", veil: true, name: "The Mudlark", rung: "Escling",
    anima: 2, lyt: 1, lyf: 0, tint: "#c9a227", tempo: 1.02,
    arms: ["none"],
    moveset: { coinstorm: 8, swipe: 3, feint: 4 },
    lore: "Knee-high, filthy, and richer than most of Candlemarch. It throws Divine Crests at travellers — real ones, the Empire's own, which is a fact nobody in Hollowmere has been able to get comfortable with. It does this because a startled traveller drops things, and a dropped thing in a fen is a found thing. The Crests are not among the things you get to keep. They go in the water, every time, and it goes in after them. It has never once been sorry. It has, on three occasions, been extremely apologetic, which is different.",
  },

  // ── THE REEDMAW ── the hold, taught by the thing that invented
  // it. It does not strike. It OPENS, and then keeps opening, and
  // the only answer to a thing that will not stop is to not stop
  // either.
  // 5 → 3 LYF, 2026-07-29. THE CEILING IS NOT A TARGET. caP'n:
  // "basically the hardest ones only — don't wanna give it to
  //  everyone then it doesn't feel rare." The Reedmaw is a Beb in
  // the middle of an act, not a Champion at the end of one, and it
  // was sitting at the same wall as T'yphiz. Three is a real skin
  // that a fresh edge still gets through in two answers. (Dial.)
  reedmaw: {
    id: "reedmaw", veil: true, name: "Reedmaw", rung: "Beb",
    anima: 2, lyt: 1, lyf: 2, tint: "#3f8f7a", tempo: 0.92,
    arms: ["none"], bar: true,
    moveset: { jet: 6, undertow: 4, swipe: 3, feint: 3 },
    lore: "There is a place in the reeds where the reeds are wrong. Stand still long enough and the wrong part opens. The fenfolk have a word for it and they will not teach you the word, on the grounds that saying it out loud in the wrong acre is how people learn what it means.",
  },

  // ── THE DISCIPLE OF T'YPHIZ ─────────────────────────────────
  // Old Maren named it in Hollowmere on the first night: "a thing
  // with a bill of beaten iron, east of here." It has been in the
  // bestiary since the slice was founded, waiting for a road.
  //
  // It is the first CHAMPION Perry meets — one full rung above
  // him, where Th'rakk was merely level — and everything about the
  // fight says so in a different language:
  //   · Lyf at the CEILING behind Lyt 2. It is the first wall in
  //     the game that a fresh edge genuinely has to grind, and the
  //     first foe whose hits cost you two Anima at a time.
  //   · SIGHT DARK. It is the first thing in the Empire to put
  //     the coals out (R-41), and it does not gloat about it.
  //   · The CLEFT — the game's first chord. Two answers, one
  //     instant, one hand each.
  //   · It carries the Bill. Break it and the Bill is yours,
  //     which is where Whyllie's second-hand one came from and
  //     why his story about it was always slightly evasive.
  //
  // 9 → 5 LYF, 2026-07-29, THE FOE LYF CEILING. It kept its Anima.
  // A Champion is allowed the ceiling; nothing is allowed past it.
  disciple: {
    id: "disciple", veil: true, name: "The Disciple of T'yphiz", rung: "Champion",
    anima: 4, lyt: 2, lyf: 3, tint: "#a06ac0", tempo: 0.86,
    boss: true, bar: true, rig: "disciple",
    arms: ["bill"],
    moveset: { cleft: 5, slash: 4, comet: 4, lunge: 3, feint: 4, calamity: 3 },
    lore: "It wears a bill of beaten iron in its master's honour, and it has worn it so long that the honour and the iron have stopped being separate things. It does not burn. It was never on fire. It walked into the fen behind T'yphiz on the third night of Reedbrook and it has been standing in the chapel ever since, waiting for the sky to be told what to do.",
  },
  // `animaSeat` is the data knob that says "this thing is allowed
  // past ANIMA_COMMON_CAP." One field, no name-matching against
  // prose, and the audit reads it instead of guessing. T'yphiz is
  // at 3 today and climbs toward the ceiling later; the seat is
  // what makes that climb legal without a code change.
  typhiz: {
    id: "typhiz", veil: true, name: "T'yphiz", rung: "Champion",
    anima: 3, lyt: 3, lyf: 5, tint: "#d43a24", tempo: 0.76,
    // ── `boss` / `bar` ADDED 2026-07-30 — auditBestiary()'s first
    //    real catch, and it was a good one.
    // The Champion at the end of the game has never carried either
    // flag. Two consequences, both invisible until he stood on a
    // road: `bar` is what draws the floating Lyf pips, so the
    // BIGGEST WALL IN THE TITLE would have been the only boss
    // fighting you without showing it — and EnemyBody resolves its
    // rig as `foe.rig || (foe.boss ? "warden" : "husk")`, so
    // T'yphiz would have rendered on the ordinary husk frame.
    // Horned, winged, tail pointed yet paddled, and built out of a
    // burning farmhand.
    //
    // Nothing regressed, because he is not placed on a script yet
    // — Act III's road is the Ashfall Stair and it is unbuilt. He
    // is correct BEFORE he is ever walked toward, which is the
    // only cheap time to be correct about a thing like this.
    animaSeat: true, boss: true, bar: true,
    moveset: { hurl: 5, swipe: 4, slash: 4, lunge: 3, feint: 4, calamity: 5 },
    lore: "Horned. Winged. A tail pointed yet paddled, feet webbed, a bill like a duck's — and none of it funny once the sky catches fire. The village named it. The name did not help them.",
  },
};


// ══════════════════════════════════════════════════════════════
//  WHICH TIER A FOE BELONGS TO — and the audit that enforces it
// ══════════════════════════════════════════════════════════════
// A foe's act is not derivable from anything it carries, and it
// should not be: the same husk could legitimately stand on two
// roads. So it is stated, once, here — a map from foe id to tier
// id, and an unlisted foe is treated as its own island (audited
// against the ceilings only). One line per foe, and moving a foe
// between acts is that one line.
//
// (T'yphiz sits in act3 and act3 has no script yet. That is
//  correct — he is the END GAME Champion and the road to him is
//  the Ashfall Stair, still unbuilt. Filing him under the tier he
//  is balanced FOR is what stops him being re-tuned as though he
//  were an Act II boss the next time somebody sweeps this file.)
export const FOE_ACT = {
  burning_husk: "act1", ridge_husk: "act1", chain_warden: "act1",
  warden_beneath: "act1",
  ash_hound: "act2", ember_imp: "act2", mudlark: "act2",
  reedmaw: "act2", disciple: "act2",
  typhiz: "act3",
};

// ── HOW BIG A FOE ACTUALLY IS, under the regenerating wall ────
// The number nobody could see before, and the reason the whole
// bestiary needed re-tiering on 2026-07-30: a foe's real size is
// not its Lyf and not its Anima, it is how many answers it takes
// at a given edge. `anima × (⌈lyf ÷ edge⌉ + 1)`.
//
// Use it when tuning. A row that looks reasonable and audits clean
// can still be a twenty-answer slog, and this is the function that
// says so out loud before a player has to find out.
export const answersToRuin = (foe, edge = 1) => {
  const e = Math.max(1, edge || 1);
  const wall = Math.ceil((foe?.lyf || 0) / e);
  return (foe?.anima || 1) * (wall + 1);
};

// ── ENCOUNTERS — battles, not scripts ────────────────────────
// An encounter no longer lists its strikes. It states how LONG
// the thing intends to fight (beats), how hard it breathes
// between them (gap), and — optionally — overrides the enemy's
// natural moveset. Everything else is rolled live off the run's
// seed, escalating as the foe takes damage.
//
//   beats   how many strikes it throws before it gives up and
//           shambles off. Survive them all and it retreats: you
//           pass, you get nothing. Break it and you get the Ruin.
//   gap     [min,max] ms of quiet between strikes.
//   moveset optional override — a teaching fight can refuse to
//           lie, and the second one can start.
export const ENCOUNTERS = {
  e_first_husk: {
    id: "e_first_husk", enemy: "burning_husk",
    intro: "A hand closes on your ankle. The pyre is not finished with its dead.",
    beats: 2, gap: [900, 1300],
    moveset: { swipe: 9, lunge: 2 },        // the first fight never lies to you
    // STALE TEXT KILLED 2026-07-27 — this still described the old
    // rose (S D F high / J K L low), which the Grid Relay deleted.
    // It has been wrong on screen since that pass shipped.
    teach: "It winds up from somewhere. Answer that somewhere. Left column is your left hand, right column your right, and each row is the same finger on both — F J on top, D K in the middle, S L on the bottom. Every coal burns its own colour. Learn the colours; the letters do not follow you out of here.",
    // The tutorial is LETTERED whatever the Soul says, so a New
    // Game after a hundred clears still teaches properly.
    sight: SIGHT.LETTERED,
  },
  e_husk_pair: {
    id: "e_husk_pair", enemy: "burning_husk", count: 2,
    intro: "Two more rise. The fire is generous tonight.",
    beats: 5, gap: [780, 1200],
    moveset: { swipe: 6, lunge: 4, slash: 2, feint: 2 },
    // No `sight` — it inherits. Lettered on a first walk, coloured
    // on every walk after. This is the fight where a returning
    // player first works without letters, and it is a pair of
    // husks on purpose: the softest thing on the road.
  },
  // ── THE TUTORIAL'S BOSS ──
  // Numbers here are DIALS: 5 Lyf to chip through, then 1 Anima to
  // finish — six clean answers total, inside ten beats. Generous
  // on purpose — this is the fight that has to teach the chain,
  // and a wall does not teach anything.
  e_ridge: {
    id: "e_ridge", enemy: "chain_warden",
    intro: "Something is standing in the gate. It is not smoking. Whatever burned in it burned out, and what is left is cold and blue and holding a chain.",
    beats: 10, gap: [720, 1180],
    teach: "It will not come to you. It throws the blade PAST you — then hauls, and the blade comes back from a side it did not tell you. Watch the chain go tight. And watch the coals: the warden has moved them. Each one still burns its own colour. Only the places are lies.",
    // ── THE FIRST FOE THAT GETS CREATIVE, 2026-07-27 (caP'n) ──
    // One word. That is the whole feature on this end — the
    // director rolls a deranged permutation off the run seed and
    // the coals hang at each other's angles for the length of the
    // fight. The fire's real angle is unchanged, so a player who
    // reads the creature never notices, a player who learned the
    // COLOURS is fine, and a player who was reading "which square
    // lit up" gets taken apart. That is the lesson, and Th'rakk is
    // the right thing to teach it: it is already the one cold
    // thing on a hot road.
    //
    // A returning Soul meets this at the end of a tutorial it can
    // skip; a first-timer meets it lettered, because a live charge
    // and the tutorial both outrank the encounter (resolveSight).
    sight: SIGHT.SHUFFLED,
  },

  // ══════════════════════════════════════════════════════════
  //  ACT II — THE DROWNED MILE
  // ══════════════════════════════════════════════════════════
  // Five fights, and the Sight ladder climbs one rung per fight
  // until the last one takes the ladder away. That is not a
  // difficulty curve bolted on afterwards — it IS the act's
  // argument, said five times in five different creatures:
  //
  //   the hound     COLOURED   you know this. Go faster.
  //   the Mudlark   COLOURED   ...and it pays. Be greedy.
  //   the Reedmaw   PARTIAL    half the coals are out. Notice
  //                            that you did not need them.
  //   the imps      SHUFFLED   and they RE-shuffle, twice, mid-
  //                            fight. Position is now a liar that
  //                            changes its story.
  //   the Disciple  DARK       read the creature. That was always
  //                            the only question.
  e_fen_hound: {
    id: "e_fen_hound", enemy: "ash_hound",
    intro: "Something comes off the bank at a dead run and is already close. It does not bark. Nothing out here barks.",
    beats: 5, gap: [640, 1020],
    teach: "It does not carry anything, so nothing tells you where the blow is except the whole animal. Watch its hips — a thing that turns its whole body into a strike has to turn its whole body FIRST.",
  },
  e_mudlark: {
    id: "e_mudlark", enemy: "mudlark",
    intro: "A shape the size of your knee stands up out of the mud holding a fistful of money, and looks at you the way a problem looks at a solution.",
    beats: 6, gap: [560, 940],
    moveset: { coinstorm: 9, feint: 3, swipe: 2 },
    teach: "It throws Divine Crests. They bounce off the boards once before they reach you, so the angle it left on is NOT the angle it arrives on — watch the second half of the flight, never the first. You do not keep them; nobody keeps them. You get paid for the answer, same as always, and the coin goes in the fen.",
  },
  e_reedmaw: {
    id: "e_reedmaw", enemy: "reedmaw",
    intro: "The reeds ahead are the wrong colour and have been for about four seconds. Then they open.",
    beats: 7, gap: [800, 1240],
    teach: "This one does not hit you. It POURS. Meet it on the beat like anything else — and then STAY THERE. Hold the angle down until it stops. Let go early and the rest of it goes through the gap you just made.",
    // ── PARTIAL gets its first owner, 2026-07-28 (R-41) ──
    // Three coals lit, three out, rolled off the run seed. It is
    // the gentlest possible way to be told the coals were never
    // the point — you will answer three strikes with no coal at
    // all before you notice you did it.
    sight: SIGHT.PARTIAL,
  },
  e_imp_pair: {
    id: "e_imp_pair", enemy: "ember_imp", count: 2,
    intro: "Two of them, sat on the same drowned roof beam, kicking their heels. They have been waiting. They are DELIGHTED.",
    beats: 8, gap: [700, 1100],
    teach: "Their skin turns a lazy answer aside — chip damage is worthless here, so a GOOD keeps you alive and does nothing else. You need the clean break, or something that ignores skin. And they have moved the coals. Twice. They will do it again.",
    sight: SIGHT.SHUFFLED,
    // ── THE RE-SHUFFLE, 2026-07-28 (R-41) ──
    // Th'rakk rolls the permutation once, at the start. The imps
    // re-roll it every time one of them is wounded. A player who
    // learned the new positions in the first thirty seconds gets
    // to learn them again, and then once more, which is the exact
    // moment most people stop looking at the coals for good.
    reshuffle: true,
  },
  // ── THE BOSS ──
  // 5 Anima behind Lyf 1 with Lyt 2 of its own, over 12 beats.
  // Numbers are DIALS. The shape is not: it must be survivable
  // with a base-Lyt beb who reads well (you eat the Lyf wall,
  // never break it, and it retreats), and BREAKABLE by a beb who
  // brought the right thing — the Cleaver, the Bill, Black Water,
  // a Fracture, or three Ascensions of Sharpened Answer.
  //
  // "You pass, you get nothing" has never mattered before now.
  // Here it costs you the Bill.
  e_disciple: {
    id: "e_disciple", enemy: "disciple",
    intro: "It is standing in the water where the altar used to be, and it has been standing there long enough that the reeds have grown up around its feet and been drowned again. It does not burn. It turns its head, and the bill catches what light there is, and there is not much.",
    beats: 12, gap: [760, 1220],
    teach: "It brings the bill down through TWO places at once. One key from each hand, together — the row you press is the row it comes from, and there is one from your left and one from your right. And the coals are out. All of them. Everything you have learned about reading a creature was for this.",
    // ── DARK gets its first owner, 2026-07-28 (R-41) ──
    // caP'n's ladder, finished. A live charge still outranks it —
    // that is the whole reason Tomes, Treasures, nodes and Imperas
    // grant charges at all, and the Censer, the Page, the Recall
    // and the Third Eye are all sitting in the shop waiting for
    // exactly this fight.
    sight: SIGHT.DARK,
  },
};


// ── CAMERA SHOTS — solved marks, not tuned freedoms ──────────
// mode "fixed":  a nailed-down tripod (pos, aim in world units).
// mode "dolly":  x rides Perry (pos = [perry.x+dx, y, z]); y/z/aim
//                offsets are nailed — a camera on rails beside the
//                road. One retained freedom, on rails, on purpose.
// mode "fp":     Perry's own eyes (yaw relative to his facing).
// Every shot change between beats is a CUT (instant), like film.
export const SHOTS = {
  s_wide_open:  { mode: "fixed", pos: [-6, 4.2, 9.5], aim: [4, 1.1, -2], fov: 42 },
  s_dolly_side: { mode: "dolly", dx: 1.2, y: 1.7, z: 6.2, aimDy: 0.9, aimDx: 0.6, fov: 40 },
  s_dolly_front:{ mode: "dolly", dx: 4.6, y: 1.4, z: 1.6, aimDy: 1.0, aimDx: -1.5, fov: 44 },

  // ── THE FIGHT MARKS, reframed 2026-07-27 ──
  // A fight camera has ONE job now: keep Perry centred with a
  // full lane of clear air on BOTH sides, because the foe is
  // going to be standing in one of them. The old marks sat
  // downstage of Perry at dx +1.8, which put the enemy directly
  // between the lens and the man — the enemy occluded the very
  // guard it was aiming at. These sit square, dead level with
  // the rose, and never dolly past him.
  //   `combat: true` tells the director this shot is a fight
  //   mark; the hint rose and the lane logic key off it.
  s_qte_close:  { mode: "dolly", combat: true, dx: 0.05, y: 1.02, z: 4.15, aimDy: 0.62, aimDx: 0.02, fov: 44 },
  s_qte_wide:   { mode: "dolly", combat: true, dx: 0.05, y: 1.35, z: 5.60, aimDy: 0.70, aimDx: 0.02, fov: 42 },
  s_qte_close2: { mode: "dolly", combat: true, dx: -0.10, y: 1.10, z: 4.35, aimDy: 0.64, aimDx: 0.02, fov: 44 },

  s_fp_back:    { mode: "fp", yaw: Math.PI, fov: 58 },
  s_gate:       { mode: "fixed", pos: [36, 1.2, 5.5], aim: [33, 1.4, -1], fov: 40 },
  s_dolly_rise: { mode: "fixed", pos: [38, 6.5, 10.5], aim: [46, 0.6, -4], fov: 40 },

  // ── ACT II — THE DROWNED MILE ──────────────────────────────
  // The fen's shots sit LOWER than the Cinders Road's, all of
  // them, on purpose. On the road the camera looked at a small
  // creature walking through a big burned country; here it is
  // down near the waterline with him, because the whole feeling
  // of the act is that the ground has stopped being reliable and
  // the horizon is four feet away in every direction.
  //
  // The three fight marks are `combat: true` like every fight
  // mark — the guard rose, the lane logic and the hint rose all
  // key off that one flag, not off which act they are in.
  s_fen_open:   { mode: "fixed", pos: [-5, 2.6, 8.5], aim: [5, 0.8, -2], fov: 44 },
  s_fen_side:   { mode: "dolly", dx: 1.4, y: 1.15, z: 5.6, aimDy: 0.7, aimDx: 0.7, fov: 42 },
  s_fen_low:    { mode: "dolly", dx: 2.8, y: 0.44, z: 3.4, aimDy: 0.55, aimDx: -0.6, fov: 46 },
  s_fen_wide:   { mode: "dolly", dx: -3.2, y: 2.9, z: 7.4, aimDy: 0.6, aimDx: 2.2, fov: 40 },
  s_qte_water:  { mode: "dolly", combat: true, dx: 0.05, y: 0.94, z: 4.05, aimDy: 0.60, aimDx: 0.02, fov: 45 },
  s_qte_reeds:  { mode: "dolly", combat: true, dx: 0.05, y: 1.28, z: 5.35, aimDy: 0.68, aimDx: 0.02, fov: 43 },
  s_qte_chapel: { mode: "dolly", combat: true, dx: -0.08, y: 1.06, z: 4.45, aimDy: 0.64, aimDx: 0.02, fov: 44 },
  s_chapel:     { mode: "fixed", pos: [43.5, 1.6, 6.2], aim: [40.5, 2.2, -1.5], fov: 38 },
  s_fen_rise:   { mode: "fixed", pos: [46, 5.4, 9.8], aim: [54, 1.0, -3.5], fov: 40 },
  s_fp_mile:    { mode: "fp", yaw: Math.PI, fov: 56 },
};

// ── ACT I — THE CINDERS ROAD (the run script) ────────────────
// Perry walks +X from x=0 to ROAD_END. Reedbrook's pyres line
// the road; Hollowmere's lanterns wait past the ridge.
export const ROAD_END = 44;

export const ACT1 = [
  // ── R-09, RESOLVED THE OTHER WAY, 2026-07-28 (caP'n) ──
  // "I opted to make the tutorial area the actual first level
  //  again since it's actually pretty tough but not too tough — it
  //  can be the tutorial but still a real first level, so let's
  //  rename it to the Level 1 and there is no skip now."
  //
  // The plan of record was: Cinders becomes THE TUTORIAL, gets a
  // SKIP, and the road out of Hollowmere becomes Level 1. That is
  // now RETIRED. The road is Level 1. It teaches BY BEING PLAYED,
  // which is the only way anything has ever taught anyone
  // anything, and there is no door out of it. The fens are Level 2.
  { type: "title", shot: "s_wide_open", dur: 3400,
    text: "LEVEL 1 — THE CINDERS ROAD", sub: "Reedbrook burns behind you" },
  { type: "walk", shot: "s_dolly_side", to: 6,
    line: "Three days the fire ate. On the third night, it was full." },
  { type: "qte", shot: "s_qte_close", enc: "e_first_husk", at: 7 },
  // FIRE 1 — immediately after the tutorial husk. Early on purpose:
  // the player meets the mechanic while it costs them nothing, so
  // the second fire is a system they recognise rather than a
  // surprise. It is also where they learn an Art may be spoken at
  // a fire, before the fighting starts.
  { type: "kit", kind: "bonfire", at: 10, dur: 3000 },
  { type: "walk", shot: "s_dolly_side", to: 14,
    line: "You do not look at what the pyres hold. You already know their names." },
  { type: "look", shot: "s_fp_back", dur: 4200,
    line: "Reedbrook. Home. The word already tastes of ash. On a fencepost, a crow with a knowing eye watches you go. It does not blink." },
  { type: "walk", shot: "s_dolly_front", to: 22,
    line: "Small legs. Long road. A beb walks anyway." },
  { type: "qte", shot: "s_qte_wide", enc: "e_husk_pair", at: 23 },
  { type: "walk", shot: "s_dolly_side", to: 30,
    line: "The village called it T'yphiz. Horned. Winged. A tail like a paddle and a bill like a duck's. You will not laugh when you see it." },
  // FIRE 2 — the last one before the Ridge Gate, which is the
  // first fight in the game that can actually take a beb apart.
  // Whatever you are holding when you sit down here is what you
  // take into it.
  { type: "kit", kind: "statue", at: 31, dur: 3200 },
  { type: "title", shot: "s_gate", dur: 2600, text: "THE RIDGE GATE", sub: "" },
  { type: "look", shot: "s_gate", dur: 3400,
    line: "Some strikes are lies. A parry spent on a lie is a parry lost. Wait for the true one." },
  { type: "qte", shot: "s_qte_close2", enc: "e_ridge", at: 33 },
  { type: "walk", shot: "s_dolly_rise", to: ROAD_END,
    line: "Lantern-light past the ridge. Hollowmere. Your legs remember how to hurry." },
  { type: "arrive" },
];


// ══════════════════════════════════════════════════════════════
//  LEVEL 2 — THE DROWNED MILE   (internal id: act2)
// ══════════════════════════════════════════════════════════════
// THE VYREN PATTERN, honoured: caP'n renamed the acts to LEVELS on
// 2026-07-28 and that is a pure DISPLAY swap. Every internal id in
// this file stays `act1` / `act2` / `ACT1` / `ACT2` / `ACTS`, for
// the same reason Terror never surfaced its legacy card ids: the
// name on the glass is the player's, the id in the code is ours,
// and a rename must never be allowed to become a migration.
// Old Maren said it on the first night, and she said it plainly:
// "T'yphiz keeps a disciple in the fens east of here — a thing
// with a bill of beaten iron. Find it, little one, and you find
// the road to its master."
//
// This is that road. It is longer than the Cinders (52 against
// 44), it has five fights against three, and it is COLD — the
// first act was a country on fire and this one is a country that
// the fire could not be bothered with. Every design decision in
// it points at one sentence: the coals were never the point.
//
// STRUCTURE, and why each beat is where it is:
//   the hound      speed, with no weapon to read. Warm-up.
//   the Mudlark    the joke, and the purse. Also secretly the
//                  lesson that a projectile's arrival angle is
//                  not its departure angle.
//   the look back  J'ynxi again, closer. She is not in this game.
//   the Reedmaw    the HOLD, and half the coals out.
//   the imps       the LYF CYCLE, enforced for the first time,
//                  by the softest thing in the act. You will
//                  survive this fight and you will not break it,
//                  and that is the fen telling you to go shopping.
//   the chapel     the Disciple. Dark, chorded, and one full rung
//                  above a beb.
export const ROAD_END_2 = 52;

export const ACT2 = [
  { type: "title", shot: "s_fen_open", dur: 3600,
    text: "LEVEL 2 — THE DROWNED MILE", sub: "East of Hollowmere, the ground stops agreeing to be ground" },
  { type: "walk", shot: "s_fen_side", to: 6,
    line: "Hollowmere's last lantern goes out behind you at the third bend. Nobody put it out." },
  { type: "qte", shot: "s_qte_water", enc: "e_fen_hound", at: 7 },
  { type: "walk", shot: "s_fen_low", to: 13,
    line: "The road stops being a road and becomes a promise about where a road used to be. Boards, mostly. Some of the boards are doors." },
  { type: "qte", shot: "s_qte_water", enc: "e_mudlark", at: 14 },
  // FIRE 1 — she is sat on the bank with a kettle and no questions.
  { type: "kit", kind: "lady", at: 18, dur: 3400 },
  { type: "walk", shot: "s_fen_side", to: 20,
    line: "Somebody's chimney stands in open water with nothing under it. The fen does not bury things. It just keeps them at the depth it found them." },
  { type: "look", shot: "s_fp_mile", dur: 4400,
    line: "On a mooring post, close enough to touch, the crow. The same crow. It is not following you — it is AHEAD of you, and it was ahead of you at Reedbrook too, and you are only now doing that arithmetic. It tilts its head. Teal eye. Still not blinking." },
  { type: "qte", shot: "s_qte_reeds", enc: "e_reedmaw", at: 26 },
  // FIRE 2 — a stranger, mid-mile, sharing what he has without
  // looking up. Sits between the Reedmaw and the imps so the whole
  // back half of the fen is one held breath.
  { type: "kit", kind: "stranger", at: 30, dur: 3200 },
  { type: "walk", shot: "s_fen_wide", to: 32,
    line: "Far off past the reeds the ground finally gets up again, and keeps going, and becomes a mountain with a hole burned through the cloud above it. That is where it sleeps. You can see the road to it from here, which is worse than not being able to." },
  { type: "qte", shot: "s_qte_reeds", enc: "e_imp_pair", at: 33 },
  { type: "walk", shot: "s_fen_low", to: 38,
    line: "Bell-rope, floating. Then the bell, on its side, in the shallows, with a fish living in it." },
  { type: "title", shot: "s_chapel", dur: 3000, text: "THE DROWNED CHAPEL", sub: "" },
  // FIRE 3 — the chapel itself, and the last breath you will get
  // before the Disciple. Everything you did not spend on the mile
  // is what you walk into the Cleft holding.
  { type: "kit", kind: "church", at: 39, dur: 3600 },
  { type: "look", shot: "s_chapel", dur: 4200,
    line: "Some things do not burn. This one wears a bill of beaten iron in its master's honour and it is one whole rung above you, and it will bring that bill down through two places at the same time. One answer from each paw, together. And it has put the coals out — every one of them. Read the creature. That was always the only question." },
  { type: "qte", shot: "s_qte_chapel", enc: "e_disciple", at: 41 },
  { type: "walk", shot: "s_fen_rise", to: ROAD_END_2,
    line: "The water gives your legs back one at a time. Up the far bank there are candles in windows — dozens of them, hundreds, a whole hillside of small lit rooms. Candlemarch. Somebody up there is still lighting lamps for people who might be coming." },
  { type: "arrive" },
];


// ══════════════════════════════════════════════════════════════
//  THE ACTS REGISTRY — added 2026-07-28
// ══════════════════════════════════════════════════════════════
// The director used to import ACT1 and hardcode it. That was
// correct for exactly as long as there was one act, and it is the
// "is this the set of things that exist, or the set of things that
// existed the day I wrote it" trap the Exception Law names.
//
// An act is now DATA, and everything a run needs to know about
// itself lives in one row:
//
//   script     the beat list
//   end        ROAD_END for this act (the walk's last x)
//   biome      which world the stage dresses — "cinders" | "fen"
//   teaches    letters the coals until this level is first cleared.
//              R-09, resolved 2026-07-28: the Cinders Road is a
//              real Level 1 that happens to teach, not a tutorial
//              with a door out of it.
//   gate       the milestone required to walk it, or null
//   milestone  what ARRIVING sets
//   village    where arriving puts you
//   kindling   the arrival purse
//   next       the act after this one, or null
//
// Adding Act III is a row here plus a script. That is the whole
// point of writing this pass this way.
export const ACTS = {
  act1: {
    id: "act1", name: "THE CINDERS ROAD", numeral: "LEVEL 1",
    sub: "Reedbrook burns behind you",
    script: ACT1, end: ROAD_END, biome: "cinders",
    // `teaches` raises the banner; `skippable` opens the door out.
    // Two fields, not one, because a later act may well want to
    // teach something without being skippable — and an act that is
    // skippable without teaching is a perfectly good thing to
    // build too. Keeping them apart is the Exception Law.
    teaches: true, skippable: true,
    gate: null, milestone: "hollowmere", village: "hollowmere",
    kindling: RULES.KINDLING_ARRIVE, next: "act2",
    endTitle: "HOLLOWMERE",
    endLine: "Lantern light. Voices. Somebody is already bringing soup.",
    fellLine: "The Cinders Road keeps what it catches. Everything you learned, you keep — every star, every record, and every splinter of Kindling you answered for. Only the walk is lost.",
    enter: "ENTER HOLLOWMERE",
  },
  act2: {
    id: "act2", name: "THE DROWNED MILE", numeral: "LEVEL 2",
    sub: "East of Hollowmere, the ground stops agreeing to be ground",
    script: ACT2, end: ROAD_END_2, biome: "fen",
    teaches: false,
    gate: "hollowmere", milestone: "candlemarch", village: "candlemarch",
    kindling: RULES.KINDLING_ARRIVE_ACT2, next: null,
    endTitle: "CANDLEMARCH",
    endLine: "Warm air off a hill full of windows. Somewhere above you a lamplighter is working his way along a row, and does not stop, and does not look down, and says: “evening.”",
    fellLine: "The fen keeps what it catches, and it keeps it at the depth it found it. Everything you learned, you keep — every star, every record, and every splinter of Kindling you answered for. Only the walk is lost.",
    enter: "CLIMB INTO CANDLEMARCH",
  },
};
export const ACT_ORDER = ["act1", "act2"];

// Which acts this Soul may walk. An act with no gate is always
// open; an act with a gate needs that milestone. DERIVED, never
// stored — same law as everything else in this file.
export const unlockedActs = (soul) =>
  ACT_ORDER.filter((id) => {
    const a = ACTS[id];
    return !a.gate || !!soul?.milestones?.[a.gate];
  });

// The furthest act this Soul has earned the right to walk. This is
// what CONTINUE means.
export const latestAct = (soul) => {
  const open = unlockedActs(soul);
  return open[open.length - 1] || "act1";
};

// ══════════════════════════════════════════════════════════════
//  WHYLLIE'S STOCK — one cart, one list, gated by the road
// ══════════════════════════════════════════════════════════════
// Restructured 2026-07-28. `wares` and `armoury` used to hang off
// the village, which was right while there was one village — and
// then a second village turned it into two lists that would drift,
// which is the exact failure mode the Outfitter file was created
// to prevent in the first place.
//
// So the stock belongs to WHYLLIE, because it always did. He is
// not a Hollowmere fixture; he is a man with a cart who turns up
// where the money is, and the money is wherever a beb who keeps
// surviving things happens to be standing. Every screen that shows
// a counter shows THIS list.
//
// `gate` is a milestone. No gate means he always has it. The fen
// gear appears the moment you have been to the fen, which is both
// the honest supply-chain answer and a small, free reward for
// arriving somewhere new.
export const STOCK = {
  wares: [
    { id: "tome_page_of_names" },
    { id: "tome_kindled_blade" },
    { id: "tome_fork_of_embers" },
    { id: "tome_rays_of_hope" },
    { id: "curio_wizards_cap" },
    { id: "tome_ferrymans_due", gate: "candlemarch" },
    { id: "tome_black_water", gate: "candlemarch" },
    { id: "curio_drowned_coin", gate: "candlemarch" },
    // ── THE REGALIA SHELF, 2026-07-28 ──
    // Ungated on purpose, and that is a real decision rather than
    // an oversight. The whole reason the counter left Hollowmere
    // was that the player who most needs to buy something is the
    // one who keeps dying before the village — and a player who
    // cannot get past the ridge is exactly the player a free
    // first power per fight was written for. He has them in stock.
    // He has always had them in stock. He is that kind of man.
    { id: "tome_mirror_pool" },
    { id: "tome_hollow_crown" },
    { id: "tome_long_hour" },
  ],
  armoury: [
    { id: "t_pyre_ladle" },
    { id: "t_ash_cleaver" },
    { id: "t_lantern_hook" },
    { id: "t_twin_needles" },
    { id: "t_bill_of_iron" },
    { id: "t_mire_fork", gate: "candlemarch" },
    { id: "t_drowned_censer", gate: "candlemarch" },
    { id: "t_bell_clapper", gate: "candlemarch" },
  ],
};

// What he will actually sell THIS Soul, today.
export const stockFor = (soul, key) =>
  (STOCK[key] || [])
    .filter((s) => !s.gate || !!soul?.milestones?.[s.gate])
    .map((s) => s.id);


// ══════════════════════════════════════════════════════════════
//  THE VILLAGES
// ══════════════════════════════════════════════════════════════
// A village is the end of one act and the door to the next. Same
// shape every time, so a third one is a data row.
//
//   depart      the act id this village's road leads to, or null
//   departLabel the button
//   departLine  the honest text under it
export const VILLAGES = {
  hollowmere: {
    id: "hollowmere", name: "HOLLOWMERE",
    sub: "The first light after the fire",
    tint: "#ffb058",
    arriveLines: [
      "The gate wardens lower their pikes when they see how small you are. Then they see your eyes, and raise them again — respectfully.",
      "Word of Reedbrook came ahead of you on the wind. Nobody asks. Somebody brings soup.",
    ],
    arriveGrant: "For surviving the Cinders Road: your heart kindles (+1 Anima), and the wardens press an armful of Kindling into your paws — dry, split, and worth something to Whyllie.",
    speak: [
      { who: "Gate Warden", text: "A beb. Alone. Off the Cinders Road. Either the Unstable World has a sense of humor, or you are something else." },
      { who: "Old Maren", text: "T'yphiz keeps a disciple in the fens east of here — a thing with a bill of beaten iron. Find it, little one, and you find the road to its master." },
      { who: "Whyllie", text: "Ohhh no no no, tiny customer, do not touch the— actually. Actually, touch whatever you like. You have the look of someone about to be very good for business." },
      { who: "Old Maren", text: "N'ullus takes and O'mnis swallows, and between them a small thing may still slip through. Sleep, beb. Grow. The fen will keep." },
    ],
    replayAct: "act1",
    replayLabel: "WALK THE CINDERS ROAD AGAIN",
    replayLine: "The road refills with Husks every night. Kindling earned there is permanent — this is how a beb gets big enough to matter.",
    depart: "act2",
    departLabel: "EAST — INTO THE DROWNED MILE",
    departLine: "Old Maren pointed east and did not soften it. Five days of standing water, and a thing at the end of it wearing iron in its master's honour. Your Soul, your wares, your records and every splinter you carry come with you.",
  },

  // ── CANDLEMARCH ─────────────────────────────────────────────
  // The far bank. A hill town of lamplighters who have made a
  // civic institution out of leaving a light on for people who
  // might still be walking. The mountain is visible from every
  // window in it, which is either very brave or an accident of
  // geology that nobody has been able to fix.
  //
  // Whyllie is here. Whyllie got here first. Nobody knows how.
  candlemarch: {
    id: "candlemarch", name: "CANDLEMARCH",
    sub: "The last lit place before the mountain",
    tint: "#ffd08a",
    arriveLines: [
      "The hill is on fire in the good way. Every window has a candle in it and none of the windows are anyone's — the lamplighters do the whole town, top to bottom, every night, for travellers who have not arrived yet and may not.",
      "You are dripping fen water onto three hundred years of doorstep. A woman with a taper the length of a fishing rod looks down at you, works something out, and lights the lamp above your head specifically.",
    ],
    arriveGrant: "For crossing the Drowned Mile: your heart kindles again (+1 Anima), and Candlemarch pays what Candlemarch pays anyone who comes up out of that water on their own legs.",
    speak: [
      { who: "Tobber the Lamplighter", text: "We light the whole hill. Every night, every window, whether or not there's anyone in the room. Cost of the wax is nothing next to the cost of somebody getting this far and finding it dark." },
      { who: "Sister Ashlin", text: "The chapel you came through was ours, before the water. It had a bell. You will have seen the bell. What I want you to understand is that the water did not take that chapel — the water was invited, by us, in a hurry, because the alternative was standing there when the thing in the sky came back." },
      { who: "Whyllie", text: "TINY CUSTOMER. Do not ask me how I got here before you did. Ask me instead about the censer, which is drowned, and smoking, and mine, and — for you, and only because I watched you walk out of that fen — ninety." },
      { who: "Tobber the Lamplighter", text: "Up. That's the only direction left. The Ashfall Stair goes up the mountain in eleven switchbacks and there is nothing living on any of them. It sleeps at the top, beb. You already knew that. You've been able to see it since the reeds." },
    ],
    replayAct: "act2",
    replayLabel: "WALK THE DROWNED MILE AGAIN",
    replayLine: "The fen puts everything back. The Mudlark in particular is fine, is thriving, and has already re-stocked.",
    depart: null,
    departLabel: null,
    departLine: "THE ASHFALL STAIR — eleven switchbacks and a hole burned through the cloud. (Act III is being forged. Your Soul, your wares and your records are safe and will travel with you — they always have been, and they always will be.)",
  },
};

// Kept as an alias so nothing that imported the old name breaks.
// The Vyren pattern: display and structure may change, internal
// ids never do, and no consumer should have to care which pass
// it was written in.
export const VILLAGE = VILLAGES.hollowmere;

export const villageFor = (soul) =>
  (soul?.milestones?.candlemarch ? VILLAGES.candlemarch : VILLAGES.hollowmere);

// ── THE TALE — Codex story pages ─────────────────────────────
export const TALE = {
  pages: [
    { title: "Reedbrook", text: "There was a village by slow water, and the village was called Reedbrook, and in Reedbrook lived a beb called Perry, and every one of these facts is now past tense except the last one." },
    { title: "The Fire", text: "It came at night, as fires that are also creatures prefer. Horns. Wings. Webbed feet that hissed on the wet reeds. A pointed tail, paddled like an oar. A duck's bill, which nobody laughed at, because it was the size of a door and full of light. The village named it T'yphiz — a Champion-level being — as if naming it could file it somewhere. Reedbrook burned for three days." },
    { title: "The Beb", text: "A beb is the second rung of seven. A beb is small. Everyone knows what a beb can and cannot do. Everyone is about to be wrong. You are Perry, and you parry — and every strike in the Unstable World has an answer, if you are willing to stand inside its arc and give one." },
    { title: "The Ash Prophecy", text: "Should T'yphiz burn every last refuge, the fire will have nothing left to eat but itself. Mark this inversion well: if your adversary achieves their ambition, YOU win. The keen-eyed say a crow already knows how the tale ends, and finds it very funny." },
    // ── ADDED 2026-07-28 with the Drowned Mile ──
    { title: "Hollowmere", text: "They gave you soup and a second heartbeat and did not once ask you to be bigger than you are. Old Maren pointed east with two fingers, the way you point at something you would rather not name twice, and said there is a disciple in the fens. Find it and you find the road to its master. Then she said sleep. Then she said grow. Both of those were instructions." },
    { title: "The Drowned Mile", text: "East of Hollowmere the ground stops agreeing to be ground. The fire never came here — there was nothing here dry enough to interest it — and so the fens are the one part of the country that is exactly as it always was, which turns out not to be comforting. The water does not bury what it takes. It keeps everything at the depth it found it, forever, at the angle it went in." },
    { title: "The Disciple", text: "It does not burn. That is the first thing and it is the whole thing. Every enemy on the Cinders Road was something the fire had happened TO; the Disciple walked into the fen behind T'yphiz on the third night of Reedbrook with its own two feet and has been standing in the chapel ever since, in the cold, in the dark, waiting for the sky to be told what to do. It wears a bill of beaten iron in its master's honour. It is a Champion. You are a Beb. That is a whole rung, and the rung is the argument." },
    { title: "The Crow, Again", text: "She was on the fencepost at Reedbrook and she is on a mooring post in the fens and both times she was there BEFORE you. Do that arithmetic slowly. She is not following the tale. The tale is going where she already is. Nobody in Hollowmere has a name for her; a beb who has been to certain other places in the Empire would have one immediately, and would probably sit down." },
  ],
};

// ── TEACHINGS — Codex tutorial pages ─────────────────────────
export const TEACHINGS = [
  { title: "The Grid", lines: [
    "There is no prompt on the screen. There never will be again. The foe's BODY tells you everything.",
    "Six angles on a 2 x 3 grid, both paws already resting on them. The COLUMN is which hand — left column is your left hand. The ROW is which finger, the same finger on both hands: F J on top (index), D K in the middle, S L on the bottom (ring).",
    "F and J get the top row because they are the only two keys you can find without looking.",
    "When something winds up, an ember arc paints the plane it is travelling on, and that arc FILLS toward you. When it arrives, answer its angle.",
    "Guess wrong EARLY and nothing happens — a probe is free. Guess wrong LATE and you have committed, and it lands.",
  ]},
  { title: "The Coals", lines: [
    "Six coals hang where the six answers live. On your first road they carry their LETTERS and their COLOURS both. After that road, the letters do not come back on their own.",
    "The colours do. HIGH burns warm — F amber, J ember red. MID burns cold — D cyan, K deep blue. LOW burns bright — S violet, L magenta. Row is the family; the left one is always the deeper of the pair.",
    "Learn the colours, not the squares. Something on this road will eventually MOVE the coals — each will still burn its own colour, but it will hang at another one's angle, and everything you knew about position will be a lie told in the right handwriting.",
    "Something later will put them out entirely. That is not a punishment. That is the game finally asking you the only question it ever had: can you read the creature?",
    "Until you can — a Treasure, a node, a Page, an Impera. Any of them will make a coal say its name again, for a moment, even to a foe that snuffed it.",
  ]},
  { title: "The Parry", lines: [
    "PERFECT — answered at the heart of the beat, on the right angle. You defend, and your Lyt breaks clean through the foe's skin.",
    "GOOD — answered within the beat. You defend. The foe's Lyf holds; chip damage is worthless in this Empire.",
    "MISS — wrong angle when it counted, or no answer at all. The foe's Lyt Dims your Anima.",
  ]},
  { title: "What comes at you", lines: [
    "SWIPE — a wide arm from a corner. The honest one. Learn on it.",
    "LUNGE — straight down the middle, fast, and it closes distance. Overhead or brace.",
    "SLASH — twice, and the second is quicker than the first. Twin Ember Needles answer both with one press.",
    "HURL — it tears a piece of itself loose and throws it. Long tell, real flight, generous window. The Lantern Hook is greedy for these.",
    "FEINT — a lie. It draws back and does not come. Press nothing.",
    "CALAMITY — three angles in a row, then a brace. Answer every beat and it FRACTURES, and your next answer lands free. Drop one and the whole thing lands at once.",
    "CHAIN — it plants, and throws the blade PAST you. It hangs behind your head on a slack chain while you wait. When the chain snaps tight, that is the haul: the blade comes back in from one side, and the chain is the only thing that tells you which.",
  ]},
  // ── THE DROWNED MILE'S LESSONS, 2026-07-28 ──
  { title: "The Chord", lines: [
    "Some things come at you from two places in the same instant. There is no order to them and no gap to slip through. There is only both.",
    "ONE KEY FROM EACH HAND, TOGETHER. F+L. D+K. S+J. Any pair with one from the left column and one from the right, and the pair the strike wants is the pair it is telling you.",
    "This is the whole reason the grid replaced the rose. On the old layout a chord was two fingers of one hand, and hands do not do that. On the grid it is your left index and your right ring finger, which is a thing hands do without being asked.",
    "You have about a seventh of a second of slack between the two presses. The LATER one is what gets timed — so a sloppy pair that arrives together grades on its slowest hand, and a perfect press followed by a late one is a late one.",
    "Only one thing in the fens does this. It is the last thing in the fens.",
  ]},
  { title: "The Hold", lines: [
    "Not everything is a beat. Some things POUR.",
    "Meet it on the angle exactly as you would meet anything — and then STAY THERE. Keep the key down. The strike is not over when it arrives; it is over when it stops.",
    "Let go early and the rest of it goes straight through the gap you just opened. There is a little slack for a bounced key, and only a little.",
    "The Mire Fork was built for standing in things. The Sunken Bell's Clapper very much was not.",
  ]},
  { title: "What the fens throw", lines: [
    "ROUNDHOUSE — no weapon, no arm. It turns its whole body into the blow, which means it has to turn its whole body first. Watch the hips.",
    "COIN STORM — a Mudlark throws Divine Crests at you and they BOUNCE off the boards on the way. The angle it left on is not the angle it arrives on. Watch the second half of the flight. You never keep the coin. You get paid for the answer.",
    "DROWN — a jet of black water. This is a HOLD. Meet it, then stand in it.",
    "UNDERTOW — the same, from underneath, on the bottom row, and longer.",
    "CINDERFALL — a comet. It comes down out of the dark, so the sky is the tell and the creature is not. Long flight, generous window.",
    "THE CLEFT — the chord. Two answers, one instant, one hand each.",
  ]},
  { title: "The Answer — U'hlti", lines: [
    "Three Chao AND two Anima. It is the only thing in the game that spends both, and it is meant to hurt.",
    "The next strike is answered PERFECTLY. Whatever it is. From wherever it comes. Chord, hold, lie, Calamity beat, in the dark, at the end of a mile of water.",
    "Key 4, once you have lit the star at the top of the constellation — and that star needs three others under it first.",
    "It will not kill you. Like every Impera, it refuses to be spoken if speaking it would be the last thing you did.",
  ]},
  { title: "Feints", lines: [
    "Some strikes are lies. A feint telegraphs, then withholds.",
    "Press nothing during a lie. Pressing anything is a MISS — and a feint snuffs any flame you keep lit (ATV wares beware).",
    "A wounded foe lies MORE. The closer it is to breaking, the less you can trust it.",
  ]},
  { title: "Your Treasure", lines: [
    "You parry with something. It is modelled, carried, swung, and it throws sparks when it meets fire.",
    "A Treasure never fights for you — it shapes THE ANSWER. Heavier bites deeper but hates low. Wider forgives but does not bite. One of them is a soup ladle.",
    "You are still a beb. You will always be a beb. That was never the part that needed upgrading.",
  ]},
  { title: "Anima — life as currency", lines: [
    "Anima is your life AND your purse. A miss Dims it. At zero, your Solrend ends.",
    "You may SPEND it: the Ember Pact Impera trades 1 Anima for full Chao; Whyllie sells one ware priced in blood; The Answer (U'hlti) will one day cost Chao AND Anima.",
    "A fresh beb holds 1. Hollowmere kindled your second. The constellation holds more.",
  ]},
  { title: "Chao — the Growth Law", lines: [
    "Chao is your breath. It refills to Max as each encounter begins, and fuels your Imperas (keys 1, 2, 3).",
    "THE GROWTH LAW: clear an encounter having spent NO Chao and your Max Chao rises by 1, permanently. Restraint compounds. Only one universe rewards you for holding your breath.",
  ]},
  { title: "The Rungs", lines: [
    "Escling → Beb → Champion → Lord → Escendant → Aenarchist → Solrend. Seven rungs.",
    "You are a Beb. T'yphiz is a Champion, two rungs above you. The whole point of this tale is that this arithmetic is wrong.",
  ]},
];

// ── TUTORIAL — the banner over the teaching road ─────────────
// Three strings, and every one of them is a PROMISE the road has
// to keep. Restored 2026-07-29 after a stale-snapshot delivery
// dropped this export and broke the build.
//
// I first "fixed" that by aliasing TUTORIAL to TEACHINGS, having
// decoded the shape from webpack's column offsets — which said
// array, and was wrong. `QTEOverlay` reads `.banner`,
// `.bannerSub` and `.skipHint`. An alias would have COMPILED and
// then rendered three blank strings, which is the worse failure:
// a green build lying about a broken screen. Inference off byte
// offsets is not a substitute for reading the file.
//
// COPY LAW — unit / plain / lore / also. The banner is the unit
// (what is true), the sub is the plain (why you care), the hint
// is the door. No mechanics in the flavour, no flavour in the
// mechanics.
export const TUTORIAL = {
  banner: "YOU CANNOT FALL HERE",
  bannerSub: "The Cinders Road is teaching. Nothing you lose on it stays lost.",
  skipHint: "ESC — SKIP THE ROAD",

  // ══════════════════════════════════════════════════════════
  //  THE REPORT CARD — 2026-07-30, restored and finished
  // ══════════════════════════════════════════════════════════
  // caP'n: "we had a pop up after every fight telling you how
  //  many times you could've died given your Anima."
  //
  // That is the ENTIRE justification for the deathlessness. A
  // road that cannot kill you and never mentions it teaches that
  // nothing matters; a road that cannot kill you and shows you
  // the bill afterwards teaches the exact thing the real road is
  // about to charge for. The number is the lesson. The mercy is
  // just the delivery mechanism.
  //
  // `spared` is that number — how many times your Anima actually
  // reached zero. `struck` is every blow that reached you at all.
  // They are different facts and the card prints both, because
  // "hit nine times, dead twice" and "hit twice, dead twice" are
  // two completely different players.
  cardTitle: "THE ROAD KEPT YOU",
  cardTitleClean: "NOT A MARK ON YOU",
  cardStruck: "BLOWS THAT REACHED YOU",
  cardSpared: "TIMES YOU WOULD HAVE FALLEN",
  // Spoken only when the number is 0 — a card that congratulates
  // you in the same words it uses to bury you is a card nobody
  // reads twice.
  cardClean: "Nothing touched you. Out there, that is what it looks like when it goes right.",
  cardOne: "Once. On any road but this one, that was the walk.",
  cardMany: "That many. The Cinders Road is the only stretch in the Empire that gives them back.",
  cardGo: "PRESS ANY KEY",

  // Spoken the moment your Anima actually reaches zero. It has to
  // name what happened out loud — a bar that empties and then
  // silently refills reads as a rendering bug, and a player who
  // thinks the bar is broken stops watching it, which is the one
  // thing this road exists to teach them to do.
  sparedLine: "The road takes your weight for a moment, and hands it back. Anywhere else, that was the walk.",

  // The door out is two presses, not one. ESC is also the key
  // every menu in this title closes with, and a single tap
  // ending the road by accident is a bug report waiting to be
  // filed. The arm lasts SKIP_ARM_MS and then forgets itself.
  skipArm: "PRESS ESC AGAIN TO LEAVE THE ROAD",
  skipArmSub: "You keep what you have earned. You do not keep what you have not.",
  skipLine: "You take the ridge path. Hollowmere's lights are already on.",
};


// ── Derivations (DERIVE, DON'T STORE) ────────────────────────
// Store the inputs (nodes, milestones, chaoGrown, deviant,
// ascensions bought). Compute the outputs. Standing derives from
// ascensions; every cap derives from the record of how it was
// earned; every LEVEL derives from a rank pool that stars and gear
// both pour into.

export const nodeById = (id) => NODES.find((n) => n.id === id) || null;

// The Treasure in his paws. Everyone always has one — a beb who
// owns nothing still walked out of Reedbrook holding a reed.
export const deriveTreasure = (soul) =>
  TREASURES[soul?.treasure] || TREASURES.t_reed_switch;
export const deriveFinish = (soul) =>
  FINISHES[soul?.finish] || FINISHES.f_ember;
export const ownsTreasure = (soul, id) =>
  id === "t_reed_switch" || !!soul?.treasures?.[id];

// ══════════════════════════════════════════════════════════════
//  DERIVE ARM — the weapon PLUS everything it has Ascended into
// ══════════════════════════════════════════════════════════════
// THE SINGLE MOST IMPORTANT FUNCTION ADDED ON 2026-07-30, and the
// one seam the whole Sheathe restructure rides on.
//
// `deriveTreasure` answers "which row is he holding." This answers
// "what is the thing in his paws actually WORTH right now," and
// every stat derivation below reads THIS one. A rung that grants
// `ranks: { grace: 2 }` is indistinguishable from a Treasure that
// shipped with it, a rung that grants `lyt: 1` is indistinguishable
// from base Lyt, and nothing downstream of here knows or cares
// that an Ascension happened. DERIVE, DON'T STORE, one more level
// up — same as `ascensions`, same reason.
//
// THE AWAKENING IS HANDLED HERE AND NOWHERE ELSE. caP'n: "you
// start a mission, defeat certain amount of enemies with it even
// now (stat wise, it's back to level 1 not visually) to Awaken
// it." So a weapon mid-Awakening returns its BASE ROW — no rungs,
// no Lord, nothing — while `soul.arms[id].awakening` is open and
// unfinished. It looks identical in his paws and it is a stick.
// One `if`, in one function, and every screen and the entire
// combat loop tell the same true story about it for free.
const applyArmGrant = (out, g) => {
  if (!g) return;
  for (const k of ["lyt", "lyf", "sight", "pierce", "ladle"]) {
    if (g[k]) out[k] = (out[k] || 0) + g[k];
  }
  for (const k of ["windowMod", "grip", "hurlMod"]) {
    if (g[k]) out[k] = (out[k] || 0) + g[k];
  }
  if (g.ranks) {
    out.ranks = { ...(out.ranks || {}) };
    for (const [t, n] of Object.entries(g.ranks)) out.ranks[t] = (out.ranks[t] || 0) + (n || 0);
  }
  if (g.regalia) out.regalia = [...(out.regalia || []), g.regalia];
  if (g.title) out.title = g.title;
};

// Is this weapon currently walking the Awakening — stripped back
// to nothing until it has earned the crown?
export const armAwakening = (soul, id) => {
  const st = soul?.arms?.[id];
  if (!st?.awakening || st.lord) return null;
  return st.awakening;
};

export const deriveArm = (soul, id = null) => {
  const base = id ? (TREASURES[id] || TREASURES.t_reed_switch) : deriveTreasure(soul);
  const out = { ...base, ranks: { ...(base.ranks || {}) }, regalia: [], lord: false };
  const st = soul?.arms?.[base.id];
  if (!st) return out;

  // THE AWAKENING — it is a stick again, and it means it.
  if (st.awakening && !st.lord) return out;

  const tree = ARM_TREES[base.id];
  if (!tree) return out;
  for (const rung of ARM_LADDER) {
    if (st.rungs?.[rung.key]) applyArmGrant(out, tree[rung.key]);
  }
  if (st.lord) { applyArmGrant(out, tree.lord); out.lord = true; }
  return out;
};

// How far up its own ladder a weapon has climbed. Level, like
// everything else in the Empire, tops out at 3 — and LORD is not
// a fourth level, it is what happens after the third one is spent.
export const armProgress = (soul, id) => {
  const st = soul?.arms?.[id] || {};
  const lit = ARM_LADDER.filter((r) => st.rungs?.[r.key]);
  const level = lit.reduce((m, r) => Math.max(m, r.level), 0);
  const next = ARM_LADDER.find((r) => !st.rungs?.[r.key]) || null;
  return {
    id, lit: lit.length, of: ARM_LADDER.length,
    level: Math.min(RULES.LEVEL_CAP, level),
    next,
    crowned: lit.length >= ARM_LADDER.length,   // every rung — eligible
    awakening: armAwakening(soul, id),
    lord: !!st.lord,
    tree: ARM_TREES[id] || null,
  };
};

// ══════════════════════════════════════════════════════════════
//  THE RANK POOL — stars and gear, pouring into one bucket
// ══════════════════════════════════════════════════════════════
// The most important function in the file. Everything a player can
// raise goes through here, which is why a Treasure granting ranks
// and a star granting ranks are indistinguishable downstream —
// they are the same number in the same pool, and the LEVEL falls
// out of the total.
//
// Three sources, all additive, none special-cased:
//   1. STARS   — `fx: { rank, n }` on a taken constellation node
//   2. TREASURE — `ranks: { wind: 3 }` on what is in your paws
//   3. CURIOS  — same shape, for anything worn
//
// A Treasure taken off takes its ranks with it, and the level
// drops. That is honest and it is visible in Kit Up before you
// unequip anything. A build is a thing you are WEARING, not a
// thing you have banked.
export const deriveRanks = (soul) => {
  const pool = {};
  for (const k of TRACK_ORDER) pool[k] = 0;

  for (const id of soulNodes(soul)) {
    const fx = nodeById(id)?.fx;
    if (fx?.rank && TRACKABLE.has(fx.rank)) pool[fx.rank] += fx.n || 1;
  }
  const add = (grants) => {
    for (const [k, n] of Object.entries(grants || {})) {
      if (TRACKABLE.has(k)) pool[k] += n || 0;
    }
  };
  add(deriveArm(soul).ranks);   // the ASCENDED weapon, not the shelf row
  for (const id of Object.keys(soul?.curios || {})) {
    if (soul.curios[id]) add(CURIOS[id]?.ranks);
  }
  return pool;
};

// One track's full story — ranks, level, how far to the next.
export const deriveTrack = (soul, key) =>
  trackProgress(deriveRanks(soul)[key] || 0);

// Level shorthand. `deriveLevel(soul, "wind")` → 0 … 3.
export const deriveLevel = (soul, key) => trackLevel(deriveRanks(soul)[key] || 0);

export const deriveAnimaCap = (soul) => {
  let cap = RULES.ANIMA_BASE;
  if (soul?.milestones?.hollowmere) cap += 1;  // "your first free one"
  if (soul?.milestones?.candlemarch) cap += 1; // and the far bank's
  cap += deriveLevel(soul, "heart") * TRACKS.heart.per;
  // milestones may sit ATOP the natural cap — a gift from a village
  // is not the same thing as a star you climbed to, and the ladder
  // should never be able to make a gift worthless.
  return Math.min(cap, RULES.ANIMA_NAT_CAP + 2);
};

export const deriveMaxChao = (soul) => {
  let cap = RULES.CHAO_BASE;
  if (soul?.species === "h01") cap += 1; // Radiant reserves
  cap += soul?.chaoGrown || 0;           // the Growth Law's record
  cap += deriveLevel(soul, "breath") * TRACKS.breath.per;
  return Math.min(cap, RULES.CHAO_NAT_CAP);
};

// ── LYT — WHAT YOU ARE AND WHAT YOU ARE HOLDING. NEVER WHAT YOU KNOW.
// Note what is NOT in this function: any reference to a node's `fx`.
// That absence is the Lyt Refusal, and it is load-bearing. If Lyt
// ever starts creeping up again, this is the function that grew a
// loop it should not have.
export const deriveLyt = (soul) => {
  let lyt = RULES.LYT_BASE;
  if (soul?.species === "chaos_dragon") lyt += 1; // Glass storm
  lyt += deriveArm(soul).lyt || 0;                // what he is holding, Ascension and all
  return lyt;
};

// ── LYF — how many bad answers your paws will forgive ──────────
// Yours, not theirs. A foe's Lyf is skin; yours is breath. Base 1,
// and every point past that is something you went and got: a
// milestone, a Treasure, a Curio. Deliberately NOT a track — being
// harder to wind is a thing the WORLD gives you for surviving it,
// not a thing you buy in a shop, and keeping it off the sky is what
// stops "stamina build" from becoming the only build.
export const deriveLyf = (soul) => {
  let lyf = RULES.LYF_BASE;
  if (soul?.milestones?.hollowmere) lyf += 1;
  if (soul?.milestones?.candlemarch) lyf += 1;
  lyf += deriveArm(soul).lyf || 0;
  for (const id of Object.keys(soul?.curios || {})) {
    if (soul.curios[id]) lyf += CURIOS[id]?.lyf || 0;
  }
  return Math.min(lyf, RULES.LYF_NAT_CAP);
};

// ── EXHAUST — the milliseconds your paws belong to the fen ─────
// The Sharpened Answer track buys these back, one level at a time,
// and it can never buy all of them. A beb who runs out of breath is
// always going to lose a moment; the question is how big a moment.
export const deriveExhaustMs = (soul) => {
  const cut = deriveLevel(soul, "wind") * TRACKS.wind.per;
  return Math.max(RULES.EXHAUST_FLOOR, RULES.EXHAUST_MS - cut);
};

export const deriveGrace = (soul) =>
  deriveLevel(soul, "grace") * TRACKS.grace.per; // temporal % — allowed

// ── SIGHT — how many coals will say their name, per encounter ──
// DERIVE, DON'T STORE, like everything else: it is a function of
// what you took (nodes) and what you are holding (Treasure). The
// Tome and the Impera add to it DURING a run and are not part of
// the standing pool, because they are consumed.
//
// NOTE THE ABSENCE OF A SOUL FIELD. Nothing here writes anything
// new to the database — no schema change, no rules change, no
// whitelist exposure. The whole Sight ladder rides on `nodes`,
// `treasure` and `tomes`, all three of which already existed.
export const deriveSight = (soul) =>
  (deriveArm(soul).sight || 0) + deriveLevel(soul, "sight") * TRACKS.sight.per;

// ── GRIP — how forgiving your hold is ────────────────────────
// Same shape as Grace, on a different verb. Additive, from the
// constellation and from what is in your paws. A negative Treasure
// grip (the Clapper) can and should drag this below zero — a very
// heavy thing is bad at standing still under pressure, and saying
// so honestly is more interesting than a Treasure with no downside.
export const deriveGrip = (soul) =>
  (deriveArm(soul).grip || 0) + deriveLevel(soul, "grip") * TRACKS.grip.per;

// Has this Soul lit the star at the top? (R-12)
export const hasUhlti = (soul) =>
  soulNodes(soul).some((id) => nodeById(id)?.fx?.uhlti);

// ══════════════════════════════════════════════════════════════
//  THE REGALIA DERIVATIONS — what you own, and what you carry
// ══════════════════════════════════════════════════════════════
// Two different questions, and keeping them two functions is the
// whole reason Kit Up has anything to do. OWNING is a fact about
// your constellation. CARRYING is a decision you made about it.

// Everything this Soul has learned, in registry order so the Kit
// Up screen never reshuffles itself between visits.
export const deriveRegalia = (soul) => {
  const owned = new Set();
  // ── THE SKY — legacy, and on borrowed time (R-52) ──
  // Under the Sheathe's restructure NO Art belongs to Perry. These
  // stars are still lit because unplugging them before the sword
  // tree has a screen would leave a live Soul with no route to any
  // Art at all. The rows are flagged `legacy: true`; the refund
  // migration is R-52 and it is a deliberate, flagged schema job.
  for (const id of soulNodes(soul)) {
    const fx = nodeById(id)?.fx;
    if (fx?.regalia) owned.add(fx.regalia);
  }
  // ── THE SWORD — where every Art actually lives now ──
  for (const id of deriveArm(soul).regalia || []) owned.add(id);
  return REGALIA_ORDER.filter((id) => owned.has(id));
};

// ── THE FIVE YOU TOOK ────────────────────────────────────────
// Returns a fixed-length array of RULES.SLOTS entries, each an id
// or null. Index 0 is key "1".
//
// THE LEGACY FALLBACK IS LOAD-BEARING, not politeness. Every Soul
// that existed before this pass has no `slots` field at all, and
// those players did not consent to walking onto the Cinders Road
// with an empty Grimoire. So a Soul with nothing stored simply
// carries the first five things it owns, in registry order — which
// for every pre-existing Soul is exactly the loadout it had
// yesterday, on the same keys, in the same order. Nobody logs in
// to a nerf.
//
// It also self-heals in three directions at once, and it has to:
// entries you no longer own are dropped (a New Game), duplicates
// are dropped (a bad write), and the array is re-padded to length
// (RULES.SLOTS changed). Self-healing beats correct-if-you-
// remember — this file has already lost one playtest to a latch
// that never fired.
export const deriveSlots = (soul) => {
  const owned = deriveRegalia(soul);
  const stored = Array.isArray(soul?.slots) ? soul.slots : null;
  const out = [];
  const used = new Set();

  if (stored) {
    for (const raw of stored) {
      if (out.length >= RULES.SLOTS) break;
      // "-" is the empty-slot placeholder written by setSlots.
      // Realtime Database drops nulls out of an array and shifts
      // every index after them, which would silently re-key a
      // player's whole Grimoire between sessions. Anything this
      // function does not recognise is simply an empty slot.
      const id = raw && raw !== "-" ? raw : null;
      if (id && owned.includes(id) && !used.has(id)) { used.add(id); out.push(id); }
      else out.push(null);
    }
  } else {
    // No stored loadout — carry what you own, best five, in order.
    for (const id of owned) {
      if (out.length >= RULES.SLOTS) break;
      used.add(id); out.push(id);
    }
  }
  while (out.length < RULES.SLOTS) out.push(null);
  return out.slice(0, RULES.SLOTS);
};

// The key a slot answers to. ONE function, so the overlay, the
// Kit Up screen, the Codex and the director's own keydown handler
// cannot disagree about which finger fires which power — which is
// the exact class of bug the Screen Law was written to kill.
export const slotKey = (i) => String(i + 1);
export const SLOT_KEYS = Array.from({ length: RULES.SLOTS }, (_, i) => slotKey(i));

// Owned powers that are NOT currently carried. The Kit Up screen
// shows these greyed beneath the rack, because a power you own and
// left at home should feel like a decision, not an absence.
export const deriveBenched = (soul) => {
  const carried = new Set(deriveSlots(soul).filter(Boolean));
  return deriveRegalia(soul).filter((id) => !carried.has(id));
};

// KEPT, and still correct: "which Imperas does this Soul know."
// Every existing caller means exactly that and still gets exactly
// that. The director no longer uses it to decide what is bound to
// a key — that is deriveSlots' job now — but nothing that asked
// the old question gets a new answer. (Vyren pattern.)
export const deriveImperas = (soul) => {
  const out = [];
  for (const id of soulNodes(soul)) {
    const fx = nodeById(id)?.fx;
    if (fx?.impera) out.push(fx.impera || fx.regalia);
    else if (fx?.regalia) out.push(fx.regalia);
  }
  return out.filter(Boolean);
};

// Has this Soul lit the star at the very top? Used for flavour
// and for the Kit Up header — never for gating, which is the
// slot's job.
export const hasEmperium = (soul) => deriveRegalia(soul).includes("e_perry_emperium");

export const spentPoints = (soul) => {
  let s = 0;
  for (const id of soulNodes(soul)) s += nodeById(id)?.cost || 0;
  return s;
};

// ── ASCENSION POINTS — bought, then derived ──────────────────
// `soul.ascensions` is the count ever PURCHASED with ⋔, add-only,
// never decremented — that is the stored input. What is FREE right
// now is still derived, from bought minus spent-on-stars. Law 14
// intact: store the input, compute the output.
export const availablePoints = (soul) =>
  Math.max(0, (soul?.ascensions || 0) - spentPoints(soul));

// ── STANDING — derived from ASCENSIONS, not from a purse ──────
// It has to hang off something that only ever goes up. Kindling
// no longer does — you spend it now — so a title pinned to your
// wallet would DROP when you bought an axe, which is absurd. Points
// bought is the honest monotonic record of how far a beb has come.
export const deriveStanding = (soul) => {
  const a = soul?.ascensions || 0;
  if (a >= 24) return "Beb of the Third Kindling";
  if (a >= 12) return "Beb of the Second Kindling";
  if (a >= 4)  return "Beb of the First Kindling";
  return "Beb of Reedbrook";
};

// A fresh Soul — what New Game forges (the Soul is lost all at once).
export const freshSoul = (species) => ({
  species,
  // ── ASCENSIONS, not Renown (Two-Currency Law, 2026-07-29) ──
  // Points BOUGHT with Kindling, add-only, never decremented.
  // `renown` is gone from a fresh Soul entirely; an existing Soul
  // still carrying one is converted once, on load, by
  // migrateRenown() in perryProgression.
  ascensions: 0,
  nodes: {},
  chaoGrown: 0,
  deviant: false,
  milestones: {},
  tomes: {},     // { tomeId: count } — armed automatically next run
  curios: {},    // { curioId: true }
  treasures: {}, // { treasureId: true } — the reed is free and implicit
  treasure: "t_reed_switch",  // what is actually in his paws
  finish: "f_ember",          // cosmetic only
  // ── THE GRIMOIRE ──
  // Five nulls, not five defaults. A fresh beb knows no powers at
  // all, so an empty rack is the honest state — and deriveSlots'
  // fallback fills it the moment the first star is lit, without a
  // write. Nothing about a loadout is ever stored that could be
  // computed from what you own.
  slots: [null, null, null, null, null],
  // ══════════════════════════════════════════════════════════
  //  THE ARMS DRAWER — the Sheathe's ledger (2026-07-30)
  // ══════════════════════════════════════════════════════════
  // One entry PER WEAPON, keyed by Treasure id, and it stays there
  // forever. caP'n: "can switch and level up others if you want,
  // pay currency and reset your levels."
  //
  // Read that carefully — the rungs are NOT refunded on a switch.
  // They are LEFT BEHIND, on the weapon that has them, and they
  // are still lit when you come back to it. The REFORGE_COST is a
  // fee for the Sheathe's attention, not a respec, which is why
  // this is a map and not a single active record. Investing in a
  // second sword is a second investment; abandoning the first one
  // costs you nothing except the road you are not walking with it.
  //
  //   arms: {
  //     t_reed_switch: {
  //       rungs:     { a1: true, a2: true },   // merge-flags, add-only
  //       awakening: { at, ruins },            // open mission, or absent
  //       lord:      true,                     // past the ladder
  //     },
  //   }
  //
  // Empty on a fresh Soul. A weapon with no entry is simply a
  // weapon nobody has spoken to yet — `deriveArm` hands back its
  // base row and nothing anywhere needs a null check.
  arms: {},
  createdAt: Date.now(),
});

// ══════════════════════════════════════════════════════════════
//  THE BESTIARY AUDIT — the ceilings, enforced (2026-07-29)
// ══════════════════════════════════════════════════════════════
// Two ceilings, two different KINDS of rule, and the difference
// is the whole reason this function exists:
//
//   FOE_LYF_CEILING (5) — a VALUE cap. No foe may exceed it, and
//     any number of foes may sit at it. Purely arithmetic, so a
//     machine can check it, so a machine should.
//
//   ANIMA_CEILING (5) — a value cap AND a SEAT budget: five, and
//     no more than five things in the whole game ever hold it.
//     A machine can check the value. It cannot check the budget,
//     because the budget lives in ANIMA_CEILING_SEATS, which is
//     prose. So this counts holders and compares that count to
//     the seat list, and shouts if they disagree.
//
// WHY A FUNCTION AND NOT A CLAMP. Clamping a data row silently
// makes a wrong number playable, and a wrong number that plays is
// a wrong number that ships. This refuses to fix anything — it
// only tells you, loudly, at boot, in dev.
//
// caP'n's rarity intent is NOT enforced here and deliberately so:
// "the hardest ones only" is a judgement about which foes deserve
// the ceiling, and judgement is what the ledger below is for. The
// audit reports the roster; a human reads it.
// ── THE COMMON CAP, ADDED 2026-07-29 ──────────────────────────
// The block above shipped checking ANIMA_CEILING and nothing else,
// while the comment on T'yphiz promised "the audit reads `animaSeat`
// instead of guessing." It did not. `ANIMA_COMMON_CAP` was written,
// documented, and load-bearing in prose, and no line of code had
// ever read it — which is the exact failure mode the workflow warns
// about: a brace-balance check finds damage, never absence.
//
// It matters because the common cap, not the ceiling, is the rule
// that does the day-to-day work. Nothing in the game is near Anima
// 5; plenty of things are one careless edit away from Anima 3. The
// ceiling catches a foe nobody would have shipped anyway. THIS
// catches the one you actually would have.
export const CEILING_HOLDERS = () => {
  const lyf = [], anima = [], seated = [], over = [];
  for (const e of Object.values(ENEMIES)) {
    if ((e.lyf || 0) > RULES.FOE_LYF_CEILING) {
      over.push(`${e.name} — Lyf ${e.lyf} exceeds the ceiling of ${RULES.FOE_LYF_CEILING}`);
    }
    if ((e.anima || 0) > RULES.ANIMA_CEILING) {
      over.push(`${e.name} — Anima ${e.anima} exceeds the ceiling of ${RULES.ANIMA_CEILING}`);
    }
    // The seat is the licence. Without one, 2 is the wall — and the
    // error says which field to add, because an audit that only says
    // "no" costs somebody a grep.
    //
    // ── AMENDED 2026-07-30, and the merged audit caught it ──
    // This line read `!e.animaSeat` and predates caP'n's act-tier
    // ruling, which lets an Act II BOSS stand at Anima 4 without
    // holding a ceiling seat. Seats are a reservation on FIVE, not
    // a licence for three. Under the old line The Disciple — a
    // Champion, correctly at Anima 4 — failed an audit it should
    // never have been subject to.
    //
    // So the common cap now binds what it was always describing:
    // things that are neither a boss nor a ceiling holder. A boss
    // answers to `ACT_TIERS` a few lines below instead, and it
    // answers there properly — the Disciple at Anima 5 would still
    // be caught, by the rule that actually governs it.
    if (!e.animaSeat && !e.boss && (e.anima || 0) > RULES.ANIMA_COMMON_CAP) {
      over.push(
        `${e.name} — Anima ${e.anima} is over the common cap of ${RULES.ANIMA_COMMON_CAP} ` +
        `and is neither a boss nor a seat holder. Lower it, add \`boss: true\` and let its act tier ` +
        `govern it, or add \`animaSeat: true\` and take a seat in ANIMA_CEILING_SEATS.`
      );
    }
    if (e.animaSeat) seated.push(e.name);
    if ((e.lyf || 0) === RULES.FOE_LYF_CEILING) lyf.push(e.name);
    if ((e.anima || 0) === RULES.ANIMA_CEILING) anima.push(e.name);

    // ══════════════════════════════════════════════════════════
    //  THE ACT TIERS — folded in here, 2026-07-30
    // ══════════════════════════════════════════════════════════
    // These checks shipped as a SECOND `auditBestiary` 1,200 lines
    // up this file and broke the build: `Identifier 'auditBestiary'
    // has already been declared`. Two functions auditing the same
    // table is the exact shape of caP'n's own law — two hand-
    // maintained copies of the same mathematics will drift — so the
    // fix is not a rename. It is this: ONE audit, one entry point,
    // and the tier rules live beside the ceiling rules that were
    // always going to be checked in the same breath.
    //
    // THE THREE CEILINGS ARE NOT THE SAME CEILING:
    //   · a COMMON foe (no `boss`)       → ANIMA_COMMON_CAP, above
    //   · a BOSS                         → ACT_TIERS[act].bossAnima
    //   · a CEILING HOLDER (`animaSeat`) → ANIMA_CEILING, five ever
    //
    // Reads FLAGS, never names. `boss`, `animaSeat` and `secret`
    // are the three knobs that change what a row may be, and a
    // fourth kind of exception is a fourth flag, not an edit here.
    const tier = RULES.ACT_TIERS[FOE_ACT[e.id]] || null;
    // A secret seat-holder is the flagged exception to its tier —
    // caP'n's "maybe 1, for like a supppper secret boss." It still
    // answers to the ceilings above; it is only excused its act.
    const exempt = e.animaSeat && e.secret;
    if (tier && !exempt) {
      if (e.boss) {
        if ((e.anima || 0) > tier.bossAnima) {
          over.push(`${e.name} — boss Anima ${e.anima} is over the ${tier.name} cap of ${tier.bossAnima}.`);
        }
        if ((e.lyf || 0) > tier.bossLyf) {
          over.push(`${e.name} — boss Lyf ${e.lyf} is over the ${tier.name} cap of ${tier.bossLyf}.`);
        }
      } else if ((e.lyf || 0) > tier.commonLyf) {
        over.push(
          `${e.name} — common Lyf ${e.lyf} is over the ${tier.name} cap of ${tier.commonLyf}. ` +
          `Lower it, or add \`boss: true\` if it was meant to be one.`
        );
      }
    }
    // A seat is a reservation on the hardest things in the Empire.
    // Nothing without a boss flag has any business holding one.
    if (e.animaSeat && !e.boss) {
      over.push(`${e.name} — holds an Anima seat but carries no \`boss: true\`.`);
    }
  }
  const seats = RULES.ANIMA_CEILING_SEATS.length;
  // Two ways to overspend the budget, and they are not the same
  // thing: more foes AT the ceiling than there are seats, and more
  // foes CLAIMING a seat than there are seats. A foe can hold a
  // seat while standing at 3 — T'yphiz does today — so the claim
  // has to be counted separately or the budget leaks quietly.
  if (anima.length > seats) {
    over.push(`${anima.length} foes hold the Anima Ceiling and there are only ${seats} seats: ${anima.join(", ")}`);
  }
  if (seated.length > seats) {
    over.push(`${seated.length} foes claim a seat and there are only ${seats}: ${seated.join(", ")}`);
  }
  return { lyf, anima, seated, over, seats };
};

// Call once at boot. Dev-only by design — a shipped build has no
// business paying for this, and a shipped build has already been
// audited or it should not have shipped.
export const auditBestiary = () => {
  const { lyf, anima, seated, over, seats } = CEILING_HOLDERS();
  for (const line of over) console.error(`⛓ BESTIARY: ${line}`);
  if (!over.length) {
    console.info(
      `⛓ BESTIARY clean · Lyf ceiling (${RULES.FOE_LYF_CEILING}) held by ${lyf.length}: ${lyf.join(", ") || "nobody"}` +
      ` · Anima ceiling (${RULES.ANIMA_CEILING}) held by ${anima.length}: ${anima.join(", ") || "nobody"}` +
      ` · seats claimed ${seated.length}/${seats}: ${seated.join(", ") || "nobody"}`
    );
  }
  return over.length === 0;
};
